// cspell:ignore dataname, sessionm, freedompay

import {
  allEnumValues,
  BrinkApiServer,
  CampusCardAccountType,
  CampusCardAccountTypeHelper,
  ComoTransactionIdSource,
  ComoTransactionIdSourceHelper,
  DefaultModBehavior,
  DefaultSelectionType,
  DeliverectOrderType,
  DeliverectOrderTypeHelper,
  DonationRoundUpPromptType,
  EigenApiServer,
  FulfillmentMethod,
  GivexApiServer,
  HardwareReadMethod,
  IntegrationInstanceType,
  IntegrationInstanceTypeHelper,
  IntegrationSystem,
  KioskPaymentTerminalState,
  KioskPaymentTerminalStateHelper,
  LovesSandboxApiServer,
  LovesSandboxApiServerHelper,
  LovesConcept,
  LoyaltyAuthMethod,
  MenuItemCategory,
  NcrAlohaOrderMode,
  OloCustomKeyOrderField,
  OloHandoffMode,
  OloHandoffModeHelper,
  OloOrderHistorySource,
  OloOrderHistorySourceHelper,
  OloSetTableNumberAs,
  OloSetTableNumberAsHelper,
  OmnivorePosType,
  OmnivorePosTypeHelper,
  PaymentProcessor,
  PaytronixCheckNumberSource,
  PaytronixCheckNumberSourceHelper,
  PaytronixGiftAuthSource,
  PaytronixGiftAuthSourceHelper,
  PinataApiServer,
  PinataPayTransactionResult,
  PinataPayTransactionResultHelper,
  PinataTableTrackerRequestType,
  PinataTableTrackerResult,
  PunchhApiServer,
  PunchhOloPhoneSlug,
  PunchhOloSlug,
  PunchhPrinterBarcodeType,
  QsrAutomationsDepartmentKey,
  QsrAutomationsItemIdKey,
  QsrAutomationsMode,
  QsrAutomationsModeHelper,
  SendDescriptionToPos,
  SimphonyInformationLineField,
  StarbucksConcept,
  StarbucksConceptHelper,
  StarbucksConsumptionType,
  StarbucksConsumptionTypeHelper,
  StarbucksCouponType,
  StarbucksMerchantId,
  StarbucksOrderType,
  StarbucksOrderTypeHelper,
  SyncMenuSectionImagesMode,
  TransactCampusCardAccountType,
  TransactCampusCardAccountTypeHelper,
  TransactCampusCardHostSystem,
  TransactCampusCardHostSystemHelper,
  UserRight,
  WorldpayGiftCardType,
  WorldpayMarketCode,
} from '@biteinc/enums';

import type * as Schema from './schema.js';
import { ipAddressRegexString, vistaSchemaVersionRegexString } from './validators.js';

export type I9nFieldSchemaOptions = {
  isSetAfterFirstSync?: true;
  isSetAfterSecondSync?: true;
  isSetAfterThirdSync?: true;
  identifiesTheLocation?: true;
  skipWhenPrefilling?: true;
  isRequiredForValidation?: true;
  requiredForOperations?: true;
  requiresUserInput?: true;
  requiresValidationIfChanged?: true;
};

export type AnyModelFieldWithIntegrationOptions = Schema.AnyModelField & I9nFieldSchemaOptions;

type BaseI9nSchema<
  System extends IntegrationSystem,
  FieldNames extends string = string,
> = Readonly<{
  /** @deprecated use system instead */
  _id: System;
  system: System;
  name: string;
  isBrandSpecific: boolean;
  iconUrl: string;
  searchTokens: string[];
  searchField: string | null;
  isDeprecated?: true;
  requiresOAuth?: boolean;
  scheduledSyncIntervalHours?: number;
  canUsePinata?: boolean;
  fields: Record<FieldNames, Readonly<AnyModelFieldWithIntegrationOptions>>;
  genericFieldDefaultValueByFieldName?: Record<string, any>;
}>;

type I9nSchemaNonPos<
  System extends Exclude<IntegrationSystem, IntegrationSystem.Pos>,
  FieldNames extends string = string,
> = BaseI9nSchema<System, FieldNames> &
  Readonly<
    | {
        requiredPosSystem: IntegrationSystem.Pos;
      }
    | {
        requiredPosSystem: null;
        incompatiblePosSystems: IntegrationSystem.Pos[];
      }
  >;

export type I9nSchemaCampusCard<
  System extends IntegrationSystem.CampusCard,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'campus-card';
    requiredPaymentSystem: IntegrationSystem.KioskPayment | null;
    supportsHid?: boolean;
    validatesTaxExemption?: true;
    validCardNumberInputRegex?: string;
    validCardNumberLengths?: number[];
    validPinInputRegex?: string;
    validPinLengths?: number[];
    readMethods: HardwareReadMethod[];
    canSync?: boolean;
  }>;

export type I9nSchemaCashMachine<
  System extends IntegrationSystem.CashMachine,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'cash-machine';
  }>;

export type I9nSchemaCompCard<
  System extends IntegrationSystem.CompCard,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'comp-card';

    supportsBarcode?: boolean;
    validCardNumberInputRegex: string;
    validCardNumberLengths: number[];
    validPinInputRegex?: string;
    validPinLengths?: number[];
    readMethods: HardwareReadMethod[];
  }>;

export type I9nSchemaEcommPayment<
  System extends IntegrationSystem.EcommPayment,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'ecomm-payment';

    minTransactionAmount: number;
    supportsStoringPaymentMethods: boolean;
  }>;

export type I9nSchemaEmailNotifier<
  System extends IntegrationSystem.SmsNotifier,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'email-notifier';
  }>;

export type I9nSchemaFulfillment<
  System extends IntegrationSystem.Fulfillment,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'fulfillment';

    /** Null indicates that this i9n does not send orders, therefore has no check ID */
    fulfillmentCheckIdKey: string | null;
    supportsOrderStatusPolling?: boolean;
    supportsOrderStatusWebhooks?: boolean;
    supportsOrderValidation: boolean;
    supportsThirdPartyOrderSending: boolean;

    diningOptionFields?: Record<string, AnyModelFieldWithIntegrationOptions>;
    diningOptionConditions?: Schema.Condition[];

    supportsFutureOrders?: boolean;

    canSync: boolean;
  }>;

export type I9nSchemaTableTracker<
  System extends IntegrationSystem.TableTracker,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'table-tracker';
    shouldSkipTableCustomerIdentifier?: boolean;
  }>;

export type I9nSchemaKioskPayment<
  System extends IntegrationSystem.KioskPayment,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'kiosk-payment';

    apiPayment: {
      canActivateTerminals: boolean;
      canCancelPayment: boolean;
      canDeactivateTerminals: boolean;
      canFetchListOfTerminals: boolean;
      canFetchTerminalStatus: boolean;
      canFetchTerminalModel: boolean;
      canUpdateTerminalIdleText: boolean;
      canWakeUpTerminals: boolean;
    } | null;
    canDisplayText: boolean;
    canSync: boolean;
    requiresRefundsToBeProcessedOnNative?: true;
    minTransactionAmount: number;
  }>;

export type I9nSchemaLoyaltyEarner<
  System extends IntegrationSystem.LoyaltyEarner,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<
    { type: 'loyalty-earner' } & (
      | {
          allowUserToLogIn: false;
          skipSendOrder: false;
        }
      | {
          allowUserToLogIn: true;
          skipSendOrder: true;
          supportedAuthMethods: LoyaltyAuthMethod[];
        }
    )
  >;

export type I9nSchemaLoyalty<
  System extends IntegrationSystem.Loyalty,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'loyalty';

    /** Null indicates that this i9n does not send orders, therefore has no check ID */
    loyaltyCheckIdKey: string | null;
    applyRewardSteps: ('loyalty' | 'pos')[];
    autoApplyFirstAvailableReward?: boolean;
    canSync: boolean;
    couponsAndLoyaltyEarningCompatible?: boolean;
    /** Indicates if the "Loyalty Discount on the POS" field is required for operations */
    requiresDiscountId: boolean;
    sendOrderWithoutAuth?: boolean;
    skipSendOrder?: boolean;
    waitForSendOrderBeforePrint?: boolean;
    supportsCompoundMenuItems: boolean;
    supportsOrderHistoryLookUp?: boolean;
    supportsCoupons?: boolean;
    supportsOrderValidation: boolean;
    supportsRewardsNoOrder?: boolean;
    supportsDistinguishingApplicableRewards?: boolean;
    supportsSignupInstantEnroll: boolean;
    createsTransactions?: boolean;
    supportedAuthMethods: LoyaltyAuthMethod[];
    /** Subset of `supportedAuthMethods` verifiable via OTP login; absent = no OTP. */
    otpEligibleAuthMethods?: LoyaltyAuthMethod[];
    supportsOrderCreation?: true;

    validCardNumberLengths?: number[];

    alwaysUsedAsPayment?: boolean;
    loyaltyNeedsSignOutAfterPartialPayment?: boolean;
    skipRedundantRewardFetch?: boolean; // skip get-available-rewards refetch when the order is unchanged

    fields: {
      authMethods: AnyModelFieldWithIntegrationOptions;
      hideStatusAndPoints: AnyModelFieldWithIntegrationOptions;
      useLoyaltyForPayments?: AnyModelFieldWithIntegrationOptions;
    } & Record<FieldNames, AnyModelFieldWithIntegrationOptions>;
  }>;

export type I9nSchemaMarketing<
  System extends IntegrationSystem.Marketing,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'marketing';

    supportsOrderValidation: boolean;
    skipSendOrder: boolean;

    /**
     * True if this integration supports surveys and the guest must be routed through Bite Admin in
     * order to access the survey.
     */
    routeSurveysThroughBite: boolean;
  }>;

export type I9nSchemaPos<
  System extends IntegrationSystem.Pos,
  FieldNames extends string = string,
> = BaseI9nSchema<System, FieldNames> &
  Readonly<{
    type: 'pos';

    /** Not all POS i9ns will use webhooks, but make it required to ensure it's never missing */
    webhookIdKey: 'i9nId' | 'priceOptions.i9nId' | 'posId';
    posCheckIdKey: string;
    locationIdKey: FieldNames;
    posOrderIdKey?: string;
    searchableFieldKeys?: string[];
    manuallySetMultipleOfSameMod?: boolean;
    supportsApplyingDiscounts?: boolean;
    supportsPosCoupons?: boolean;
    supportsPayingWithCash?: boolean;
    supportsTips?: boolean;
    supportsTipValidation?: boolean;
    supportsFutureOrders?: boolean;
    supportsItemRecipient?: boolean;
    supportsOrderStatusPolling?: boolean;
    supportsOrderStatusWebhooks?: boolean;
    syncsMultipleOfSameMod?: boolean;
    syncsOpenHoursTimetables?: boolean;
    syncsOpenHoursOverrides?: boolean;
    syncNotes?: string;
    syncsOrders?: boolean;
    syncsSupplementaryData?: boolean;
    supportsOrderCreation?: boolean;
    /** Whether the POS supports concurrent order validations (required for scanner-only mode) */
    supportsConcurrentValidation?: boolean;

    /** @description indicates if the POS client can use the loyalty's original reward ID instead of the loyaltyDiscountId on the POS integration */
    supportsUseLoyaltyI9nId?: true;

    requiresNewPosOrderOnLoyaltySignout?: boolean;

    couponsAndLoyaltyEarningCompatible?: boolean;
    supportsCompoundMenuItems?: true;
    syncsSections: boolean;
    syncsMenuStructures: boolean;
    /** Indicates if data about discounts will be stored in `syncedData.discountById` when synced */
    syncsDiscounts?: boolean;
    syncsTaxes?: boolean;
    /** Indicates if the manually created tax profile is synced from POS to items/mods */
    syncsItemTaxProfiles?: boolean;
    requiresManualDiscountManagement?: boolean;
    usesManualTaxProfiles?: true;
    diningOptionFields?: Record<string, AnyModelFieldWithIntegrationOptions>;

    syncsCalories: boolean;
    syncsMacronutrients: boolean;
    syncsBadges?: boolean;

    validateOnLoyaltyAuth?: true;
    /** Whether checkout can skip validation when WIP already validated the unchanged order */
    skipRedundantValidation?: boolean;
  }>;

export type I9nSchemaPosDataSource<
  System extends IntegrationSystem.PosDataSource,
  FieldNames extends string = string,
> = BaseI9nSchema<System, FieldNames> &
  Readonly<{
    type: 'pos-data-source';
  }>;

export type I9nSchemaSmsNotifier<
  System extends IntegrationSystem.SmsNotifier,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'sms-notifier';
    /** @deprecated use `type` instead */
    isSmsNotifier: true;

    canSync: boolean;
  }>;

export type I9nSchemaStoredValue<
  System extends IntegrationSystem.StoredValue,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'stored-value';
    /** @deprecated use `type` instead */
    isGiftCard: true;
    requiredPaymentSystem: IntegrationSystem.KioskPayment | null;

    usesApiPayment?: true;

    supportsBarcode?: boolean;
    supportsHid?: boolean;
    validatesTaxExemption?: true;
    /**
     * If true, the stored value charge controller will strip the order's tip
     * before charging this integration, since it cannot accept a tip portion.
     */
    stripTip?: true;
    validCardNumberInputRegex?: string;
    validCardNumberLengths?: number[];
    validPinInputRegex?: string;
    validPinLengths?: number[];
    readMethods: HardwareReadMethod[];
    canSync?: boolean;
    // make sure to implement in client
    supportsStoreValueBalanceLookup?: boolean;
  }>;

export type I9nSchemaSupplementaryMenuSource<
  System extends IntegrationSystem.SupplementaryMenuSource,
  FieldNames extends string = string,
> = I9nSchemaNonPos<System, FieldNames> &
  Readonly<{
    type: 'supplementary-menu-source';
  }>;

type BaseI9nSchemaBySystem = {
  [System in IntegrationSystem.CampusCard]: I9nSchemaCampusCard<System>;
} & {
  [System in IntegrationSystem.CashMachine]: I9nSchemaCashMachine<System>;
} & {
  [System in IntegrationSystem.CompCard]: I9nSchemaCompCard<System>;
} & {
  [System in IntegrationSystem.EcommPayment]: I9nSchemaEcommPayment<System>;
} & {
  [System in IntegrationSystem.EmailNotifier]: I9nSchemaEmailNotifier<System>;
} & {
  [System in IntegrationSystem.Fulfillment]: I9nSchemaFulfillment<System>;
} & {
  [System in IntegrationSystem.KioskPayment]: I9nSchemaKioskPayment<System>;
} & {
  [System in IntegrationSystem.Loyalty]: I9nSchemaLoyalty<System>;
} & {
  [System in IntegrationSystem.LoyaltyEarner]: I9nSchemaLoyaltyEarner<System>;
} & {
  [System in IntegrationSystem.Marketing]: I9nSchemaMarketing<System>;
} & {
  [System in IntegrationSystem.Pos]: I9nSchemaPos<System>;
} & {
  [System in IntegrationSystem.PosDataSource]: I9nSchemaPosDataSource<System>;
} & {
  [System in IntegrationSystem.SmsNotifier]: I9nSchemaSmsNotifier<System>;
} & {
  [System in IntegrationSystem.StoredValue]: I9nSchemaStoredValue<System>;
} & {
  [System in IntegrationSystem.SupplementaryMenuSource]: I9nSchemaSupplementaryMenuSource<System>;
} & {
  [System in IntegrationSystem.TableTracker]: I9nSchemaTableTracker<System>;
};

const instanceTypeFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'int',
  enum: IntegrationInstanceType,
  enumNameHelper: IntegrationInstanceTypeHelper.name,
  values: allEnumValues<IntegrationInstanceType>(IntegrationInstanceType),
  defaultValue: IntegrationInstanceType.Prod,
  displayName: 'Instance Type',
  ui: 'dropdown',
  required: true,
  requiresValidationIfChanged: true,
  writeRight: UserRight.ManageI9n,
};

// Whether this POS/fulfillment integration talks to a real/live system. Source of truth for
// order-send gating (see Integration.isLiveIntegration), independent of the location's state.
// Defaults on for prod; only prod instances can be live, so we only surface it for prod.
const isLiveIntegrationFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Is Live Integration',
  tooltip: {
    text: 'If checked, it means this is an integration for a live POS/Fulfillment system in an actual store. Only real kiosks and web clients will be able to send orders to this system.',
  },
  defaultValue: true,
  required: true,
  writeRight: UserRight.ManageI9n,
  conditions: [
    {
      field: 'instanceType',
      values: [IntegrationInstanceType.Prod],
    },
  ],
};

// Same as isLiveIntegrationFieldSchema, for POS/fulfillment schemas that have no instanceType
// dropdown to gate visibility on (always-prod systems, or apiServer-based ones).
const isLiveIntegrationUnconditionalFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Is Live Integration',
  tooltip: {
    text: 'If enabled, this integration talks to a real/live system. Real orders are only sent in prod by users with the Send Order To Live POS right.',
  },
  defaultValue: true,
  required: true,
  writeRight: UserRight.ManageI9n,
};

// The fields (with schemas) below are used outside of the actual integration so they must be
// defined exactly the same way.
const allowMultipleOfSameModFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Sync Multiple of Same Mod',
  tooltip: {
    text: 'Enabling this will prevent you from manually setting this on mod groups. Check with the brand and the POS to ensure that this should be enabled.',
  },
  writeRight: UserRight.ManageI9n,
};

const memoItemIDFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'keyString',
  displayName: 'Memo Item',
  isSetAfterFirstSync: true,
  ui: 'dropdown',
  tooltip: {
    text: 'Set after first sync. Only free menu items (non-mods) with no mod groups will be allowed. We will use this item to send order details to the POS such as pickup time.',
  },
  isNullable: true,
  writeRight: UserRight.ManageI9nLite,
};

const authMethodsFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'array',
  elementType: 'int',
  displayName: 'Auth Method',
  ui: 'table',
  placeholder: 'Select Auth Method',
  required: true,
  writeRight: UserRight.ManageI9n,
  minCount: 1,
  maxCount: 2,
};

// OTP is normally the identity of OloAccountsLoyalty (presence is the gate, no flag);
// `enableOtp` survives only for Pinata so e2e can drive OTP against fake Pinata
// send/verify endpoints without a real Olo dependency.
const enableOtpFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Enable One-Time Passcode (OTP) Login',
  tooltip: {
    text: 'Require guests logging in with email or phone to verify a one-time code (Pinata test loyalty only).',
  },
  writeRight: UserRight.ManageI9n,
};

// Olo Auth borderless (OTP) config lives on the OloAccountsLoyalty integration, not
// the Olo POS one, so the resolver answers "is OTP usable here" without a cross-integration lookup.
const oloAuthChannelIdFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'keyString',
  displayName: 'Olo Auth Channel ID',
  tooltip: {
    text: 'Identifier of the Olo channel for borderless (OTP) authentication, provided by your Olo GE Specialist. Required for an Olo Accounts Loyalty integration.',
  },
  // Required up front: the resolver treats a missing channel as `disabled`, a silent dead-end.
  required: true,
  writeRight: UserRight.ManageI9n,
};

const oloAuthApiUrlOverrideFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'keyString',
  displayName: 'Olo Auth API URL Override',
  tooltip: {
    text: 'Only to be used if Olo requests we use a non-default Olo Auth sandbox URL.',
  },
  writeRight: UserRight.ManageI9n,
  conditions: [
    {
      field: 'instanceType',
      values: [IntegrationInstanceType.Sandbox],
    },
  ],
};

// The Olo Auth Service has its own OAuth credentials, distinct from the Olo Ordering API
// key/secret; the borderless/OTP `/connect/token` (grant_type=otp) call must use these
// fields, not the Olo POS clientId/clientSecret (which the Token endpoint rejects with `invalid_client`).
const oloAuthClientIdFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'keyString',
  displayName: 'Olo Auth Client ID',
  tooltip: {
    text: 'OAuth Client ID for the Olo Auth Service (borderless/OTP). Distinct from the Olo Ordering API credentials — obtained from Olo Accounts onboarding. Required for an Olo Accounts Loyalty integration.',
  },
  required: true,
  maskInput: true,
  writeRight: UserRight.ManageI9n,
};

const oloAuthClientSecretFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'keyString',
  displayName: 'Olo Auth Client Secret',
  tooltip: {
    text: 'OAuth Client Secret for the Olo Auth Service (borderless/OTP). Distinct from the Olo Ordering API credentials — obtained from Olo Accounts onboarding. Required for an Olo Accounts Loyalty integration.',
  },
  required: true,
  maskInput: true,
  writeRight: UserRight.ManageI9n,
};

const ebtFieldSchemas: Record<string, AnyModelFieldWithIntegrationOptions> = {
  enableEbtCashBenefitAcceptance: {
    displayName: 'Enable EBT Cash Benefits',
    type: 'bool',
    writeRight: UserRight.ManageI9n,
  },
  enableEbtFoodStampAcceptance: {
    displayName: 'Enable EBT Food Stamps',
    type: 'bool',
    writeRight: UserRight.ManageI9n,
  },
};

const lookupOrderHistoryOnLoyaltySchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Lookup Order History on Loyalty',
  tooltip: {
    text: "If enabled, we will look up the user's order history on loyalty to determine if they are a first time user.",
  },
  writeRight: UserRight.ManageI9n,
};

const promptForDonationSchema: AnyModelFieldWithIntegrationOptions = {
  displayName: 'Prompt for Donation Round Up',
  type: 'stringEnum',
  enum: DonationRoundUpPromptType,
  defaultValue: DonationRoundUpPromptType.None,
  tooltip: {
    text: 'If enabled, the user will be prompted to round up their order to the nearest dollar for charity.',
  },
  writeRight: UserRight.ManageI9n,
};

const groupRewardsByTypeFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Group rewards by type (Vitrine)',
  tooltip: {
    text: 'Group rewards by type on the loyalty screen.',
  },
};

const hideStatusAndPointsFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Hide Status and Points',
  tooltip: {
    text: `Some Punchh+Olo clients don't actually use points, turning this on will hide status & points.`,
  },
};

const overrideDistinguishingApplicableRewardsFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Override distinguishing applicable rewards',
  tooltip: {
    text: 'If enabled, the loyalty screen will not distinguish applicable rewards.',
  },
  writeRight: UserRight.ManageI9n,
};

const skipIfCartOnlyContainsCategoriesFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'array',
  elementType: 'int',
  elementValues: allEnumValues<MenuItemCategory>(MenuItemCategory),
  displayName: 'Item Categories to Skip If Cart Only Contains',
  ui: 'table',
  placeholder: 'Select Item Categories',
  minCount: 1,
  exclusiveTo: 'vitrine',
  tooltip: {
    text: 'If the order contains ONLY items in the selected categories, the table tracker will not be dispensed.',
  },
  writeRight: UserRight.ManageI9n,
};

const shouldSkipTableCustomerIdentifierFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Skip Table Customer Identifier',
  tooltip: {
    text: 'If enabled, the table customer identifier will not be collected.',
  },
  writeRight: UserRight.ManageI9n,
};

const minimumTableNumberSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'int',
  displayName: 'Minimum Table Number',
  writeRight: UserRight.ManageI9n,
  min: 1,
  max: 999,
  tooltip: {
    text: 'The minimum table number to dispense the table tracker for.',
  },
};
const maximumTableNumberSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'int',
  displayName: 'Maximum Table Number',
  writeRight: UserRight.ManageI9n,
  min: 1,
  max: 999,
  tooltip: {
    text: 'The maximum table number to dispense the table tracker for.',
  },
};

// If you add a new tender type you need to update getTenderTypeIdFromCardSchemeId in the posOrderHelper
// If a new pos wants to use these you need to update getCardDependentTenderTypeId in the posOrderHelper
function generateCardSpecificTenderTypeSchema(config: {
  isSetAfterFirstSync?: true;
  isSetAfterSecondSync?: true;
  isSetAfterThirdSync?: true;
  tooltipText: string;
}): Record<string, AnyModelFieldWithIntegrationOptions> {
  const { isSetAfterFirstSync, isSetAfterSecondSync, isSetAfterThirdSync, tooltipText } = config;
  const fieldInfo = {
    ...(isSetAfterFirstSync && { isSetAfterFirstSync }),
    ...(isSetAfterSecondSync && { isSetAfterSecondSync }),
    ...(isSetAfterThirdSync && { isSetAfterThirdSync }),
    tooltip: {
      text: tooltipText,
    },
  };
  return {
    visaTenderTypeID: {
      type: 'keyString',
      displayName: 'Visa Tender Type',
      ui: 'dropdown',
      requiredForOperations: true,
      writeRight: UserRight.ManageI9nLite,
      ...fieldInfo,
    },
    masterCardTenderTypeID: {
      type: 'keyString',
      displayName: 'Mastercard Tender Type',
      ui: 'dropdown',
      requiredForOperations: true,
      writeRight: UserRight.ManageI9nLite,
      ...fieldInfo,
    },
    discoverTenderTypeID: {
      type: 'keyString',
      displayName: 'Discover Tender Type',
      ui: 'dropdown',
      requiredForOperations: true,
      writeRight: UserRight.ManageI9nLite,
      ...fieldInfo,
    },
    amExTenderTypeID: {
      type: 'keyString',
      displayName: 'Amex Tender Type',
      ui: 'dropdown',
      requiredForOperations: true,
      writeRight: UserRight.ManageI9nLite,
      ...fieldInfo,
    },
    interacTenderTypeID: {
      type: 'keyString',
      displayName: 'Interac Tender Type',
      ui: 'dropdown',
      requiredForOperations: true,
      writeRight: UserRight.ManageI9nLite,
      ...fieldInfo,
    },
    giftCardTenderTypeID: {
      type: 'keyString',
      displayName: 'Gift Card Tender Type',
      ui: 'dropdown',
      requiredForOperations: true,
      writeRight: UserRight.ManageI9nLite,
      ...fieldInfo,
    },
    mealExchangeTenderTypeID: {
      type: 'keyString',
      displayName: 'Meal Exchange Tender Type',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Can be set after first POS sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    otherTenderTypeID: {
      type: 'keyString',
      displayName: 'Other Tender Type',
      ui: 'dropdown',
      requiredForOperations: true,
      writeRight: UserRight.ManageI9nLite,
      ...fieldInfo,
    },
  };
}

const sendOrderWithoutAuthFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Send order to loyalty without auth',
  tooltip: {
    text: 'Send the order to loyalty for earning without authing user on loyalty.',
  },
  writeRight: UserRight.ManageI9n,
};

const useLoyaltyForPaymentsFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'bool',
  displayName: 'Use loyalty for payments',
  tooltip: {
    text: 'If enabled, the user will have the option to pay using loyalty at checkout.',
  },
};

const OmnivoreSchema: I9nSchemaPos<IntegrationSystem.Omnivore> = {
  _id: IntegrationSystem.Omnivore,
  system: IntegrationSystem.Omnivore,
  name: 'Omnivore',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-omnivore.png',
  searchTokens: ['omnivore'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'omnivoreTicketNumber',
  locationIdKey: 'locationID',
  manuallySetMultipleOfSameMod: true,
  supportsApplyingDiscounts: true,
  supportsPayingWithCash: true,
  supportsCompoundMenuItems: true,
  syncsSections: true,
  syncsMenuStructures: false, // Omnivore's sections are embedded in items so there's no order in which sections actually appear on omnivore
  syncsDiscounts: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    locationID: {
      type: 'keyString',
      required: true,
      displayName: 'Omnivore LocationID',
      identifiesTheLocation: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    posType: {
      displayName: 'POS Type',
      type: 'stringEnum',
      enum: OmnivorePosType,
      enumNameHelper: OmnivorePosTypeHelper.name,
      ui: 'dropdown',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    employeeID: {
      type: 'keyString',
      displayName: 'POS Employee',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    revenueCenterID: {
      type: 'keyString',
      displayName: 'Revenue Center',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    allowMultipleOfSameMod: allowMultipleOfSameModFieldSchema,
    memoItemID: memoItemIDFieldSchema,
    useUserInfo: {
      type: 'bool',
      displayName: 'Use user info instead of memo item',
      writeRight: UserRight.ManageI9nLite,
    },
    subModIdForDeselectedMods: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Send Deselected Mods With Sub Mod',
      tooltip: {
        text: 'If set default mods that are deselected will be sent. The chosen sub mod will be added to deselected mod.',
      },
      placeholder: 'Currently Not Sending',
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
    },
    sendDefaultMods: {
      type: 'bool',
      displayName: 'Send Default Mods',
      tooltip: {
        text: 'If enabled, then mods selected by default will be sent to Omnivore',
      },
      writeRight: UserRight.ManageI9nLite,
      defaultValue: true,
    },
    syncMultipleModPriceOptions: {
      type: 'bool',
      displayName: 'Sync Multiple Mod Price Options',
      tooltip: {
        text: 'If enabled, multiple price options will be synced for mods.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'keyString',
      displayName: 'POS Order Type',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    ...generateCardSpecificTenderTypeSchema({
      isSetAfterFirstSync: true,
      tooltipText: 'Not all Tender Types accept our orders. Set after first sync.',
    }),
    fullyDiscountedOrderTenderTypeID: {
      type: 'keyString',
      displayName: 'Tender Type for Fully Discounted Orders',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Not all Tender Types accept our orders. Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
  },
};

const OloSchema: I9nSchemaPos<IntegrationSystem.Olo> = {
  _id: IntegrationSystem.Olo,
  system: IntegrationSystem.Olo,
  name: 'OLO',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-olo.png',
  searchTokens: ['olo'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'oloId',
  searchableFieldKeys: ['oloOrderId'],
  /**
   * @todo DELETE once all locations use Orders API v2
   */
  posOrderIdKey: 'oloBasketId',
  locationIdKey: 'locationID',
  supportsPosCoupons: true,
  supportsPayingWithCash: true,
  supportsTips: false,
  supportsTipValidation: true,
  supportsFutureOrders: true,
  supportsItemRecipient: false,
  supportsOrderStatusPolling: true,
  supportsOrderStatusWebhooks: true,
  syncsSupplementaryData: true,
  syncsSections: true,
  syncsMenuStructures: true,
  syncsMultipleOfSameMod: true,
  syncsOpenHoursTimetables: true,
  syncsOpenHoursOverrides: true,
  supportsOrderCreation: true,
  requiresNewPosOrderOnLoyaltySignout: true,
  couponsAndLoyaltyEarningCompatible: true,
  syncsBadges: true,
  syncsCalories: true,
  syncsMacronutrients: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    alternativeSandboxUrl: {
      type: 'keyString',
      displayName: 'Alternative Sandbox URL',
      tooltip: {
        text: 'Only to be used if we are testing new olo features and they request we use a different sandbox url.',
      },
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'instanceType',
          values: [IntegrationInstanceType.Sandbox],
        },
      ],
    },
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'See OLO Developer Portal for value. If not set, uses Bite default, which is generally not desirable.',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    clientId: {
      type: 'keyString',
      displayName: 'ClientID',
      required: true,
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'See OLO Developer Portal for value. If not set, uses Bite default, which is generally not desirable.',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    clientSecret: {
      type: 'keyString',
      displayName: 'Client Secret',
      required: true,
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'See OLO Developer Portal for value. If not set, uses Bite default, which is generally not desirable.',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    locationID: {
      requiredForOperations: true,
      type: 'keyString',
      displayName: 'OLO Location',
      identifiesTheLocation: true,
      isSetAfterFirstSync: true,
      ui: 'dropdown',
      tooltip: {
        text: 'The specific restaurant ID. Available after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    syncMenuSectionImages: {
      displayName: 'Sync Menu Section Images',
      type: 'stringEnum',
      enum: SyncMenuSectionImagesMode,
      defaultValue: SyncMenuSectionImagesMode.DoNotSync,
      writeRight: UserRight.ManageI9n,
      ui: 'dropdown',
    },
    syncModGroupDescriptions: {
      type: 'bool',
      defaultValue: true,
      displayName: 'Sync Mod Group Descriptions',
      writeRight: UserRight.ManageI9n,
    },
    syncFullScreenFlow: {
      type: 'bool',
      displayName: 'Sync Full Screen Flow',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Requires custom metadata on relevant olo items to say BuilderMode=1 (originally for Newks only)',
        isWarning: true,
      },
    },
    utensilsItemID: {
      type: 'keyString',
      displayName: 'Utensils Item',
      ui: 'dropdown',
      isSetAfterSecondSync: true,
      tooltip: {
        text: 'Set after first sync. Only select "Utensils", we will use this for customers to opt in for utensils',
      },
      writeRight: UserRight.ManageI9nLite,
      isNullable: true,
    },
    sendGuestEmailReceipt: {
      type: 'bool',
      displayName: 'Send Guest Email',
      tooltip: {
        text: 'Send the guest email in the email field if supplied by the guest as part of customer identifiers for a non web/catering channel.',
      },
      writeRight: UserRight.ManageI9n,
    },
    loyaltyEnforcesPhoneNumber: {
      type: 'bool',
      displayName: 'Loyalty Enforces Phone',
      tooltip: {
        text: 'If a user is authed into loyalty and the phone number is required, then we will not override the phone number field with our fake one.',
      },
      writeRight: UserRight.ManageI9n,
    },
    customFields: {
      type: 'array',
      elementType: 'object',
      ui: 'accordion',
      uiSubtype: 'OloCustomFields',
      displayName: 'Custom Fields',
      writeRight: UserRight.ManageI9n,
      fields: {
        customFieldKey: {
          type: 'keyString',
          required: true,
          displayName: 'Custom Field Key',
          writeRight: UserRight.ManageI9n,
        },
        orderField: {
          type: 'keyString',
          required: true,
          ui: 'dropdown',
          displayName: 'Value',
          writeRight: UserRight.ManageI9n,
        },
        templateString: {
          type: 'keyString',
          required: true,
          displayName: 'Custom Value Template',
          validationRegex:
            '^(([a-zA-Z0-9\\-\\s\\.#:\\[\\]]+)|(?:{{{?(\\/[a-z]+)?(#[a-z]+)?[a-zA-Z_\\s]*}?}}))+$',
          tooltip: {
            text: 'Consult eng for help setting this up. Available tokens: ORDER_NUMBER, ORDER_TYPE, GUEST_NAME, GUEST_PHONE_NUMBER, TABLE_NUMBER, OUTPOST_NAME, IS_CASH, KIOSK_NAME',
          },
          writeRight: UserRight.ManageI9n,
          conditions: [
            {
              field: 'orderField',
              values: [OloCustomKeyOrderField.TemplateString],
            },
          ],
        },
        defaultValue: {
          type: 'keyString',
          displayName: 'Default Value',
          tooltip: {
            text: 'If the value for the order field is empty, this value will be used.',
          },
          conditions: [
            {
              field: 'orderField',
              values: [
                OloCustomKeyOrderField.TemplateString,
                OloCustomKeyOrderField.GuestPhoneNumber,
                OloCustomKeyOrderField.GuestEmail,
              ],
            },
          ],
        },
      },
    },
    setTableNumberAs: {
      type: 'stringEnum',
      enum: OloSetTableNumberAs,
      enumNameHelper: OloSetTableNumberAsHelper.name,
      ui: 'dropdown',
      displayName: 'Set Table Number as',
      tooltip: {
        text: 'Only enable for Starbucks or Portillos! Handoff mode must be dine in!',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9n,
      isNullable: true,
    },
    orderHistorySource: {
      type: 'stringEnum',
      enum: OloOrderHistorySource,
      enumNameHelper: OloOrderHistorySourceHelper.name,
      ui: 'dropdown',
      displayName: 'Order History Source',
      tooltip: {
        text: 'Controls which OLO endpoint is used for order history lookup.',
      },
      writeRight: UserRight.ManageI9n,
      isNullable: true,
    },
    preferMarketplaceProductImages: {
      type: 'bool',
      displayName: 'Prefer "Marketplace Product" Images',
      tooltip: {
        text: 'If enabled, "marketplace product" images will be the preferred type when syncing images from Olo. These images may be higher quality and could resolve display issues.',
      },
      writeRight: UserRight.ManageI9n,
    },
    shouldCoalesceModsAndModGroups: {
      type: 'bool',
      displayName: 'Coalesce Mods and Mod Groups',
      tooltip: {
        text: 'If enabled, the mods and mod groups will be coalesced into shorter lists.',
      },
      writeRight: UserRight.ManageI9n,
    },
    shouldCoalesceModGroupsWithoutChainId: {
      type: 'bool',
      displayName: 'Coalesce Mod Groups Without Chain ID',
      tooltip: {
        text: 'If enabled, mod groups will be coalesced by ignoring the chain ID. This will result in fewer mod groups, but mod groups may be shared by more items and modifiers.',
      },
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'shouldCoalesceModsAndModGroups',
          values: [true],
        },
      ],
    },
    shouldSquashSingletonModLayers: {
      type: 'bool',
      displayName: 'Squash Singleton Mod Layers',
      tooltip: {
        text: 'If enabled, a mod group with only a single non-leaf mod will not by synced.',
      },
      writeRight: UserRight.ManageI9n,
    },
    shouldSquashRedundantModLayers: {
      type: 'bool',
      displayName: 'Squash Redundant Mod Layers',
      tooltip: {
        text: 'If enabled, intermediary mod groups that do not allow guest customization will not be synced. For example, a mod group that requires three choices and only has three mods to choose from is considered redundant.',
      },
      writeRight: UserRight.ManageI9n,
    },
    shouldUseI9nIdFallback: {
      type: 'bool',
      displayName: 'Use Olo ID Fallback',
      tooltip: {
        text: 'If we fail to match an Olo model to a Bite model during sync using the Olo ID, then we will attempt to match using the POS ID. If POS ID does not match, then we will attempt to use the name.',
      },
    },
    combineMultiplePaymentsIntoOne: {
      displayName: 'Combine Multiple Payments Into One',
      type: 'bool',
      tooltip: {
        text: 'Check this only if the brand does not want to enable the use of /multiplepayments endpoint on OLO',
      },
      writeRight: UserRight.ManageI9n,
    },
    promptForDonationRoundUp: promptForDonationSchema,
    sendCustomerIdAsPhoneNumber: {
      type: 'bool',
      displayName: 'Customer Identifier as Phone Number',
      tooltip: {
        text: 'If we have a customer identifier we will send it in the contact number field instead of customer phone number.',
      },
      writeRight: UserRight.ManageI9n,
    },
    attemptToResolveBasket223ValidationError: {
      displayName: 'Attempt to Resolve Basket 223 Validation Error',
      type: 'bool',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Vitrine only. GCN creates a new basket each time Olo responds with 200/223',
      },
    },
  },
  diningOptionFields: {
    orderTypeID: {
      displayName: 'Handoff Mode',
      type: 'stringEnum',
      enum: OloHandoffMode,
      enumNameHelper: OloHandoffModeHelper.name,
      defaultValue: OloHandoffMode.Pickup,
      required: true,
      requiredForOperations: true,
      requiresValidationIfChanged: true,
      ui: 'dropdown',
      isSetAfterSecondSync: true,
      tooltip: {
        text: 'More values could become available after the first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    taxExemptOrderTypeID: {
      displayName: 'Tax Exempt Handoff Mode',
      type: 'stringEnum',
      enum: OloHandoffMode,
      enumNameHelper: OloHandoffModeHelper.name,
      isSetAfterSecondSync: true,
      tooltip: {
        text: 'The handoff mode to be used for tax-exempt orders (e.g., EBT Food). Set after second sync.',
      },
      ui: 'dropdown',
      writeRight: UserRight.ManageI9nLite,
    },
  },
  genericFieldDefaultValueByFieldName: {
    syncBadges: true,
  },
};

const OloStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.OloStoredValue> = {
  _id: IntegrationSystem.OloStoredValue,
  system: IntegrationSystem.OloStoredValue,
  name: 'OLO Stored Value',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-olo-stored-value.png',
  searchTokens: ['olo-stored-value'],
  searchField: null,
  type: 'stored-value',
  isGiftCard: true,
  supportsBarcode: true,
  requiredPosSystem: IntegrationSystem.Olo,
  requiredPaymentSystem: null,
  validCardNumberLengths: [14, 16, 19, 21],
  validPinLengths: [3, 4, 6, 8],
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Scanner, HardwareReadMethod.Manual],
  supportsStoreValueBalanceLookup: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    requiresPin: {
      type: 'bool',
      displayName: 'Show PIN Input on Gift Card view',
      writeRight: UserRight.ManageI9n,
      defaultValue: true,
    },
    opsApiClientId: {
      type: 'keyString',
      displayName: 'Ops Api ClientID',
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'See OLO Developer Portal for value. If not set, uses Bite default, which is generally not desirable.',
      },
      writeRight: UserRight.ManageI9n,
    },
    opsApiClientSecret: {
      type: 'keyString',
      displayName: 'Ops Api Client Secret',
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'See OLO Developer Portal for value. If not set, uses Bite default, which is generally not desirable.',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    hasBarcodes: {
      type: 'bool',
      displayName: 'Has Barcodes',
      tooltip: {
        text: 'Set to true if the gift cards have a scannable barcode.',
      },
      writeRight: UserRight.ManageI9n,
      defaultValue: true,
    },
  },
};

const OloPayKioskOrderingApiSchema: I9nSchemaKioskPayment<IntegrationSystem.OloPayKioskOrderingApi> =
  {
    _id: IntegrationSystem.OloPayKioskOrderingApi,
    system: IntegrationSystem.OloPayKioskOrderingApi,
    name: 'Olo Pay Kiosk (Ordering API)',
    isBrandSpecific: false,
    iconUrl: 'https://static.bureau.getbite.com/images/icon-olo-pay-kiosk.png',
    searchTokens: ['olo-pay-kiosk'],
    searchField: 'locationID',
    type: 'kiosk-payment',
    apiPayment: {
      canActivateTerminals: false,
      canCancelPayment: true,
      canDeactivateTerminals: false,
      canFetchListOfTerminals: false,
      canFetchTerminalStatus: true,
      canFetchTerminalModel: false,
      canUpdateTerminalIdleText: false,
      canWakeUpTerminals: false,
    },
    canDisplayText: false,
    canSync: true,
    minTransactionAmount: 0,
    requiredPosSystem: IntegrationSystem.Olo,
    fields: {
      instanceType: instanceTypeFieldSchema,
      alternativeSandboxUrl: {
        type: 'keyString',
        displayName: 'Alternative Sandbox URL',
        tooltip: {
          text: 'Only to be used if we are testing new olo features and they request we use a different sandbox url.',
        },
        requiresValidationIfChanged: true,
        writeRight: UserRight.ManageI9n,
        conditions: [
          {
            field: 'instanceType',
            values: [IntegrationInstanceType.Sandbox],
          },
        ],
      },
      alternativeOpsApiSandboxUrl: {
        type: 'keyString',
        displayName: 'Alternative Ops Api Sandbox URL',
        tooltip: {
          text: 'Only to be used if we are testing new olo features and they request we use a different sandbox url.',
        },
        requiresValidationIfChanged: true,
        writeRight: UserRight.ManageI9n,
        conditions: [
          {
            field: 'instanceType',
            values: [IntegrationInstanceType.Sandbox],
          },
        ],
      },
      apiKey: {
        type: 'keyString',
        displayName: 'API Key',
        requiresValidationIfChanged: true,
        tooltip: {
          text: 'See OLO Developer Portal for value - should match POS Integration. If not set, uses Bite default, which is generally not desirable.',
        },
        writeRight: UserRight.ManageI9n,
        maskInput: true,
      },
      clientId: {
        type: 'keyString',
        displayName: 'ClientID',
        requiresValidationIfChanged: true,
        tooltip: {
          text: 'See OLO Developer Portal for value - should match POS Integration. If not set, uses Bite default, which is generally not desirable.',
        },
        writeRight: UserRight.ManageI9n,
        maskInput: true,
      },
      clientSecret: {
        type: 'keyString',
        displayName: 'Client Secret',
        requiresValidationIfChanged: true,
        tooltip: {
          text: 'See OLO Developer Portal for value - should match POS Integration. If not set, uses Bite default, which is generally not desirable.',
        },
        writeRight: UserRight.ManageI9n,
        maskInput: true,
      },
      locationID: {
        requiredForOperations: true,
        type: 'keyString',
        displayName: 'OLO Location',
        ui: 'dropdown',
        isSetAfterFirstSync: true,
        identifiesTheLocation: true,
        tooltip: {
          text: 'The specific restaurant ID. Available after the first sync.',
        },
        writeRight: UserRight.ManageI9n,
      },
      opsApiClientId: {
        type: 'keyString',
        displayName: 'Ops Api ClientID',
        requiresValidationIfChanged: true,
        tooltip: {
          text: 'See OLO Developer Portal for value. If not set, uses Bite default, which is generally not desirable.',
        },
        writeRight: UserRight.ManageI9n,
      },
      opsApiClientSecret: {
        type: 'keyString',
        displayName: 'Ops Api Client Secret',
        requiresValidationIfChanged: true,
        tooltip: {
          text: 'See OLO Developer Portal for value. If not set, uses Bite default, which is generally not desirable.',
        },
        writeRight: UserRight.ManageI9n,
        maskInput: true,
      },
      enableTerminalStatusFetch: {
        type: 'bool',
        displayName: 'Enable Terminal Status Fetch',
        biteOnly: true,
        writeRight: UserRight.BiteEng,
        tooltip: {
          text: 'When enabled, the kiosk will fetch the terminal status from Olo. While the API stabilizes, this is gated to Bite Engineering.',
        },
      },
    },
  };

const OloPayKioskPaymentApiSchema: I9nSchemaKioskPayment<IntegrationSystem.OloPayKioskPaymentApi> =
  {
    _id: IntegrationSystem.OloPayKioskPaymentApi,
    system: IntegrationSystem.OloPayKioskPaymentApi,
    name: 'Olo Pay Kiosk (Payment API)',
    isBrandSpecific: false,
    iconUrl: 'https://static.bureau.getbite.com/images/icon-olo-pay-kiosk.png',
    searchTokens: ['olo-pay-kiosk'],
    searchField: 'merchantId',
    type: 'kiosk-payment',
    apiPayment: {
      canActivateTerminals: false,
      canCancelPayment: true,
      canDeactivateTerminals: false,
      canFetchListOfTerminals: false,
      canFetchTerminalStatus: true,
      canFetchTerminalModel: false,
      canUpdateTerminalIdleText: false,
      canWakeUpTerminals: false,
    },
    canDisplayText: false,
    canSync: true,
    minTransactionAmount: 0,
    requiredPosSystem: null,
    incompatiblePosSystems: [],
    fields: {
      instanceType: instanceTypeFieldSchema,
      clientId: {
        displayName: 'ClientID',
        type: 'keyString',
        required: true,
        requiresValidationIfChanged: true,
        writeRight: UserRight.ManageI9n,
        maskInput: true,
      },
      clientSecret: {
        displayName: 'Client Secret',
        type: 'keyString',
        required: true,
        requiresValidationIfChanged: true,
        writeRight: UserRight.ManageI9n,
        maskInput: true,
      },
      merchantId: {
        displayName: 'Merchant ID',
        type: 'keyString',
        required: true,
        requiresValidationIfChanged: true,
        identifiesTheLocation: true,
        writeRight: UserRight.ManageI9n,
        maskInput: true,
      },
    },
  };

const punchhBusinessKeyFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'keyString',
  displayName: 'Punchh Business Key',
  tooltip: {
    text: 'Business key provided by Punchh. Sent as btoken on POS API requests.',
  },
  requiresValidationIfChanged: true,
  writeRight: UserRight.ManageI9n,
  maskInput: true,
};

const PunchhSchema: I9nSchemaLoyalty<IntegrationSystem.Punchh> = {
  _id: IntegrationSystem.Punchh,
  system: IntegrationSystem.Punchh,
  name: 'Punchh Direct',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-punchh.png',
  searchTokens: ['punchh'],
  searchField: 'locationKey',
  loyaltyCheckIdKey: 'transaction_no',
  type: 'loyalty',
  supportsOrderValidation: true,
  canSync: true,
  canUsePinata: true,
  requiresDiscountId: true,
  applyRewardSteps: ['loyalty', 'pos'],
  supportsCompoundMenuItems: true,
  couponsAndLoyaltyEarningCompatible: true,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: true,
  supportsCoupons: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.Olo, IntegrationSystem.SpotOn],
  supportedAuthMethods: [LoyaltyAuthMethod.Barcode, LoyaltyAuthMethod.PhoneNumber],
  supportsOrderCreation: true,
  supportsSignupInstantEnroll: true,

  fields: {
    instanceType: instanceTypeFieldSchema,
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PunchhApiServer>(PunchhApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    locationKey: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    businessKey: punchhBusinessKeyFieldSchema,
    enableAuthedLoyalty: {
      type: 'bool',
      displayName: 'Enable Authed Loyalty',
      tooltip: {
        text: 'Allows the guest to sign in to their loyalty account. Only works at locations with Brink Direct integrations.',
      },
      writeRight: UserRight.ManageI9n,
    },
    sendOrderWithoutAuth: sendOrderWithoutAuthFieldSchema,
    showEarnedPointEstimate: {
      type: 'bool',
      displayName: 'Show earned point estimate',
      tooltip: {
        text: 'Upon login, show an estimate of points earned based on the order subTotal.',
      },
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    earnedPointsMultiplier: {
      type: 'float',
      displayName: 'Earned points multiplier',
      tooltip: {
        text: 'The ratio of dollars earned to points. This value will be used as dollar_amount * value = points_estimated.',
      },
      writeRight: UserRight.ManageI9n,
    },
    allowRedemptionCodeWithReward: {
      type: 'bool',
      displayName: 'Allow redemption code with reward',
      tooltip: {
        text: 'Allows the user to also redeem a redemption code (points) along with loyalty reward(s).',
      },
    },
    printerBarcodeType: {
      displayName: 'Printer Barcode Type',
      type: 'stringEnum',
      enum: PunchhPrinterBarcodeType,
      ui: 'dropdown',
      tooltip: {
        text: 'The type of barcode to print on the receipt.',
      },
      required: true,
    },
    addQrCodeToEmailReceipt: {
      type: 'bool',
      displayName: 'Add QR code to email receipt',
      tooltip: {
        text: 'Add the Punchh QR code to the email receipt for user scanning and earning.',
      },
    },
    useLoyaltyForPayments: useLoyaltyForPaymentsFieldSchema,
    showPointsAsDollars: {
      type: 'bool',
      displayName: 'Show points as dollars',
      tooltip: {
        text: 'Show the points on account as dollars.',
      },
    },
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    allowPointsBalanceRedemption: {
      type: 'bool',
      displayName: 'Allow points balance redemption',
      tooltip: {
        text: 'Allow the user to redeem their points balance.',
      },
    },
    overrideDistinguishingApplicableRewards: overrideDistinguishingApplicableRewardsFieldSchema,
    showAllProgramRewards: {
      type: 'bool',
      displayName: 'Show all program rewards',
      tooltip: {
        text: 'Show all rewards from the program.',
      },
    },
    allowUnauthedCouponsInLoyalty: {
      type: 'bool',
      displayName: 'Allow unauthed coupons in loyalty',
      tooltip: {
        text: 'Allow unauthed coupons in loyalty',
        isWarning: true,
      },
    },
    sendDiscountI9nId: {
      type: 'bool',
      displayName: 'Send Discount I9n ID',
      tooltip: {
        text: 'If enabled, the discount I9n ID will be sent to Punchh instead of the "Loyalty Discount on the POS"',
      },
      writeRight: UserRight.ManageI9n,
    },
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
    sendBrinkOrderId: {
      type: 'bool',
      displayName: 'Send Brink Order ID',
      tooltip: {
        text: 'Brink Only. If enabled, the Brink order ID will be sent to Punchh.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const AttentiveSchema: I9nSchemaMarketing<IntegrationSystem.Attentive> = {
  _id: IntegrationSystem.Attentive,
  system: IntegrationSystem.Attentive,
  name: 'Attentive',
  isBrandSpecific: false,
  type: 'marketing',
  searchTokens: ['attentive'],
  searchField: null,
  supportsOrderValidation: false,
  skipSendOrder: false,
  routeSurveysThroughBite: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-attentive.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    signUpSourceId: {
      type: 'keyString',
      displayName: 'Sign Up Source ID',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
  },
};

const VibesSchema: I9nSchemaMarketing<IntegrationSystem.Vibes> = {
  _id: IntegrationSystem.Vibes,
  system: IntegrationSystem.Vibes,
  name: 'Vibes',
  isBrandSpecific: false,
  type: 'marketing',
  searchTokens: ['vibes'],
  searchField: null,
  supportsOrderValidation: false,
  skipSendOrder: false,
  routeSurveysThroughBite: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-vibes.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    companyId: {
      type: 'keyString',
      displayName: 'Company Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    acquisitionCampaignId: {
      type: 'keyString',
      displayName: 'Acquisition Campaign ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    alreadySubscribedEventType: {
      type: 'keyString',
      displayName: '"Already Subscribed" Event Type',
      requiresValidationIfChanged: true,
      isNullable: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'If the customer is already subscribed to the acquisition campaign, then an event will be created with this type.',
      },
    },
  },
};

const QualtricsSchema: I9nSchemaMarketing<IntegrationSystem.Qualtrics> = {
  _id: IntegrationSystem.Qualtrics,
  system: IntegrationSystem.Qualtrics,
  name: 'Qualtrics',
  isBrandSpecific: false,
  type: 'marketing',
  searchTokens: ['qualtrics'],
  searchField: null,
  supportsOrderValidation: false,
  skipSendOrder: false,
  routeSurveysThroughBite: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-qualtrics.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    surveyUrl: {
      type: 'url',
      displayName: 'Survey URL',
      required: true,
      tooltip: {
        text: 'Ex: https://sample.example.com/path/to/survey',
      },
    },
    header: {
      type: 'codeString',
      displayName: 'Header Text',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Available tags: <s1>, <s2>, and <s3> for text size; <b> for bold; <qrcode> for QR codes.',
      },
    },
    footer: {
      type: 'codeString',
      displayName: 'Footer Text',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Available tags: <s1>, <s2>, and <s3> for text size; <b> for bold; <qrcode> for QR codes.',
      },
    },
    printAboveLoyalty: {
      type: 'bool',
      displayName: 'Print Above Loyalty Barcode',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'When enabled, the survey code will print above the loyalty barcode instead of below it.',
      },
    },
  },
};

const SmgSchema: I9nSchemaMarketing<IntegrationSystem.Smg> = {
  _id: IntegrationSystem.Smg,
  system: IntegrationSystem.Smg,
  name: 'SMG',
  isBrandSpecific: false,
  type: 'marketing',
  searchTokens: ['smg'],
  searchField: 'storeID',
  supportsOrderValidation: true,
  skipSendOrder: true,
  routeSurveysThroughBite: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-smg.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    companyId: {
      displayName: 'Company ID',
      type: 'keyString',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    storeID: {
      displayName: 'Store ID',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    transactionLowerBound: {
      type: 'int',
      required: true,
      displayName: 'Transaction No. Lower Bound',
      writeRight: UserRight.ManageI9n,
    },
    transactionUpperBound: {
      type: 'int',
      required: true,
      displayName: 'Transaction No. Upper Bound',
      writeRight: UserRight.ManageI9n,
    },
    header: {
      type: 'codeString',
      displayName: 'Header Text',
      writeRight: UserRight.ManageI9n,
    },
    footer: {
      type: 'codeString',
      displayName: 'Footer Text',
      writeRight: UserRight.ManageI9n,
    },
    printAboveLoyalty: {
      type: 'bool',
      displayName: 'Print Above Loyalty Barcode',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'When enabled, the SMG survey code will print above the loyalty barcode instead of below it.',
      },
    },
  },
};

const SalidoSchema: I9nSchemaPos<IntegrationSystem.Salido> = {
  _id: IntegrationSystem.Salido,
  system: IntegrationSystem.Salido,
  name: 'Salido',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-salido.png',
  searchTokens: ['salido'],
  searchField: 'clientId',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'salidoCheckId',
  locationIdKey: 'revenueCenterId',
  supportsApplyingDiscounts: true,
  supportsTips: true,
  syncsSections: true,
  syncsMenuStructures: false, // Salido's sections are embedded in items so there's no order in which sections actually appear on Salido
  syncNotes: 'Changes in Salido POS are made available approx. once per hour.',
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    clientId: {
      type: 'keyString',
      displayName: 'Client ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    clientSecret: {
      type: 'keyString',
      displayName: 'Client Secret',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    username: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    password: {
      type: 'password',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    revenueCenterId: {
      type: 'keyString',
      displayName: 'Revenue Center ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9nLite,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'keyString',
      required: true,
      displayName: 'Order Type ID',
      requiredForOperations: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9nLite,
    },
  },
};

// Upserve/Breadcrumb is still available for existing customers, but is EOL and not available for
// new customers.
const BreadcrumbSchema: I9nSchemaPos<IntegrationSystem.BreadcrumbDeprecated> = {
  _id: IntegrationSystem.BreadcrumbDeprecated,
  system: IntegrationSystem.BreadcrumbDeprecated,
  name: 'Upserve/Breadcrumb (EOL)',
  isBrandSpecific: false,
  isDeprecated: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-breadcrumb.png',
  searchTokens: ['upserve'],
  searchField: 'username',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'breadcrumbId',
  locationIdKey: 'username',
  supportsTips: true,
  syncsSections: true,
  syncsMenuStructures: true,
  syncsTaxes: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    username: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    password: {
      type: 'password',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
  },
};

const RevelSchema: I9nSchemaPos<IntegrationSystem.Revel> = {
  _id: IntegrationSystem.Revel,
  system: IntegrationSystem.Revel,
  name: 'Revel',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-revel.png',
  searchTokens: ['revel'],
  // NEEDS to be apiUrl so webhooks can look up the orgs from webhooks
  // There is not a single unique identifier for a location
  // It is the url + establishment id
  searchField: 'apiUrl',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'revelId',
  manuallySetMultipleOfSameMod: true,
  locationIdKey: 'establishmentId',
  supportsTips: true,
  supportsPayingWithCash: true,
  supportsPosCoupons: true,
  syncsSections: true,
  syncsMenuStructures: true,
  syncsDiscounts: true,
  supportsCompoundMenuItems: true,
  scheduledSyncIntervalHours: 12,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsFutureOrders: true,
  supportsConcurrentValidation: true,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    apiUrl: {
      type: 'keyString',
      displayName: 'Api Url',
      required: true,
      requiresValidationIfChanged: true,
      validationRegex: '^https://[0-9a-zA-Z\\-\\_]+\\.revelup\\.com$',
      tooltip: {
        text: 'Ex: https://restaurant-name.revelup.com',
      },
    },
    apiKey: {
      type: 'keyString',
      displayName: 'Api Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    apiSecret: {
      type: 'keyString',
      displayName: 'Api Secret',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    establishmentId: {
      type: 'keyString',
      displayName: 'Establishment Id',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    checkInventoryForAvailability: {
      type: 'bool',
      displayName: "Check Item's Inventory for Availability",
      tooltip: {
        text: 'If an item from Revel has an inventory less than or equal to 0 it will not be available',
      },
      writeRight: UserRight.ManageI9n,
    },
    menuIds: {
      type: 'array',
      elementType: 'keyString',
      displayName: 'Custom Menus',
      ui: 'table',
      placeholder: 'Select a menu to sync',
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'By default we fetch the web ordering menu but it can be changed to a custom one after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    allowMultipleOfSameMod: allowMultipleOfSameModFieldSchema,
    defaultDynamicComboGroupName: {
      type: 'keyString',
      displayName: 'Default Dynamic Combo Group Name',
      tooltip: {
        text: 'If an item has at least one dynamic combo we will create a group to hold all of them. This is the name of that group.',
      },
      defaultValue: 'Make It A Combo',
      writeRight: UserRight.ManageI9nLite,
    },
    sendOrdersAsAsap: {
      type: 'bool',
      displayName: 'Send Orders as ASAP',
      tooltip: {
        text: 'If enabled, orders will be sent to Revel as ASAP, unless they are i9n future orders.',
      },
      writeRight: UserRight.ManageI9n,
    },
    linkDiscountToCoupon: {
      type: 'bool',
      displayName: 'Link Discount to Coupon',
      tooltip: {
        text: 'If the order has only one discount, we will assume it is the coupon code used. Will not work with auto discounts',
      },
      writeRight: UserRight.ManageI9n,
    },
    sendFakePhoneNumber: {
      type: 'bool',
      displayName: 'Send Fake Phone Number',
      tooltip: {
        text: 'If enabled, we will send a fake phone number to Revel for orders that do not have a phone number.',
      },
      writeRight: UserRight.ManageI9n,
    },
    fetchAlternateIds: {
      type: 'bool',
      displayName: 'Fetch Alternate IDs',
      tooltip: {
        text: 'Alternate ids are used when sending a menu item Punchh',
      },
      writeRight: UserRight.ManageI9n,
    },
    alwaysSendPickUpTime: {
      type: 'bool',
      displayName: 'Always Send Pickup Time',
      tooltip: {
        text: 'If enabled, pickup_time will be sent on every order (set to the current time for ASAP orders). Future orders continue to use their scheduled pickup time.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'int',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      displayName: 'Dining Option',
      ui: 'dropdown',
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    cashOrderTypeID: {
      type: 'int',
      isSetAfterFirstSync: true,
      isNullable: true,
      displayName: 'Cash Dining Option',
      ui: 'dropdown',
      tooltip: {
        text: 'If set will be used for cash orders instead of the default dining option.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    ...generateCardSpecificTenderTypeSchema({
      isSetAfterFirstSync: true,
      tooltipText: 'Set after first sync. Only active external tender types get synced.',
    }),
  },
};

const CardConnectSchema: I9nSchemaKioskPayment<IntegrationSystem.CardConnect> = {
  _id: IntegrationSystem.CardConnect,
  system: IntegrationSystem.CardConnect,
  name: 'CardConnect',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-card-connect.png',
  searchTokens: ['card-connect'],
  searchField: 'merchantId',
  type: 'kiosk-payment',
  apiPayment: {
    canActivateTerminals: false,
    canCancelPayment: false,
    canDeactivateTerminals: false,
    canFetchListOfTerminals: true,
    canFetchTerminalStatus: true,
    canFetchTerminalModel: true,
    canUpdateTerminalIdleText: true,
    canWakeUpTerminals: false,
  },
  canDisplayText: true,
  canSync: false,
  minTransactionAmount: 0,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    instanceType: instanceTypeFieldSchema,
    merchantId: {
      type: 'keyString',
      displayName: 'Merchant ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    cardPointeUsername: {
      type: 'keyString',
      displayName: 'Username',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    cardPointePassword: {
      type: 'keyString',
      displayName: 'Password',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    skipConnectingToTerminalWhenGettingStatus: {
      type: 'bool',
      displayName: 'Do not connect to payment device when getting status',
      tooltip: {
        text: 'If enabled, Bite cannot guarantee the payment device is fully operable',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9n,
    },
    disableHttpsSessionCache: {
      type: 'bool',
      displayName: 'Disable HTTPS session cache',
      tooltip: {
        text: 'Forces a full handshake on every new TCP connection to CardConnect. Keep-alive reuse is unaffected.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const TccSchema: I9nSchemaStoredValue<IntegrationSystem.Tcc> = {
  _id: IntegrationSystem.Tcc,
  system: IntegrationSystem.Tcc,
  name: 'TCC',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-tcc.png',
  searchTokens: ['tcc'],
  searchField: 'tccLocationId',
  type: 'stored-value',
  isGiftCard: true,
  validCardNumberLengths: [16],
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Manual],
  fields: {
    instanceType: instanceTypeFieldSchema,
    clientUid: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    clientId: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    tccLocationId: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    revenueCenterId: {
      type: 'keyString',
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'Optional. Defaults to 0 on TCC side if blank.',
      },
    },
  },
};

const PaytronixOloSchema: I9nSchemaLoyalty<IntegrationSystem.PaytronixOlo> = {
  _id: IntegrationSystem.PaytronixOlo,
  system: IntegrationSystem.PaytronixOlo,
  name: 'Paytronix+OLO',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-paytronix-olo.png',
  searchTokens: ['paytronix', 'paytronix-olo'],
  searchField: 'merchantID',
  loyaltyCheckIdKey: 'pxTransactionId',
  type: 'loyalty',
  requiredPosSystem: IntegrationSystem.Olo,
  applyRewardSteps: ['loyalty', 'pos'],
  requiresDiscountId: false,
  supportsOrderValidation: false,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: false,
  supportsCompoundMenuItems: false,
  supportsOrderHistoryLookUp: true,
  couponsAndLoyaltyEarningCompatible: true,
  canSync: false,
  supportedAuthMethods: [LoyaltyAuthMethod.EmailPhoneNumber, LoyaltyAuthMethod.PhoneNumber],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    merchantID: {
      type: 'int',
      displayName: 'Paytronix Merchant ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    itemKey: {
      type: 'keyString',
      displayName: 'Item Key',
      tooltip: {
        text: 'The key used to tie an item on Paytronix to the POS. If barcode is selected, and none exists on the item, we fall back to the i9nId.',
      },
      required: true,
      writeRight: UserRight.ManageI9n,
      ui: 'dropdown',
      validationRegex: '^posId|i9nId|barcode$',
      defaultValue: 'i9nId',
    },
    storeCode: {
      type: 'keyString',
      displayName: 'Paytronix Store Code',
      // This field is optional, but if it's present it's unique to the location
      identifiesTheLocation: true,
      tooltip: {
        text: 'Paytronix provided store code; used in "passive earning" if phone number is provided.',
      },
      writeRight: UserRight.ManageI9n,
    },
    cardTemplateCode: {
      type: 'keyString',
      displayName: 'Card Template Code',
      tooltip: {
        text: "Must check with operator for this value. Default: '0'.",
      },
      writeRight: UserRight.ManageI9n,
    },
    rewardsWhiteList: {
      type: 'keyString',
      displayName: 'Reward ID White List',
      tooltip: {
        text: 'Optional. Comma separated list of reward reference values to allow.',
      },
      writeRight: UserRight.ManageI9n,
    },
    sendOrderWithoutAuth: sendOrderWithoutAuthFieldSchema,
    clientId: {
      type: 'keyString',
      displayName: 'Client ID',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'If different from Bite default values',
      },
      maskInput: true,
    },
    clientSecret: {
      type: 'keyString',
      displayName: 'Client Secret',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'If different from Bite default values',
      },
      maskInput: true,
    },
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    overrideDistinguishingApplicableRewards: overrideDistinguishingApplicableRewardsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const PeetsPaytronixOloSchema: I9nSchemaLoyalty<IntegrationSystem.PeetsPaytronixOlo> = {
  _id: IntegrationSystem.PeetsPaytronixOlo,
  system: IntegrationSystem.PeetsPaytronixOlo,
  name: 'Peets Paytronix+OLO',
  isBrandSpecific: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-paytronix-olo-peets-coffee.png',
  searchTokens: ['paytronix', 'paytronix-peets'],
  searchField: 'merchantID',
  loyaltyCheckIdKey: 'pxTransactionId',
  type: 'loyalty',
  requiredPosSystem: IntegrationSystem.Olo,
  applyRewardSteps: ['loyalty', 'pos'],
  requiresDiscountId: false,
  supportsOrderValidation: false,
  supportsRewardsNoOrder: true,
  supportsOrderHistoryLookUp: true,
  supportsDistinguishingApplicableRewards: false,
  supportsCompoundMenuItems: false,
  canSync: false,
  supportedAuthMethods: [
    LoyaltyAuthMethod.Barcode,
    LoyaltyAuthMethod.CardNumber,
    LoyaltyAuthMethod.PhoneNumber,
  ],
  validCardNumberLengths: [16],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    merchantID: {
      type: 'int',
      displayName: 'Paytronix Merchant ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    itemKey: {
      type: 'keyString',
      displayName: 'Item Key',
      tooltip: {
        text: 'The key used to tie an item on Paytronix to the POS. If barcode is selected, and none exists on the item, we fall back to the i9nId.',
      },
      required: true,
      writeRight: UserRight.ManageI9n,
      ui: 'dropdown',
      validationRegex: '^posId|i9nId|barcode$',
      defaultValue: 'i9nId',
    },
    storeCode: {
      type: 'keyString',
      displayName: 'Paytronix Store Code',
      // This field is optional, but if it's present it's unique to the location
      identifiesTheLocation: true,
      tooltip: {
        text: 'Paytronix provided store code; used in "passive earning" if phone number is provided.',
      },
      writeRight: UserRight.ManageI9n,
    },
    cardTemplateCode: {
      type: 'keyString',
      displayName: 'Card Template Code',
      tooltip: {
        text: "Must check with operator for this value. Default: '0'.",
      },
      writeRight: UserRight.ManageI9n,
    },
    rewardsWhiteList: {
      type: 'keyString',
      displayName: 'Reward ID White List',
      tooltip: {
        text: 'Optional. Comma separated list of reward reference values to allow.',
      },
      writeRight: UserRight.ManageI9n,
    },
    sendOrderWithoutAuth: sendOrderWithoutAuthFieldSchema,
    clientId: {
      type: 'keyString',
      displayName: 'Client ID',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'If different from Bite default values',
      },
      maskInput: true,
    },
    clientSecret: {
      type: 'keyString',
      displayName: 'Client Secret',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'If different from Bite default values',
      },
      maskInput: true,
    },
    subscriptionKey: {
      type: 'keyString',
      displayName: 'Subscription Key',
      writeRight: UserRight.ManageI9n,
      maskInput: true,
      required: true,
    },
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const SquarePosSchema: I9nSchemaPos<IntegrationSystem.SquarePos> = {
  _id: IntegrationSystem.SquarePos,
  system: IntegrationSystem.SquarePos,
  name: 'Square',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-square-pos.png',
  searchTokens: ['square-pos'],
  searchField: null, // This system requires oauth so we don't have a unique identifier by which to search
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'squareOrderId',
  locationIdKey: 'locationID',
  requiresOAuth: true,
  supportsPayingWithCash: true,
  supportsTips: true,
  syncsSections: true,
  syncsMenuStructures: false, // Square's sections are just randomly inserted in the menu response
  syncsTaxes: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    locationID: {
      type: 'keyString',
      displayName: 'Location ID',
      ui: 'dropdown',
      requiredForOperations: true,
      identifiesTheLocation: true,
      skipWhenPrefilling: true,
      tooltip: {
        text: 'It will be possible to set the location after OAuth is completed.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const WorldpaySchema: I9nSchemaKioskPayment<IntegrationSystem.Worldpay> = {
  _id: IntegrationSystem.Worldpay,
  system: IntegrationSystem.Worldpay,
  name: 'Worldpay',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-worldpay.png',
  searchTokens: ['worldpay'],
  searchField: 'accountId',
  type: 'kiosk-payment',
  apiPayment: {
    canActivateTerminals: true,
    canCancelPayment: false,
    canDeactivateTerminals: true,
    canFetchListOfTerminals: true,
    canFetchTerminalStatus: true,
    canFetchTerminalModel: true,
    canUpdateTerminalIdleText: true,
    canWakeUpTerminals: true,
  },
  canDisplayText: true,
  canSync: false,
  minTransactionAmount: 0,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    instanceType: instanceTypeFieldSchema,
    accountId: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      displayName: 'Account ID',
      writeRight: UserRight.ManageI9n,
    },
    accountToken: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      displayName: 'Account Token',
      writeRight: UserRight.ManageI9n,
    },
    acceptorId: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      displayName: 'Acceptor ID',
      writeRight: UserRight.ManageI9n,
    },
    marketCode: {
      displayName: 'Market Code',
      type: 'stringEnum',
      enum: WorldpayMarketCode,
      required: true,
      ui: 'dropdown',
      writeRight: UserRight.ManageI9n,
    },
    terminalId: {
      type: 'keyString',
      displayName: 'Terminal ID (TID)',
      tooltip: {
        text: "Some boarding credentials won't include this. In that case, default value '0001' to ensure pairing of new payment devices",
      },
      required: true,
      defaultValue: '0001',
      writeRight: UserRight.ManageI9n,
    },
    lastLaneId: {
      type: 'int',
      displayName: 'Last Lane ID',
      skipWhenPrefilling: true,
      tooltip: {
        text: 'Only set this if we are receiving a an error that a lane has previously been used and our counter is off. This will be auto incremented each time a lane is added.',
      },
      writeRight: UserRight.ManageI9n,
    },
    enableDebitSupport: {
      type: 'bool',
      displayName: 'Accept Debit Cards',
      defaultValue: true,
      tooltip: {
        text: 'If this is enabled, the pinpad will prompt the user to select Debit/Credit for every transaction',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9n,
    },
    allowDuplicateTransactions: {
      type: 'bool',
      displayName: 'Allow Duplicate Transactions',
      defaultValue: true,
      tooltip: {
        text: 'If this is false, Worldpay will block sequential transactions with the same amount. Check if you see a lot of transactions declined with code 23.',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9n,
    },
    enablePreRead: {
      type: 'bool',
      displayName: 'Capture Card at Session Start',
      tooltip: {
        text: 'If enabled the user will be prompted to insert their card at the start of the session.',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9n,
    },
    ...ebtFieldSchemas,
  },
};

const WorldpayStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.WorldpayStoredValue> = {
  _id: IntegrationSystem.WorldpayStoredValue,
  system: IntegrationSystem.WorldpayStoredValue,
  name: 'Worldpay Stored Value',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-worldpay-stored-value.png',
  searchTokens: ['worldpay', 'worldpay-stored-value'],
  searchField: null, // We don't have a good identifier here
  type: 'stored-value',
  isGiftCard: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: IntegrationSystem.Worldpay,
  usesApiPayment: true,
  readMethods: [HardwareReadMethod.Terminal],
  fields: {
    instanceType: instanceTypeFieldSchema,
    giftCardType: {
      type: 'stringEnum',
      enum: WorldpayGiftCardType,
      displayName: 'Gift Card Type',
      ui: 'dropdown',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    storeCardId: {
      type: 'keyString',
      displayName: 'StoreCard ID',
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'giftCardType',
          values: [WorldpayGiftCardType.StoreCard],
        },
      ],
    },
    storeCardPassword: {
      type: 'keyString',
      displayName: 'StoreCard Password',
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'giftCardType',
          values: [WorldpayGiftCardType.StoreCard],
        },
      ],
      maskInput: true,
    },
  },
};

const QsrAutomationsSchema: I9nSchemaFulfillment<IntegrationSystem.QsrAutomations> = {
  _id: IntegrationSystem.QsrAutomations,
  system: IntegrationSystem.QsrAutomations,
  name: 'QSR Automations',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-qsr-automations.png',
  searchTokens: ['qsr-automations'],
  searchField: 'qsrAutomationsSiteUid',
  fulfillmentCheckIdKey: 'qsrAutomationsCheckNumber',
  type: 'fulfillment',
  supportsOrderValidation: false,
  supportsThirdPartyOrderSending: false,
  supportsOrderStatusPolling: true,
  supportsOrderStatusWebhooks: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  canSync: false,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    qsrAutomationsMode: {
      displayName: 'Integration Mode',
      type: 'stringEnum',
      enum: QsrAutomationsMode,
      enumNameHelper: QsrAutomationsModeHelper.name,
      ui: 'dropdown',
      writeRight: UserRight.ManageI9n,
      required: true,
      defaultValue: QsrAutomationsMode.Sending,
    },
    useEnterpriseApi: {
      displayName: 'Use Enterprise API',
      type: 'bool',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      defaultValue: true,
    },
    qsrAutomationsCompanyUid: {
      displayName: 'Company UID',
      type: 'keyString',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      required: true,
      conditions: [
        {
          field: 'useEnterpriseApi',
          values: [true],
        },
      ],
    },
    qsrAutomationsSiteUid: {
      displayName: 'Site UID',
      type: 'keyString',
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      tooltip: {
        text: "This may need to be left blank until it's provided by the QSR team.",
      },
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'useEnterpriseApi',
          values: [true],
        },
      ],
    },
    serverUrl: {
      displayName: 'Server URL',
      type: 'keyString',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'ex1: http://192.168.2.13:32768; ex2: https://bite-qsr-lab.ngrok.io',
      },
      required: true,
      conditions: [
        {
          field: 'useEnterpriseApi',
          values: [false, undefined],
        },
        {
          field: 'qsrAutomationsMode',
          values: [QsrAutomationsMode.Sending],
        },
      ],
    },
    qsrAutomationsItemIdKey: {
      displayName: 'Item/Mod ID Field',
      type: 'stringEnum',
      enum: QsrAutomationsItemIdKey,
      ui: 'dropdown',
      writeRight: UserRight.ManageI9n,
      required: true,
      defaultValue: QsrAutomationsItemIdKey.ItemBiteId,
      tooltip: {
        text: "The field used to represent an item's ID (integer). Falls back to Bite ID if chosen key is missing.",
      },
    },
    qsrAutomationsDepartmentKey: {
      displayName: 'Department Field',
      type: 'stringEnum',
      enum: QsrAutomationsDepartmentKey,
      ui: 'dropdown',
      writeRight: UserRight.ManageI9n,
      required: true,
      defaultValue: QsrAutomationsDepartmentKey.SectionBiteId,
      tooltip: {
        text: "The field used to represent an item's department (integer). Falls back to Bite ID if chosen key is missing.",
      },
    },
    sendDeselectedDefaultModPrefix: {
      type: 'keyString',
      displayName: 'Send Deselected Prefixed Mod',
      tooltip: {
        text: 'If set default mods that are deselected will be sent. The value entered will be prefixed to the mod name. For example, if the prefix is "No" and the default mod is "Cheese", then "No Cheese" will be sent to QSR Automations.',
      },
      placeholder: 'Currently Not Sending',
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
    },
  },
  diningOptionFields: {
    qsrAutomationsOrderTypeID: {
      type: 'int',
      required: true,
      displayName: 'Destination ID',
      tooltip: {
        text: 'The Destination ID is defined in the CSK configuration.',
      },
      writeRight: UserRight.ManageI9nLite,
      min: 0,
    },
  },
  diningOptionConditions: [
    {
      field: 'qsrAutomationsMode',
      values: [QsrAutomationsMode.Sending],
    },
  ],
};

const brinkLocationTokenFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'keyString',
  displayName: 'Location Token',
  required: true,
  requiresValidationIfChanged: true,
  identifiesTheLocation: true,
  validationRegex: '^[a-zA-Z0-9+/]{22}==$',
  tooltip: {
    text: 'Ex: 71CgROFOSEttPmuFsB6qMQ==',
  },
  writeRight: UserRight.ManageI9n,
  maskInput: true,
};

const brinkApiServerFieldSchema: AnyModelFieldWithIntegrationOptions = {
  type: 'int',
  displayName: 'API Server',
  ui: 'dropdown',
  values: allEnumValues<BrinkApiServer>(BrinkApiServer),
  required: true,
  requiresValidationIfChanged: true,
  writeRight: UserRight.ManageI9n,
};

const ParBrinkSchema: I9nSchemaPos<IntegrationSystem.ParBrink> = {
  _id: IntegrationSystem.ParBrink,
  system: IntegrationSystem.ParBrink,
  name: 'PAR Brink',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-par-pos.png',
  searchTokens: ['brink'],
  searchField: 'locationToken',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'brinkOrderId',
  locationIdKey: 'locationToken',
  supportsFutureOrders: true,
  supportsPosCoupons: true,
  couponsAndLoyaltyEarningCompatible: true,
  supportsPayingWithCash: true,
  supportsCompoundMenuItems: true,
  syncsOrders: true,
  syncsSections: true,
  syncsMenuStructures: true,
  syncsDiscounts: true,
  supportsApplyingDiscounts: true,
  supportsUseLoyaltyI9nId: true,
  manuallySetMultipleOfSameMod: true,
  supportsTipValidation: true,
  supportsConcurrentValidation: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  fields: {
    locationToken: brinkLocationTokenFieldSchema,
    apiServer: brinkApiServerFieldSchema,
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    brinkCaCertPem: {
      type: 'bool',
      displayName: 'Use Brink CA Cert PEM',
      tooltip: {
        text: 'If enabled, the CA cert PEM file will be used for the Brink API.',
      },
      writeRight: UserRight.ManageI9n,
      defaultValue: false,
      requiresValidationIfChanged: true,
    },
    syncMenuUsingItems: {
      type: 'bool',
      displayName: 'Sync Brink Using Items (Not Menus)',
      tooltip: {
        text: 'By default we fetch online ordering menus. However, if no menus are configured we can sync the items directly.',
      },
      writeRight: UserRight.ManageI9n,
    },
    syncSettingsMenu: {
      type: 'bool',
      displayName: 'Sync Settings Menu (Menu Mod Groups)',
      tooltip: {
        text: 'If enabled, the settings version of menus will be synced, which has more information including menu mod groups',
      },
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'syncMenuUsingItems',
          values: [false, undefined],
        },
      ],
    },
    skipNonActiveItems: {
      type: 'bool',
      displayName: 'Skip non-active items',
      defaultValue: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'When enabled non active items will not be synced, when disabled non active items will be synced but marked as hidden.',
      },
    },
    skipNonActiveModifiers: {
      type: 'bool',
      displayName: 'Skip non-active modifiers',
      defaultValue: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'When enabled non active modifiers will not be synced, when disabled non active modifiers will be synced but marked as hidden.',
      },
    },
    minimizeNumberOfDenormalizedModGroups: {
      type: 'bool',
      displayName: 'Minimize number of denormalized mod groups',
      defaultValue: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: `Don't create denormalized mod groups for each top level item.`,
      },
    },
    alternateMenuId: {
      type: 'keyString',
      displayName: 'Alternate Menu',
      ui: 'dropdown',
      requiresValidationIfChanged: true,
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'By default we fetch the online ordering menu but it can be changed to an alternate one after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    memoItemID: memoItemIDFieldSchema,
    allowMultipleOfSameMod: allowMultipleOfSameModFieldSchema,
    syncIngredientItemIds: {
      type: 'bool',
      displayName: 'Sync ingredients',
      defaultValue: true,
      tooltip: {
        text: 'Some brands use Included modifiers, others use Ingredients, and others use both. They may or may not play well together.',
      },
      writeRight: UserRight.ManageI9n,
    },
    enableFreeItemsInModGroups: {
      type: 'bool',
      displayName: 'Enable free items in mod groups',
      defaultValue: true,
      tooltip: {
        text: 'This will create a separate mod group with all items being free and max set to the value of Free in Brink.',
      },
      writeRight: UserRight.ManageI9n,
    },
    useItemGroupsForLoyalty: {
      type: 'bool',
      displayName: 'Use All Item Groups for Loyalty',
      tooltip: {
        text: 'Paytronix Only. If this is enabled, we will use item groups to identify loyalty item sections.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    punchhLoyaltyId: {
      type: 'keyString',
      displayName: 'Punchh Loyalty ID',
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'With Punchh Loyalty on location, with authed guest, this item will be sent (posId) as a memo. Resolves double orders on Punchh.',
      },
    },
    punchhUserId: {
      type: 'keyString',
      displayName: 'Punchh User ID',
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'With Punchh Loyalty on location, but no authed guest, this item will be sent (posId) as a memo. Resolves double orders on Punchh.',
      },
    },
    brinkDiscountsByCardId: {
      type: 'array',
      elementType: 'object',
      ui: 'accordion',
      uiSubtype: 'BrinkDiscountByCardId',
      placeholder: 'Add a mapping',
      displayName: 'Discounts to Apply for Loyalty Cards',
      writeRight: UserRight.ManageI9nLite,
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'When the specific loyalty card is scanned, the discounts in this list will be applied.',
      },
      fields: {
        loyaltyCardId: {
          type: 'keyString',
          displayName: 'Loyalty Card Id',
          required: true,
          writeRight: UserRight.ManageI9nLite,
        },
        discountIds: {
          type: 'array',
          elementType: 'keyString',
          ui: 'table',
          placeholder: 'Select a discount to automatically apply',
          displayName: 'Discounts to Automatically Apply on Validate',
          writeRight: UserRight.ManageI9nLite,
          tooltip: {
            text: 'When validating Bite will attempt to apply all discounts on this list.',
          },
        },
      },
    },
    brinkDiscountIdsMapping: {
      type: 'array',
      elementType: 'object',
      ui: 'accordion',
      uiSubtype: 'BrinkDiscountMapByLoyaltyId',
      placeholder: 'Add a loyalty discount mapping',
      displayName: 'Loyalty Reward to POS Discount Mapping',
      writeRight: UserRight.ManageI9nLite,
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'When the specific loyalty card is scanned, the discounts in this list will be applied.',
      },
      fields: {
        loyaltyRewardId: {
          type: 'keyString',
          ui: 'dropdown',
          displayName: 'Loyalty Discount ID',
          placeholder: 'Select a loyalty discount',
          required: true,
          writeRight: UserRight.ManageI9nLite,
        },
        posDiscountId: {
          type: 'keyString',
          ui: 'dropdown',
          required: true,
          placeholder: 'Select a POS discount mapping',
          displayName: 'POS Discount ID',
          writeRight: UserRight.ManageI9nLite,
          tooltip: {
            text: 'When validating Bite will attempt to apply all discounts on this list.',
          },
        },
      },
    },
    checkIfPosIsOnline: {
      displayName: 'Check if POS is online',
      type: 'bool',
      tooltip: {
        text: 'When validating Bite will check if the main POS terminal is online.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    sendDefaultMods: {
      type: 'bool',
      displayName: 'Send Default Mods',
      tooltip: {
        text: 'If enabled, then mods selected by default will be sent to Brink, requires a sync.',
      },
      writeRight: UserRight.ManageI9nLite,
      isHidden: true,
    },
    ignorePriceOverrides: {
      type: 'bool',
      displayName: 'Ignore Price Overrides',
      tooltip: {
        text: 'If enabled, then price overrides coming from Brink will be ignored.',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9nLite,
    },
    sendDefaultModBehavior: {
      displayName: 'Send Default Mods (Is Included)',
      type: 'stringEnum',
      enum: DefaultModBehavior,
      ui: 'dropdown',
      tooltip: {
        text: 'Only applies to mods labelled as "Is Included" in Brink. This setting is overridden if the mod is also labelled as "Automatically Add". System default will send default mods; if the Brink mod group is not marked as "flow required".',
      },
      writeRight: UserRight.ManageI9nLite,
      defaultValue: DefaultModBehavior.DoNotSend,
      required: true,
    },
    sendDefaultModBehaviorAutomaticallyAdd: {
      displayName: 'Send Default Mods (Automatically Add)',
      type: 'stringEnum',
      enum: DefaultModBehavior,
      ui: 'dropdown',
      tooltip: {
        text: 'Only applies to mods labelled as "Automatically Add" in Brink. System default will send default mods; if the Brink mod group is not marked as "flow required".',
      },
      writeRight: UserRight.ManageI9nLite,
      defaultValue: DefaultModBehavior.Send,
      required: true,
    },
    biteSideModCodes: {
      type: 'array',
      elementType: 'keyString',
      displayName: 'Bite-Side Mod Codes',
      tooltip: {
        text: 'These mod codes will NOT be sent to Brink!',
        isWarning: true,
      },
      uniqueElements: true,
      writeRight: UserRight.ManageI9nLite,
    },
    automaticDiscounts: {
      type: 'array',
      displayName: 'Automatic Discounts',
      elementType: 'keyString',
      ui: 'table',
      placeholder: 'Please select a discount',
      tooltip: {
        text: 'Bite will try to auto apply any discounts here. If the discount has item groups, it will only apply to the items belonging to those groups.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    sendAddAndExtraModCodes: {
      type: 'bool',
      displayName: 'Send ADD and EXTRA Mod Codes',
      tooltip: {
        text: 'If enabled, ADD and EXTRA mod codes will be sent to the POS for modifiers selected by the guest. EXTRA is used when the mod is selected by default, otherwise ADD is used. Requires menu resync when changed.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    skipAddAndExtraModeCodesForModGroupsWithPosIds: {
      type: 'array',
      elementType: 'keyString',
      displayName: 'Skip ADD and EXTRA Mod Codes for Mod Groups',
      conditions: [
        {
          field: 'sendAddAndExtraModCodes',
          values: [true],
        },
      ],
      tooltip: {
        text: 'List of POS IDs of mod groups which should not send ADD and EXTRA mod codes. Requires menu resync when changed.',
      },
    },
    promptForDonationRoundUp: promptForDonationSchema,
    donationItemId: {
      type: 'keyString',
      displayName: 'Donation Roundup Item',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'A non-revenue item with $0 price and no modifiers, used for sending the donation amount to the POS.',
      },
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
      conditions: [
        {
          field: 'promptForDonationRoundUp',
          values: [DonationRoundUpPromptType.Custom, DonationRoundUpPromptType.Predefined],
        },
      ],
      required: true,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'keyString',
      displayName: 'Destination',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    cashOrderTypeID: {
      type: 'keyString',
      displayName: 'Cash Payment Destination',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      isNullable: true,
      tooltip: {
        text: 'If set will be used for cash orders instead of the default destination.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    ...generateCardSpecificTenderTypeSchema({
      isSetAfterFirstSync: true,
      tooltipText: 'Set after first sync. Only active external tender types get synced.',
    }),
  },
};

const ChoptLoyaltySchema: I9nSchemaLoyalty<IntegrationSystem.ChoptLoyalty> = {
  _id: IntegrationSystem.ChoptLoyalty,
  system: IntegrationSystem.ChoptLoyalty,
  name: 'Chopt Loyalty',
  isBrandSpecific: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-chopt-loyalty.png',
  searchTokens: ['chopt-loyalty'],
  searchField: 'choptLocationID',
  loyaltyCheckIdKey: null,
  type: 'loyalty',
  canSync: false,
  requiresDiscountId: true,
  autoApplyFirstAvailableReward: true,
  applyRewardSteps: ['pos', 'loyalty'],
  supportsCompoundMenuItems: true,
  supportsOrderValidation: false,
  createsTransactions: true,
  requiredPosSystem: null,
  alwaysUsedAsPayment: true,
  incompatiblePosSystems: [IntegrationSystem.Olo, IntegrationSystem.SpotOn],
  supportedAuthMethods: [LoyaltyAuthMethod.Barcode],
  supportsSignupInstantEnroll: false,
  skipSendOrder: true,

  fields: {
    instanceType: instanceTypeFieldSchema,
    choptLocationID: {
      type: 'int',
      displayName: 'Location',
      requiredForOperations: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    posTenderTypeID: {
      type: 'keyString',
      displayName: 'Tender for Chopt Loyalty credit card payments',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Can be set after first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    posGiftCardTenderTypeID: {
      type: 'keyString',
      displayName: 'Tender for Chopt Loyalty gift card payments',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Can be set after first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const PunchhOloSchema: I9nSchemaLoyalty<IntegrationSystem.PunchhOlo> = {
  _id: IntegrationSystem.PunchhOlo,
  system: IntegrationSystem.PunchhOlo,
  name: 'Punchh+OLO',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-punchh-olo.png',
  searchTokens: ['punchh', 'punchh-olo'],
  searchField: 'locationKey',
  loyaltyCheckIdKey: 'transaction_no',
  type: 'loyalty',
  canSync: true,
  requiresDiscountId: false,
  supportsOrderValidation: true,
  requiredPosSystem: IntegrationSystem.Olo,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: false,
  supportsCompoundMenuItems: false,
  supportsOrderHistoryLookUp: true,
  applyRewardSteps: ['loyalty', 'pos'],
  supportedAuthMethods: [LoyaltyAuthMethod.Barcode, LoyaltyAuthMethod.PhoneNumber],
  supportsSignupInstantEnroll: true,

  fields: {
    instanceType: instanceTypeFieldSchema,
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PunchhApiServer>(PunchhApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    locationKey: {
      type: 'keyString',
      displayName: 'Punchh Location Key',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    businessKey: punchhBusinessKeyFieldSchema,
    clientId: {
      type: 'keyString',
      displayName: 'Punchh Client ID',
      required: true,
      tooltip: {
        text: 'One Client ID per brand.',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    clientSecret: {
      type: 'keyString',
      displayName: 'Punchh Client Secret',
      required: true,
      tooltip: {
        text: 'One Client Secret per brand.',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    authMethods: authMethodsFieldSchema,
    rewardsWhiteList: {
      type: 'keyString',
      displayName: 'Reward ID White List',
      tooltip: {
        text: 'Optional. Comma separated list of reward reference values to allow.',
      },
      writeRight: UserRight.ManageI9n,
    },
    sendOrderWithoutAuth: sendOrderWithoutAuthFieldSchema,
    punchhOloProvider: {
      displayName: 'Non-Phone Provider slug to be sent to Olo',
      type: 'stringEnum',
      enum: PunchhOloSlug,
      ui: 'dropdown',
      tooltip: {
        text: 'Provider slug to be sent to Olo. Default is "Punchh"',
      },
      required: true,
    },
    punchhOloPhoneProvider: {
      displayName: 'Phone Provider slug to be sent to Olo',
      type: 'stringEnum',
      enum: PunchhOloPhoneSlug,
      ui: 'dropdown',
      tooltip: {
        text: 'Provider slug to be sent to Olo. For sandbox default is "punchh-kiosk". For production default is "punchh-phone-number"',
      },
    },
    printerBarcodeType: {
      displayName: 'Printer Barcode Type',
      type: 'stringEnum',
      enum: PunchhPrinterBarcodeType,
      ui: 'dropdown',
      tooltip: {
        text: 'The type of barcode to print on the receipt.',
      },
      required: true,
      defaultValue: PunchhPrinterBarcodeType.QR,
    },
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    fetchPointsFromPunchh: {
      type: 'bool',
      displayName: 'Fetch points from Punchh instead of OLO',
      tooltip: {
        text: 'If enabled, Bite will fetch points from Punchh instead of OLO.',
      },
    },
  },
};

const GivexSchema: I9nSchemaStoredValue<IntegrationSystem.Givex> = {
  _id: IntegrationSystem.Givex,
  system: IntegrationSystem.Givex,
  name: 'Givex',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-givex.png',
  searchTokens: ['givex'],
  searchField: 'userId',
  type: 'stored-value',
  isGiftCard: true,
  supportsBarcode: true,
  validCardNumberLengths: [9, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22],
  validPinLengths: [4, 5, 6],
  validCardNumberInputRegex: '^[0-9A-Za-z\\-]$',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Scanner, HardwareReadMethod.Manual],
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<GivexApiServer>(GivexApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    userId: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    password: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    requiresPin: {
      type: 'bool',
      displayName: 'Show PIN Input on Gift Card view',
      writeRight: UserRight.ManageI9n,
    },
  },
};

const SessionMOloSchema: I9nSchemaLoyalty<IntegrationSystem.SessionMOlo> = {
  _id: IntegrationSystem.SessionMOlo,
  system: IntegrationSystem.SessionMOlo,
  name: 'SessionM+OLO',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-sessionm-olo.png',
  searchTokens: ['sessionm'],
  searchField: 'clientId',
  loyaltyCheckIdKey: null,
  type: 'loyalty',
  requiredPosSystem: IntegrationSystem.Olo,
  supportsOrderValidation: false,
  applyRewardSteps: ['loyalty', 'pos'],
  canSync: false,
  requiresDiscountId: false,
  supportedAuthMethods: [LoyaltyAuthMethod.Barcode],
  supportsCompoundMenuItems: false,
  supportsOrderHistoryLookUp: true,
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    clientId: {
      type: 'keyString',
      displayName: 'Client ID',
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'Unique for each brand. Ask SessionM or OLO for this value. Ex: sessionm-noodles',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    loginSubdomain: {
      type: 'keyString',
      displayName: 'Brand Login Subdomain',
      tooltip: {
        text: 'Unique for each brand. Ask SessionM for this value. Ex: login-noodles',
      },
      writeRight: UserRight.ManageI9n,
    },
    providerValue: {
      type: 'keyString',
      displayName: 'Provider Value',
      tooltip: {
        text: 'Unique for each brand. Ask SessionM or OLO for this value. Ex: sessionm-noodles',
      },
      writeRight: UserRight.ManageI9n,
    },
    rewardsWhiteList: {
      type: 'keyString',
      displayName: 'Reward ID White List',
      tooltip: {
        text: 'Optional. Comma separated list of reward reference values to allow.',
      },
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const SparkflySchema: I9nSchemaLoyalty<IntegrationSystem.Sparkfly> = {
  _id: IntegrationSystem.Sparkfly,
  system: IntegrationSystem.Sparkfly,
  name: 'Sparkfly',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-sparkfly.png',
  type: 'loyalty',
  searchTokens: ['sparkfly'],
  searchField: 'channelId',
  loyaltyCheckIdKey: 'sparkflyTransactionId',
  supportsCoupons: true,
  couponsAndLoyaltyEarningCompatible: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.SpotOn],
  supportsOrderValidation: false,
  applyRewardSteps: ['loyalty', 'pos'],
  requiresDiscountId: true,
  supportsCompoundMenuItems: true,
  canSync: false,
  supportedAuthMethods: [
    LoyaltyAuthMethod.PhoneNumber,
    LoyaltyAuthMethod.Barcode,
    LoyaltyAuthMethod.CardNumber,
  ],
  validCardNumberLengths: [16],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    identity: {
      type: 'keyString',
      displayName: 'X-Auth-Identity',
      required: true,
    },
    authKey: {
      type: 'keyString',
      displayName: 'X-Auth-Key',
      required: true,
      maskInput: true,
    },
    authMethods: authMethodsFieldSchema,
    channelId: {
      type: 'keyString',
      displayName: 'Channel ID',
      required: true,
      writeRight: UserRight.ManageI9n,
      identifiesTheLocation: true,
      tooltip: {
        text: 'The channel ID for the restaurant',
      },
    },
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const SparkflyLoyaltyEarnerSchema: I9nSchemaLoyaltyEarner<IntegrationSystem.SparkflyLoyaltyEarner> =
  {
    _id: IntegrationSystem.SparkflyLoyaltyEarner,
    system: IntegrationSystem.SparkflyLoyaltyEarner,
    name: 'Sparkfly Earner',
    isBrandSpecific: false,
    iconUrl: 'https://static.bureau.getbite.com/images/icon-sparkfly-earner.png',
    type: 'loyalty-earner',
    searchTokens: ['sparkfly-loyalty-earner'],
    requiredPosSystem: null,
    incompatiblePosSystems: [],
    searchField: 'channelId',
    allowUserToLogIn: false,
    skipSendOrder: false,
    fields: {
      instanceType: instanceTypeFieldSchema,
      identity: {
        type: 'keyString',
        displayName: 'X-Auth-Identity',
        required: true,
        requiresValidationIfChanged: true,
        writeRight: UserRight.ManageI9n,
      },
      authKey: {
        type: 'keyString',
        displayName: 'X-Auth-Key',
        required: true,
        requiresValidationIfChanged: true,
        writeRight: UserRight.ManageI9n,
        maskInput: true,
      },
      channelId: {
        type: 'keyString',
        displayName: 'Channel ID',
        required: true,
        identifiesTheLocation: true,
        writeRight: UserRight.ManageI9n,
      },
    },
  };

const ToastSchema: I9nSchemaPos<IntegrationSystem.Toast> = {
  _id: IntegrationSystem.Toast,
  system: IntegrationSystem.Toast,
  name: 'Toast',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-toast.png',
  searchTokens: ['toast'],
  searchField: 'restaurantGuid',
  type: 'pos',
  webhookIdKey: 'posId',
  posCheckIdKey: 'toastId',
  locationIdKey: 'restaurantGuid',
  supportsPosCoupons: true,
  supportsPayingWithCash: true,
  supportsTips: true,
  supportsOrderStatusPolling: true,
  supportsOrderStatusWebhooks: false,
  syncsMultipleOfSameMod: true,
  syncsSections: true,
  syncsMenuStructures: true,
  syncsDiscounts: true,
  supportsCompoundMenuItems: true,
  couponsAndLoyaltyEarningCompatible: true,
  syncsCalories: true,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    restaurantGuid: {
      type: 'keyString',
      displayName: 'Restaurant GUID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    sendDescriptionToPos: {
      type: 'int',
      values: allEnumValues<SendDescriptionToPos>(SendDescriptionToPos),
      displayName: 'Send Description to POS',
      ui: 'dropdown',
      required: true,
      writeRight: UserRight.ManageMenu,
      tooltip: {
        text: 'Message will be added to each menu item',
      },
    },
    menuIds: {
      type: 'array',
      elementType: 'keyString',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      displayName: 'Menus to Sync',
      ui: 'table',
      placeholder: 'Select a menu to sync',
      emptyListWarningHtml: 'Please select at least one menu to sync.',
      tooltip: {
        text: 'By default we fetch the first menu available but it can be changed to an set of specific ones after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    alternatePaymentTypeId: {
      type: 'keyString',
      displayName: 'Payment Type',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    revenueCenterId: {
      type: 'keyString',
      displayName: 'Revenue Center',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'Set after first sync. Optional.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    descriptionForPos: {
      type: 'longString',
      displayName: 'Description For POS',
      canHaveLocalizationOverrides: true,
      writeRight: UserRight.ManageMenu,
      tooltip: {
        text: 'The message typed here will be set to the POS',
      },
    },
    automaticDiscounts: {
      type: 'array',
      elementType: 'keyString',
      ui: 'table',
      placeholder: 'Select a discount to automatically apply',
      displayName: 'Discounts to Automatically Apply on Validate',
      isSetAfterFirstSync: true,
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'When validating Bite will see if any of the discounts in this list can be applied.',
      },
    },
    toastEmployeeId: {
      type: 'keyString',
      displayName: 'POS Employee',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
      isNullable: true,
    },
    promptForDonationRoundUp: promptForDonationSchema,
    donationServiceChargeID: {
      type: 'keyString',
      displayName: 'Donation Service Charge',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'An open service charge used for sending donations',
      },
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
      conditions: [
        {
          field: 'promptForDonationRoundUp',
          values: [DonationRoundUpPromptType.Custom, DonationRoundUpPromptType.Predefined],
        },
      ],
      required: true,
    },

    /**
     * @todo Remove this once everyone is on v3
     **/
    useV3Menu: {
      type: 'bool',
      displayName: 'Use V3 Menu',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: '!!ENABLING CAN CAUSE DRASTIC MENU CHANGES!! Bite will use the v3 menu endpoint instead of v2.',
        isWarning: true,
      },
    },
    validateInventory: {
      type: 'bool',
      displayName: 'Validate Inventory',
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'Bite will check for out of stock and quantity limits during validation.',
      },
    },
    shouldUseI9nIdFallback: {
      type: 'bool',
      displayName: 'Use I9n ID Fallback',
      tooltip: {
        text: 'If we fail to match an I9n model to a Bite model during sync using the I9n ID, then we will attempt to match using the POS ID. If POS ID does not match, then we will attempt to use the name.',
      },
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'keyString',
      displayName: 'Dining Option',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    serviceChargeId: {
      type: 'keyString',
      displayName: 'Service Charge',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'Set after first sync. Optional. Will be applied to orders with only this dining option',
      },
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
    },
    serviceChargeAmount: {
      type: 'price',
      displayName: 'Service Charge Amount',
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'The amount of the service charge in dollars. Only works if the Toast service charge is open',
      },
    },
  },
};

const HeartlandSchema: I9nSchemaKioskPayment<IntegrationSystem.Heartland> = {
  _id: IntegrationSystem.Heartland,
  system: IntegrationSystem.Heartland,
  name: 'Heartland',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-heartland.png',
  searchTokens: ['heartland'],
  searchField: 'heartlandLicenseId',
  type: 'kiosk-payment',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  apiPayment: null,
  canDisplayText: true,
  canSync: false,
  minTransactionAmount: 0,
  fields: {
    instanceType: instanceTypeFieldSchema,
    heartlandLicenseId: {
      type: 'keyString',
      displayName: 'License ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    heartlandSiteId: {
      type: 'keyString',
      displayName: 'Site ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    heartlandDeviceIds: {
      displayName: 'Device IDs',
      type: 'array',
      elementType: 'keyString',
      minCount: 1,
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    heartlandUsername: {
      type: 'keyString',
      displayName: 'Username (MID)',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    heartlandPassword: {
      type: 'keyString',
      displayName: 'Password',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    // TODO(BITE-15544): Re-enable Heartland EBT fields after Heartland SDK
    // support for EBT inquiry is available and certification is complete.
  },
};

const InforSchema: I9nSchemaPos<IntegrationSystem.Infor> = {
  _id: IntegrationSystem.Infor,
  system: IntegrationSystem.Infor,
  name: 'Infor',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-infor.png',
  searchTokens: ['infor'],
  searchField: 'storeId',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'inforId',
  locationIdKey: 'storeId',
  supportsApplyingDiscounts: true,
  supportsPayingWithCash: true,
  supportsTips: true,
  syncsSections: true,
  syncsMenuStructures: false, // Infor's sections are returned at random from the API/db
  syncsDiscounts: true,
  scheduledSyncIntervalHours: 8,
  manuallySetMultipleOfSameMod: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    storeId: {
      type: 'keyString',
      displayName: 'Store Id',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    tenderTypeID: {
      type: 'keyString',
      displayName: 'Tender Type',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Can be set after first POS sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    taxExemptTenderTypeID: {
      type: 'keyString',
      displayName: 'Non-taxable Tender Type',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Can be set after first POS sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    menuItemsTopCategory: {
      type: 'keyString',
      defaultValue: 'Kiosk%20Menu',
      displayName: 'Menu Item Top Category',
      tooltip: {
        text: 'Name of the category which is a root of required menu tree. Example: Kiosk%20Menu',
      },
      writeRight: UserRight.ManageI9n,
    },
    categoriesTopCategory: {
      type: 'keyString',
      displayName: 'Categories Top Category',
      tooltip: {
        text: 'Name of the category which is a root of required menu tree. Example: Kiosk%20Menu',
      },
      writeRight: UserRight.ManageI9n,
    },
    addInternationalPhoneNumberExitCodePrefix: {
      type: 'bool',
      displayName: 'Add International Phone Number Exit Code Prefix',
      tooltip: {
        text: "Add a '+' symbol before the guest's phone number when sending it to the POS.",
      },
      writeRight: UserRight.ManageI9n,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'keyString',
      required: true,
      displayName: 'Order Type',
      requiredForOperations: true,
      tooltip: {
        text: 'Get from the customer through Infor Enterprise Manager.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    serviceChargeId: {
      type: 'keyString',
      displayName: 'Service Charge',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'Set after first sync. Optional. Will be applied to orders with only this dining option',
      },
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
    },
  },
};

const ArcherSchema: I9nSchemaStoredValue<IntegrationSystem.Archer> = {
  _id: IntegrationSystem.Archer,
  system: IntegrationSystem.Archer,
  name: 'Archer',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-archer.png',
  searchTokens: ['archer'],
  searchField: 'username',
  type: 'stored-value',
  isGiftCard: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: IntegrationSystem.Heartland,
  readMethods: [HardwareReadMethod.Terminal],
  fields: {
    username: {
      type: 'keyString',
      displayName: 'Username',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    password: {
      type: 'keyString',
      displayName: 'Password',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
  },
};

const AtriumStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.AtriumStoredValue> = {
  _id: IntegrationSystem.AtriumStoredValue,
  system: IntegrationSystem.AtriumStoredValue,
  name: 'Atrium (SV)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-atrium-stored-value.png',
  searchTokens: ['atrium-stored-value'],
  searchField: 'client',
  type: 'stored-value',
  isGiftCard: true,
  // payment tokens are 18 alphanumeric characters for web based payments
  validCardNumberLengths: [9, 18],
  validCardNumberInputRegex: '^[0-9a-zA-Z]$',
  supportsHid: true,
  validatesTaxExemption: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Hid],
  fields: {
    instanceType: instanceTypeFieldSchema,
    client: {
      type: 'keyString',
      displayName: 'Client',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    key: {
      type: 'keyString',
      displayName: 'Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    tenderId: {
      type: 'int',
      displayName: 'Tender ID',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    flashTerminalId: {
      type: 'keyString',
      displayName: 'Flash Terminal ID',
      identifiesTheLocation: true,
      tooltip: {
        text: 'If value is not set, then the default is "web".',
      },
    },
    customCardSchemeName: {
      type: 'keyString',
      displayName: 'Custom Card Name',
      tooltip: {
        text: 'Card scheme label text to override default.',
      },
    },
    externalSsoAuthUrl: {
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'Authorization URL',
      tooltip: {
        text: 'The SSO url for authentication flow',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const AtriumLoyaltySchema: I9nSchemaLoyalty<IntegrationSystem.AtriumLoyalty> = {
  _id: IntegrationSystem.AtriumLoyalty,
  system: IntegrationSystem.AtriumLoyalty,
  name: 'Atrium (Loyalty)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-atrium-loyalty.png',
  searchTokens: ['atrium-loyalty'],
  searchField: 'client',
  type: 'loyalty',
  canSync: false,
  loyaltyCheckIdKey: 'TODO',
  applyRewardSteps: ['loyalty', 'pos'],
  skipSendOrder: true,
  requiresDiscountId: true,
  supportsCompoundMenuItems: true,
  supportsOrderValidation: false,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.SpotOn],
  // UsernamePassword is for the Web External SSO flow, but we don't actually use our login flow
  supportedAuthMethods: [
    LoyaltyAuthMethod.UsernamePassword,
    LoyaltyAuthMethod.Barcode,
    LoyaltyAuthMethod.Hid,
  ],
  createsTransactions: true,
  supportsSignupInstantEnroll: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    client: {
      type: 'keyString',
      displayName: 'Client',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    key: {
      type: 'keyString',
      displayName: 'Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    decliningBalanceTenderId: {
      type: 'int',
      displayName: 'Declining Balance Tender ID',
      required: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Will be used as a redemption tender for declining balance. Atrium supports taxable and tax exempt declining balance tenders.',
      },
    },
    customCardSchemeName: {
      type: 'keyString',
      displayName: 'Declining Balance Custom Card Name',
      tooltip: {
        text: 'Declining Balance Card scheme label text to override default.',
      },
    },
    mealExchangeBalanceTenderId: {
      type: 'int',
      displayName: 'Meal Exchange Tender ID',
      isNullable: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Symphony ONLY.',
        isWarning: true,
      },
    },
    posGuestFacingMealExchangeI9nId: {
      type: 'keyString',
      displayName: 'POS Guest Facing Meal Exchange I9n ID',
      isNullable: true,
      tooltip: {
        text: 'For Aramark, put the combo meal exchange item the guest can select.',
      },
      writeRight: UserRight.ManageI9n,
    },
    mealExchangeCustomCardSchemeName: {
      type: 'keyString',
      displayName: 'Meal Exchange Custom Card Name',
      isNullable: true,
      tooltip: {
        text: 'Meal Exchange Card scheme label text to override default.',
      },
    },
    flashTerminalId: {
      type: 'keyString',
      displayName: 'Flash Terminal ID',
      identifiesTheLocation: true,
      tooltip: {
        text: 'If value is not set, then the default is "web".',
      },
    },
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
    authMethods: authMethodsFieldSchema,
    useLoyaltyForPayments: useLoyaltyForPaymentsFieldSchema,
    externalSsoAuthUrl: {
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'Authorization URL',
      tooltip: {
        text: 'The SSO url for authentication flow',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const AtriumCampusCardSchema: I9nSchemaCampusCard<IntegrationSystem.AtriumCampusCard> = {
  _id: IntegrationSystem.AtriumCampusCard,
  system: IntegrationSystem.AtriumCampusCard,
  name: 'Atrium (Campus Card)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-atrium-stored-value.png',
  searchTokens: ['atrium-campus-card'],
  searchField: 'client',
  type: 'campus-card',
  // payment tokens are 18 alphanumeric characters for web based payments
  validCardNumberLengths: [9, 18],
  validCardNumberInputRegex: '^[0-9a-zA-Z]$',
  supportsHid: true,
  validatesTaxExemption: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.SpotOn],
  requiredPaymentSystem: null,
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Hid],
  fields: {
    instanceType: instanceTypeFieldSchema,
    client: {
      type: 'keyString',
      displayName: 'Client',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    key: {
      type: 'keyString',
      displayName: 'Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    tenders: {
      type: 'array',
      elementType: 'object',
      elementUniqueIdFieldName: 'tenderNumber',
      ui: 'accordion',
      uiSubtype: 'CampusCardTender',
      placeholder: 'Add a campus card tender',
      displayName: 'Campus Card Tenders',
      required: true,
      minCount: 1,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Redemption tenders for this campus card. Atrium supports multiple taxable and tax exempt declining balance tenders, and at most one meal exchange tender (Simphony ONLY).',
      },
      fields: {
        tenderNumber: {
          type: 'keyString',
          required: true,
          validationRegex: '^[0-9]+$',
          displayName: 'Atrium Tender ID',
          tooltip: {
            text: 'Tender ID configured in Atrium for this account bucket. This value is sent to Atrium with balance checks, sales, and refunds.',
          },
          writeRight: UserRight.ManageI9n,
        },
        displayName: {
          type: 'keyString',
          required: true,
          displayName: 'Guest Facing Name',
          tooltip: {
            text: 'Name shown to guests when selecting a campus-card account.',
          },
          writeRight: UserRight.ManageI9n,
        },
        accountType: {
          type: 'stringEnum',
          enum: CampusCardAccountType,
          enumNameHelper: CampusCardAccountTypeHelper.atriumName,
          required: true,
          defaultValue: CampusCardAccountType.StoredValue,
          displayName: 'Account Type',
          tooltip: {
            text: 'Declining Balance charges the order total to the tender. Meal Exchange redeems one meal (Simphony ONLY; at most one meal exchange tender).',
          },
          ui: 'dropdown',
          writeRight: UserRight.ManageI9n,
        },
        guestFacingItemI9nId: {
          type: 'keyString',
          displayName: 'Guest Facing Meal Item I9n ID',
          isNullable: true,
          tooltip: {
            text: 'Meal exchange tenders only (required for them): the menu item the guest orders to redeem this tender. For Aramark, the combo meal exchange item.',
          },
          writeRight: UserRight.ManageI9n,
          conditions: [
            {
              field: 'accountType',
              values: [CampusCardAccountType.MealPlan],
            },
          ],
        },
      },
    },
    flashTerminalId: {
      type: 'keyString',
      displayName: 'Flash Terminal ID',
      identifiesTheLocation: true,
      tooltip: {
        text: 'If value is not set, then the default is "web".',
      },
    },
    externalSsoAuthUrl: {
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'Authorization URL',
      tooltip: {
        text: 'The SSO url for authentication flow',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const CbordStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.CbordStoredValue> = {
  _id: IntegrationSystem.CbordStoredValue,
  system: IntegrationSystem.CbordStoredValue,
  name: 'CBORD (SV)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-cbord.png',
  searchTokens: ['cbord'],
  searchField: 'institutionId',
  type: 'stored-value',
  isGiftCard: true,
  // payment tokens are 36 alphanumeric characters for web based payments
  validCardNumberLengths: [36, 11],
  validCardNumberInputRegex: '^[0-9a-zA-Z]$',
  supportsHid: true,
  validatesTaxExemption: true,
  requiredPosSystem: null,
  canSync: true,
  incompatiblePosSystems: [IntegrationSystem.Olo],
  requiredPaymentSystem: null,
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Hid],
  fields: {
    instanceType: instanceTypeFieldSchema,
    ugrydTerminalId: {
      type: 'keyString',
      displayName: 'Terminal ID',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    institutionId: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Institution ID',
      identifiesTheLocation: true,
      isSetAfterFirstSync: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Required for web token based sales',
      },
    },
    externalSsoAuthUrl: {
      type: 'keyString',
      isHidden: true,
      isReadOnly: true,
    },
  },
};

const CbordCampusCardSchema: I9nSchemaCampusCard<IntegrationSystem.CbordCampusCard> = {
  _id: IntegrationSystem.CbordCampusCard,
  system: IntegrationSystem.CbordCampusCard,
  name: 'CBORD (Campus Card)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-cbord.png',
  searchTokens: ['cbord'],
  searchField: 'institutionId',
  type: 'campus-card',
  validCardNumberLengths: [36, 11],
  validCardNumberInputRegex: '^[0-9a-zA-Z]$',
  supportsHid: true,
  validatesTaxExemption: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.Olo],
  requiredPaymentSystem: null,
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Hid],
  canSync: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    ugrydTerminalId: {
      type: 'keyString',
      displayName: 'Terminal ID',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    institutionId: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Institution ID',
      identifiesTheLocation: true,
      isSetAfterFirstSync: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Required for web token based sales',
      },
    },
    externalSsoAuthUrl: {
      type: 'keyString',
      isHidden: true,
      isReadOnly: true,
    },
  },
};

const PinataCampusCardSchema: I9nSchemaCampusCard<IntegrationSystem.PinataCampusCard> = {
  _id: IntegrationSystem.PinataCampusCard,
  system: IntegrationSystem.PinataCampusCard,
  name: 'Pinata (Campus Card)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata-stored-value.png',
  searchTokens: ['pinata-campus-card'],
  searchField: null,
  type: 'campus-card',
  validCardNumberLengths: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  validCardNumberInputRegex: '^[0-9a-zA-Z]$',
  validPinInputRegex: '^[0-9a-zA-Z]$',
  validPinLengths: [5, 6, 7, 8],
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  readMethods: [HardwareReadMethod.Scanner, HardwareReadMethod.Msr],
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PinataApiServer>(PinataApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    requestDelay: {
      type: 'int',
      displayName: 'Request Delay (ms)',
      writeRight: UserRight.ManageI9n,
    },
    requiresPin: {
      type: 'bool',
      displayName: 'Show PIN Input on Campus Card view',
      writeRight: UserRight.ManageI9n,
    },
  },
};

const QuickchargeCampusCardSchema: I9nSchemaCampusCard<IntegrationSystem.QuickchargeCampusCard> = {
  _id: IntegrationSystem.QuickchargeCampusCard,
  system: IntegrationSystem.QuickchargeCampusCard,
  name: 'Quickcharge (Campus Card)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-quickcharge.png',
  searchTokens: ['quickcharge', 'mmhayes', 'transact'],
  searchField: null,
  type: 'campus-card',
  validCardNumberLengths: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  validCardNumberInputRegex: '^[0-9a-zA-Z]$',
  supportsHid: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Hid],
  canSync: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    baseUrl: {
      type: 'url',
      displayName: 'Quickcharge Base URL',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Customer-specific URL, e.g. https://example.com/pos740/api',
      },
    },
    tenderName: {
      type: 'keyString',
      displayName: 'Tender Name',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Defaults to "quickcharge". May be customized per customer (e.g. "Payroll Deduct", "Badge Payment").',
      },
    },
  },
};

const TransactCampusCardSchema: I9nSchemaCampusCard<
  IntegrationSystem.TransactCampusCard,
  | 'transactCampusCardHostSystem'
  | 'vendorNumber'
  | 'encryptionKey'
  | 'serverAddress'
  | 'serverPort'
  | 'tenders'
  | 'cardDataRegex'
  | 'tiaRetrySettings'
> = {
  _id: IntegrationSystem.TransactCampusCard,
  system: IntegrationSystem.TransactCampusCard,
  name: 'Transact (Campus Card)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-transact-tse.png',
  searchTokens: ['transact', 'tia', 'tse', 'idx'],
  searchField: null,
  type: 'campus-card',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  supportsHid: true,
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Hid],
  fields: {
    transactCampusCardHostSystem: {
      type: 'stringEnum',
      enum: TransactCampusCardHostSystem,
      enumNameHelper: TransactCampusCardHostSystemHelper.name,
      displayName: 'Transact Host System',
      required: true,
      defaultValue: TransactCampusCardHostSystem.TSE,
      tooltip: {
        text: 'Selects host-specific TIA account lookup behavior. TSE uses balance inquiry; IDX uses authorization-limit/count-balance.',
      },
      ui: 'dropdown',
      writeRight: UserRight.ManageI9n,
    },
    vendorNumber: {
      type: 'keyString',
      displayName: 'Vendor Number',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    encryptionKey: {
      type: 'keyString',
      displayName: 'AES-128 Encryption Key',
      required: true,
      requiresValidationIfChanged: true,
      validationRegex: '^[0-9a-fA-F]{32}$',
      maskInput: true,
      writeRight: UserRight.ManageI9n,
    },
    serverAddress: {
      type: 'keyString',
      displayName: 'TIA Server Address',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    serverPort: {
      type: 'int',
      displayName: 'TIA Server Port',
      required: true,
      min: 1,
      max: 65535,
      writeRight: UserRight.ManageI9n,
    },
    tenders: {
      type: 'array',
      elementType: 'object',
      ui: 'accordion',
      uiSubtype: 'CampusCardTender',
      displayName: 'Campus Card Tenders',
      required: true,
      minCount: 1,
      writeRight: UserRight.ManageI9n,
      fields: {
        tenderNumber: {
          type: 'keyString',
          required: true,
          displayName: 'Transact Tender Number',
          tooltip: {
            text: 'Tender number configured in Transact for this account bucket. This value is sent to TIA with balance checks, sales, and refunds.',
          },
          writeRight: UserRight.ManageI9n,
        },
        displayName: {
          type: 'keyString',
          required: true,
          displayName: 'Guest Display Name',
          tooltip: {
            text: 'Name shown to guests when selecting a campus-card account.',
          },
          writeRight: UserRight.ManageI9n,
        },
        accountType: {
          type: 'stringEnum',
          enum: TransactCampusCardAccountType,
          enumNameHelper: TransactCampusCardAccountTypeHelper.name,
          required: true,
          defaultValue: TransactCampusCardAccountType.StoredValue,
          displayName: 'TIA Transaction Type Family',
          tooltip: {
            text: 'Controls which TIA transaction type BITE sends: Stored Value uses debit/auth-limit, Meal Plan uses count/count-balance, and Cash Equivalency uses cash-equivalency.',
          },
          ui: 'dropdown',
          writeRight: UserRight.ManageI9n,
        },
      },
    },
    cardDataRegex: {
      type: 'keyString',
      displayName: 'Card Data Regex',
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'Advanced. Overrides how the kiosk extracts card data from a swipe. Must contain exactly one capture group, which becomes the value sent to TIA. Leave blank to send the full Track 2 data (PAN, separator, and expiration field) with the sentinels stripped, which is what TSE expects. REQUIRED when this location also has a gift card integration: one reader serves both, so this pattern is what tells a campus card apart from a gift card, and it must match campus cards only. Example: ^;?(0900\\d+=\\d+)\\??$',
      },
      writeRight: UserRight.ManageI9n,
    },
    tiaRetrySettings: {
      type: 'object',
      displayName: 'TIA Retry Settings',
      isNullable: true,
      defaultValue: null,
      tooltip: {
        text: 'Optional TIA dropped-response retry tuning. Defaults match Transact certification guidance: 100, 300, 900, 1200ms on the first socket, then 300, 900, 1200ms after reconnect.',
      },
      writeRight: UserRight.ManageI9n,
      fields: {
        firstSocketAttempt1ResponseTimeoutMs: {
          type: 'int',
          displayName: 'Request 1 Wait (ms)',
          placeholder: 'Default: 100',
          tooltip: {
            text: 'How long to wait for the first TIA response before retrying the same sequence number. Leave blank to use the code default of 100ms.',
          },
          min: 1,
          writeRight: UserRight.ManageI9n,
        },
        firstSocketAttempt2ResponseTimeoutMs: {
          type: 'int',
          displayName: 'Request 2 Wait (ms)',
          placeholder: 'Default: 300',
          tooltip: {
            text: 'How long to wait after the second send on the original socket. John recommended 500-600ms for the current sandbox/site. Leave blank to use the code default of 300ms.',
          },
          min: 1,
          writeRight: UserRight.ManageI9n,
        },
        firstSocketAttempt3ResponseTimeoutMs: {
          type: 'int',
          displayName: 'Request 3 Wait (ms)',
          placeholder: 'Default: 900',
          tooltip: {
            text: 'How long to wait after the third send on the original socket. Leave blank to use the code default of 900ms.',
          },
          min: 1,
          writeRight: UserRight.ManageI9n,
        },
        firstSocketAttempt4ResponseTimeoutMs: {
          type: 'int',
          displayName: 'Request 4 Wait (ms)',
          placeholder: 'Default: 1200',
          tooltip: {
            text: 'How long to wait after the fourth send on the original socket before reconnecting. Leave blank to use the code default of 1200ms.',
          },
          min: 1,
          writeRight: UserRight.ManageI9n,
        },
        reconnectedSocketAttempt1ResponseTimeoutMs: {
          type: 'int',
          displayName: 'Request 5 Wait After Reconnect (ms)',
          placeholder: 'Default: 300',
          tooltip: {
            text: 'How long to wait after reconnecting and sending the same sequence number again. Leave blank to use the code default of 300ms.',
          },
          min: 1,
          writeRight: UserRight.ManageI9n,
        },
        reconnectedSocketAttempt2ResponseTimeoutMs: {
          type: 'int',
          displayName: 'Request 6 Wait After Reconnect (ms)',
          placeholder: 'Default: 900',
          tooltip: {
            text: 'How long to wait after the second send on the reconnected socket. Leave blank to use the code default of 900ms.',
          },
          min: 1,
          writeRight: UserRight.ManageI9n,
        },
        reconnectedSocketAttempt3ResponseTimeoutMs: {
          type: 'int',
          displayName: 'Request 7 Wait After Reconnect (ms)',
          placeholder: 'Default: 1200',
          tooltip: {
            text: 'How long to wait after the final retry before falling into offline handling. Leave blank to use the code default of 1200ms.',
          },
          min: 1,
          writeRight: UserRight.ManageI9n,
        },
        reconnectAfterAttemptCount: {
          type: 'int',
          displayName: 'Reconnect After Attempt Count',
          placeholder: 'Default: 4',
          tooltip: {
            text: 'Number of attempts to make on the first TIA socket before reconnecting and continuing the same-sequence retry sequence.',
          },
          min: 1,
          writeRight: UserRight.ManageI9n,
        },
      },
    },
  },
};

// PAR Brink Loyalty is still available for existing customers, but is EOL and not available for
// new customers.
const ParBrinkLoyaltySchema: I9nSchemaLoyaltyEarner<IntegrationSystem.ParBrinkLoyaltyEarner> = {
  _id: IntegrationSystem.ParBrinkLoyaltyEarner,
  system: IntegrationSystem.ParBrinkLoyaltyEarner,
  name: 'PAR Brink Loyalty Earner',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-par.png',
  searchTokens: ['brink-loyalty'],
  searchField: 'groupToken',
  type: 'loyalty-earner',
  requiredPosSystem: IntegrationSystem.ParBrink,
  allowUserToLogIn: true,
  skipSendOrder: true,
  supportedAuthMethods: [LoyaltyAuthMethod.PhoneNumber, LoyaltyAuthMethod.Barcode],
  fields: {
    groupToken: {
      type: 'keyString',
      displayName: 'Group Token',
      required: true,
      requiresValidationIfChanged: true,
      validationRegex: '^[0-9a-fA-F]{32}$',
      tooltip: {
        text: 'GroupId with no hyphens. Ex: 3bcbf6b53ea049de863ee7b1c7fe4cd9',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    loyaltyAdjustmentReasonId: {
      type: 'keyString',
      displayName: 'Loyalty Adjustment Reason Id',
      validationRegex: '^[0-9]+$',
      tooltip: {
        text: 'See Brink Loyalty Onboarding Docs.',
      },
      writeRight: UserRight.ManageI9n,
    },
    loyaltyPlanId: {
      type: 'keyString',
      displayName: 'Loyalty Plan Id',
      validationRegex: '^[0-9]+$',
      tooltip: {
        text: 'See Brink Loyalty Onboarding Docs.',
      },
      writeRight: UserRight.ManageI9n,
    },
    manuallyAddPoints: {
      type: 'bool',
      displayName: 'Add Points Manually',
      tooltip: {
        text: 'See Brink Loyalty Onboarding Docs.',
      },
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
  },
};

const ParPosDataSourceSchema: I9nSchemaPosDataSource<IntegrationSystem.ParPosDataSource> = {
  _id: IntegrationSystem.ParPosDataSource,
  system: IntegrationSystem.ParPosDataSource,
  name: 'PAR POS Data Source',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-par-pos-data.png',
  searchTokens: ['brink'],
  searchField: 'locationToken',
  type: 'pos-data-source',
  fields: {
    locationToken: brinkLocationTokenFieldSchema,
    apiServer: brinkApiServerFieldSchema,
  },
};

const PinataPosSchema: I9nSchemaPos<IntegrationSystem.PinataPos> = {
  _id: IntegrationSystem.PinataPos,
  system: IntegrationSystem.PinataPos,
  name: 'Pinata',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata.png',
  searchTokens: ['pinata'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'pinataId',
  locationIdKey: 'locationID',
  supportsPosCoupons: true,
  couponsAndLoyaltyEarningCompatible: true,
  supportsTips: true,
  supportsOrderStatusPolling: true,
  supportsOrderStatusWebhooks: true,
  supportsPayingWithCash: true,
  syncsSections: true,
  syncsMenuStructures: true,
  syncsDiscounts: true,
  supportsApplyingDiscounts: true,
  supportsCompoundMenuItems: true,
  manuallySetMultipleOfSameMod: true,
  supportsOrderCreation: true,
  supportsConcurrentValidation: true,
  skipRedundantValidation: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PinataApiServer>(PinataApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    locationID: {
      type: 'keyString',
      displayName: 'Location Id',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    requestDelay: {
      type: 'int',
      displayName: 'Request Delay (ms)',
      tooltip: {
        text: 'Useful for testing slow APIs.',
      },
      writeRight: UserRight.ManageI9n,
    },
    validateRequestDelay: {
      type: 'int',
      displayName: 'Validate Request Delay (ms)',
      tooltip: {
        text: 'Useful for testing timeouts.',
      },
      writeRight: UserRight.ManageI9n,
    },
    sendOrderRequestDelay: {
      type: 'int',
      displayName: 'Send Order Request Delay (ms)',
      tooltip: {
        text: 'Useful for testing timeouts.',
      },
      writeRight: UserRight.ManageI9n,
    },
    syncMenuImages: {
      type: 'bool',
      displayName: 'Sync Menu Item Images',
      writeRight: UserRight.ManageI9n,
    },
    syncModifierImages: {
      type: 'bool',
      displayName: 'Sync Modifier Images',
      writeRight: UserRight.ManageI9n,
    },
    syncMenuSectionImages: {
      displayName: 'Sync Menu Section Images',
      type: 'stringEnum',
      enum: SyncMenuSectionImagesMode,
      defaultValue: SyncMenuSectionImagesMode.DoNotSync,
      writeRight: UserRight.ManageI9n,
      ui: 'dropdown',
    },
    supportsTipValidation: {
      type: 'bool',
      displayName: 'Supports Tip Validation',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'If enabled, Bite will validate tips on the Pinata POS during order validation.',
      },
    },
    shouldUseI9nIdFallback: {
      type: 'bool',
      displayName: 'Use I9n ID Fallback',
      tooltip: {
        text: 'If we fail to match an I9n model to a Bite model during sync using the I9n ID, then we will attempt to match using the POS ID. If POS ID does not match, then we will attempt to use the name.',
      },
    },
    breakUpTaxes: {
      type: 'bool',
      displayName: 'Break Up Taxes',
      writeRight: UserRight.ManageI9n,
      isHidden: true,
      tooltip: {
        text: 'Test-only: when enabled, Pinata returns a State/County/City tax breakdown instead of a single combined tax so the vitrine displayIndividualTaxes setting can be exercised in e2e tests.',
      },
    },
    promptForDonationRoundUp: promptForDonationSchema,
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'keyString',
      displayName: 'POS Order Type',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    ...generateCardSpecificTenderTypeSchema({
      isSetAfterFirstSync: true,
      tooltipText: 'Not all Tender Types accept our orders. Set after first sync.',
    }),
    taxExemptOrderTypeID: {
      type: 'keyString',
      displayName: 'Tax Exempt Tender Type',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'Used for EBT Food Stamp tax exemption. Set after first sync.',
      },
    },
  },
};

const PinataLoyaltySchema: I9nSchemaLoyalty<IntegrationSystem.PinataLoyalty> = {
  _id: IntegrationSystem.PinataLoyalty,
  system: IntegrationSystem.PinataLoyalty,
  name: 'Pinata Loyalty',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata-loyalty.png',
  searchTokens: ['pinata-loyalty'],
  searchField: 'locationID',
  type: 'loyalty',
  requiresDiscountId: true,
  waitForSendOrderBeforePrint: true,
  loyaltyCheckIdKey: 'id',
  applyRewardSteps: ['loyalty', 'pos'],
  couponsAndLoyaltyEarningCompatible: true,
  supportsCompoundMenuItems: true,
  supportsCoupons: true,
  supportsRewardsNoOrder: true,
  skipRedundantRewardFetch: true,
  supportsDistinguishingApplicableRewards: true,
  supportsOrderValidation: false,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  supportsOrderCreation: true,
  createsTransactions: true,
  canSync: false,
  supportedAuthMethods: allEnumValues(LoyaltyAuthMethod),
  // e2e OTP stand-in — phone-only like real providers, though login supports all methods.
  otpEligibleAuthMethods: [LoyaltyAuthMethod.PhoneNumber],
  validCardNumberLengths: [16],
  supportsOrderHistoryLookUp: true,
  supportsSignupInstantEnroll: true,
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PinataApiServer>(PinataApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    locationID: {
      type: 'keyString',
      displayName: 'Location Id',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    requestDelay: {
      type: 'int',
      displayName: 'Request Delay (ms)',
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    enableOtp: enableOtpFieldSchema,
    allowRedemptionCodeWithReward: {
      type: 'bool',
      displayName: 'Allow redemption code with reward',
      tooltip: {
        text: 'Allows the user to also redeem a redemption code (points) along with loyalty reward(s).',
      },
    },
    useLoyaltyForPayments: useLoyaltyForPaymentsFieldSchema,
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    overrideDistinguishingApplicableRewards: overrideDistinguishingApplicableRewardsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const StripeSchema: I9nSchemaEcommPayment<IntegrationSystem.Stripe> = {
  _id: IntegrationSystem.Stripe,
  system: IntegrationSystem.Stripe,
  name: 'Stripe',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-stripe.png',
  searchTokens: ['stripe'],
  searchField: 'syncedData.authData.stripeAccountId',
  type: 'ecomm-payment',
  requiresOAuth: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  // https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
  minTransactionAmount: 50,
  supportsStoringPaymentMethods: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    baseRate: {
      type: 'float',
      displayName: 'Visa/MasterCard Rate',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Set the % to charge for Visa/MasterCard transactions as 0.02 for 2%',
      },
    },
    baseFlatRate: {
      type: 'int',
      displayName: 'Visa/MasterCard Flat Rate',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Set the fixed amount to charge for Visa/MasterCard transactions as 30 for $0.30',
      },
    },
    amexRate: {
      type: 'float',
      displayName: 'Amex Rate',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Set the % to charge for Amex transactions as 0.02 for 2%',
      },
    },
    amexFlatRate: {
      type: 'int',
      displayName: 'Amex Flat Rate',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Set the fixed amount to charge for Amex transactions as 30 for $0.30',
      },
    },
    statementDescriptor: {
      type: 'keyString',
      displayName: 'Statement Descriptor',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: "Set the statement descriptor to show on the customer's credit card statement. Use 5-22 characters.",
      },
      validationRegex: '^[a-zA-Z0-9\\s.-]{5,22}$',
    },
  },
};

const StripeTerminalSchema: I9nSchemaKioskPayment<IntegrationSystem.StripeTerminal> = {
  _id: IntegrationSystem.StripeTerminal,
  system: IntegrationSystem.StripeTerminal,
  name: 'Stripe Terminal',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-stripe-terminal.png',
  searchTokens: ['stripe-terminal'],
  searchField: 'syncedData.authData.stripeAccountId',
  type: 'kiosk-payment',
  apiPayment: {
    canActivateTerminals: true,
    canCancelPayment: true,
    canDeactivateTerminals: false, // this is most likely true but not implemented yet
    canFetchListOfTerminals: true,
    canFetchTerminalStatus: true,
    canFetchTerminalModel: true,
    canUpdateTerminalIdleText: false,
    canWakeUpTerminals: false,
  },
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiresOAuth: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    baseRate: {
      type: 'float',
      displayName: 'Visa/MasterCard Rate',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Set the % to charge for Visa/MasterCard and Non-Amex transactions as 0.02 for 2%',
      },
    },
    baseFlatRate: {
      type: 'int',
      displayName: 'Visa/MasterCard Flat Rate',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Set the fixed amount to charge for Visa/MasterCard and Non-Amex transactions as 30 for $0.30',
      },
    },
    amexRate: {
      type: 'float',
      displayName: 'Amex Rate',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Set the % to charge for Amex transactions as 0.02 for 2%',
      },
    },
    amexFlatRate: {
      type: 'int',
      displayName: 'Amex Flat Rate',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Set the fixed amount to charge for Amex transactions as 30 for $0.30',
      },
    },
    // The location id required to assist with filtering terminals registered to the stripe account
    stripeLocationId: {
      type: 'keyString',
      displayName: 'Stripe Location ID',
      isHidden: true,
      writeRight: UserRight.ManageI9n,
    },
    statementDescriptor: {
      type: 'keyString',
      displayName: 'Statement Descriptor',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: "Set the statement descriptor to show on the customer's credit card statement. Use 5-22 characters.",
      },
      validationRegex: '^[a-zA-Z0-9\\s.-]{5,22}$',
    },
  },
};

const PinataPaySchema: I9nSchemaKioskPayment<IntegrationSystem.PinataPay> = {
  _id: IntegrationSystem.PinataPay,
  system: IntegrationSystem.PinataPay,
  name: 'Pinata Pay',
  isBrandSpecific: false,
  type: 'kiosk-payment',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  minTransactionAmount: 0,
  apiPayment: {
    canActivateTerminals: true,
    canCancelPayment: true,
    canDeactivateTerminals: true,
    canFetchListOfTerminals: true,
    canFetchTerminalStatus: true,
    canFetchTerminalModel: false,
    canUpdateTerminalIdleText: false,
    canWakeUpTerminals: false,
  },
  canDisplayText: false,
  canSync: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata.png',
  searchTokens: ['pinata-pay'],
  searchField: null,
  fields: {
    pinataPayTransactionResult: {
      displayName: 'Transaction Result',
      type: 'stringEnum',
      enum: PinataPayTransactionResult,
      enumNameHelper: PinataPayTransactionResultHelper.name,
      ui: 'dropdown',
      defaultValue: PinataPayTransactionResult.Approval,
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    pinataPayTerminalState: {
      displayName: 'Terminal State',
      type: 'int',
      enum: KioskPaymentTerminalState,
      enumNameHelper: KioskPaymentTerminalStateHelper.name,
      ui: 'dropdown',
      defaultValue: KioskPaymentTerminalState.READY,
      writeRight: UserRight.ManageI9n,
    },
    pinataPaySurcharge: {
      type: 'price',
      displayName: 'Surcharge',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Added on top of the requested amount on approved payments, like a card surcharge from a real terminal',
      },
    },
    ...ebtFieldSchemas,
  },
};

const PinataPayNativeSchema: I9nSchemaKioskPayment<IntegrationSystem.PinataPayNative> = {
  _id: IntegrationSystem.PinataPayNative,
  system: IntegrationSystem.PinataPayNative,
  name: 'Pinata Pay Native',
  isBrandSpecific: false,
  type: 'kiosk-payment',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  minTransactionAmount: 0,
  canDisplayText: false,
  canSync: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata.png',
  searchTokens: ['pinata-pay'],
  searchField: null,
  apiPayment: null,
  fields: {
    pinataPayTransactionResult: {
      displayName: 'Transaction Result',
      type: 'stringEnum',
      enum: PinataPayTransactionResult,
      enumNameHelper: PinataPayTransactionResultHelper.name,
      ui: 'dropdown',
      defaultValue: PinataPayTransactionResult.Approval,
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    ...ebtFieldSchemas,
  },
};

const PinataPayStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.PinataPayStoredValue> = {
  _id: IntegrationSystem.PinataPayStoredValue,
  system: IntegrationSystem.PinataPayStoredValue,
  name: 'Pinata Pay Stored Value (Terminal)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata.png',
  searchTokens: ['pinata'],
  searchField: null, // We don't have a good identifier here
  type: 'stored-value',
  isGiftCard: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: IntegrationSystem.PinataPay,
  usesApiPayment: true,
  readMethods: [HardwareReadMethod.Terminal],
  supportsStoreValueBalanceLookup: true,
  fields: {
    pinataPayTransactionResult: {
      displayName: 'Transaction Result',
      type: 'stringEnum',
      enum: PinataPayTransactionResult,
      enumNameHelper: PinataPayTransactionResultHelper.name,
      ui: 'dropdown',
      defaultValue: PinataPayTransactionResult.Approval,
      required: true,
      writeRight: UserRight.ManageI9n,
    },
  },
};

const PinataPayNativeStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.PinataPayNativeStoredValue> =
  {
    _id: IntegrationSystem.PinataPayNativeStoredValue,
    system: IntegrationSystem.PinataPayNativeStoredValue,
    name: 'Pinata Pay Native Stored Value (Terminal)',
    isBrandSpecific: false,
    iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata.png',
    searchTokens: ['pinata'],
    searchField: null, // We don't have a good identifier here
    type: 'stored-value',
    isGiftCard: true,
    requiredPosSystem: null,
    incompatiblePosSystems: [],
    requiredPaymentSystem: IntegrationSystem.PinataPay,
    readMethods: [HardwareReadMethod.Terminal],
    fields: {
      pinataPayTransactionResult: {
        displayName: 'Transaction Result',
        enum: PinataPayTransactionResult,
        enumNameHelper: PinataPayTransactionResultHelper.name,
        type: 'stringEnum',
        ui: 'dropdown',
        defaultValue: PinataPayTransactionResult.Approval,
        required: true,
        writeRight: UserRight.ManageI9n,
      },
    },
  };

const FreedomPayFlashSchema: I9nSchemaEcommPayment<IntegrationSystem.FreedomPayFlash> = {
  _id: IntegrationSystem.FreedomPayFlash,
  system: IntegrationSystem.FreedomPayFlash,
  name: 'FreedomPay Flash',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-freedompay-ecomm.png',
  searchTokens: ['freedompay-flash'],
  searchField: 'esKey',
  type: 'ecomm-payment',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  minTransactionAmount: 0,
  supportsStoringPaymentMethods: true,
  // These fields are identical to those of FreedomPayKiosk.
  // If you change one, you may need to change the other.
  fields: {
    instanceType: instanceTypeFieldSchema,
    esKey: {
      type: 'keyString',
      displayName: 'EsKey',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    storeId: {
      type: 'keyString',
      displayName: 'Store ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    terminalId: {
      type: 'keyString',
      displayName: 'Terminal ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
  },
};

const TwilioSchema: I9nSchemaSmsNotifier<IntegrationSystem.Twilio> = {
  _id: IntegrationSystem.Twilio,
  system: IntegrationSystem.Twilio,
  name: 'Twilio',
  isBrandSpecific: false,
  searchTokens: ['twilio'],
  searchField: 'accountSid',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-twilio.png',
  type: 'sms-notifier',
  isSmsNotifier: true,
  canSync: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    accountSid: {
      type: 'keyString',
      displayName: 'Account SID',
      required: true,
      validationRegex: '^AC[a-z0-9]{32}$',
      requiresValidationIfChanged: true,
      placeholder: 'AC59f9bbba55e7bb83305cef281ba2d070',
      writeRight: UserRight.ManageI9n,
    },
    authToken: {
      type: 'keyString',
      displayName: 'Auth Token',
      required: true,
      validationRegex: '^[a-z0-9]{32}$',
      requiresValidationIfChanged: true,
      placeholder: '59f9bbba55e7bb83305cef281ba2d071',
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    senderPhoneNumberId: {
      type: 'keyString',
      displayName: 'Sender Phone Number',
      ui: 'dropdown',
      writeRight: UserRight.ManageI9nLite,
      isSetAfterFirstSync: true,
    },
    senderMessageGroupId: {
      type: 'keyString',
      displayName: 'Sender Message Group',
      validationRegex: '^MG[a-z0-9]{32}$',
      placeholder: 'MG59f9bbba55e7bb83305cef281ba2d072',
      writeRight: UserRight.ManageI9n,
    },
  },
};

const PinataNotifierSchema: I9nSchemaSmsNotifier<IntegrationSystem.PinataNotifier> = {
  _id: IntegrationSystem.PinataNotifier,
  system: IntegrationSystem.PinataNotifier,
  name: 'Pinata Notifier',
  isBrandSpecific: false,
  searchTokens: ['pinata-notifier', 'pinata-notify', 'pinata-sms'],
  searchField: 'notifierId',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata-notifier.png',
  type: 'sms-notifier',
  isSmsNotifier: true,
  requiredPosSystem: IntegrationSystem.PinataPos,
  canSync: false,
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PinataApiServer>(PinataApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    notifierId: {
      type: 'keyString',
      displayName: 'Notifier Id',
      required: true,
      validationRegex: '^[a-z0-9]+$',
      requiresValidationIfChanged: true,
      placeholder: 'abc123',
      writeRight: UserRight.ManageI9n,
    },
  },
};

const ComoSchema: I9nSchemaLoyalty<IntegrationSystem.Como> = {
  _id: IntegrationSystem.Como,
  system: IntegrationSystem.Como,
  name: 'Como Loyalty',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-como.png',
  type: 'loyalty',
  searchTokens: ['como'],
  searchField: 'apiKey',
  loyaltyCheckIdKey: 'comoConfirmation',
  requiresDiscountId: true,
  applyRewardSteps: ['loyalty', 'pos'],
  supportsCompoundMenuItems: true,
  couponsAndLoyaltyEarningCompatible: true,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: true,
  requiredPosSystem: null,
  supportsCoupons: true,
  supportsOrderValidation: false,
  incompatiblePosSystems: [IntegrationSystem.Olo, IntegrationSystem.SpotOn],
  canSync: false,
  supportedAuthMethods: [LoyaltyAuthMethod.PhoneNumber, LoyaltyAuthMethod.Barcode],
  createsTransactions: true,
  supportsSignupInstantEnroll: false,

  fields: {
    apiKey: {
      displayName: 'API Key',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    authMethods: authMethodsFieldSchema,
    businessID: {
      type: 'keyString',
      displayName: 'Business ID',
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    branchID: {
      type: 'keyString',
      displayName: 'Branch ID',
      identifiesTheLocation: true,
      tooltip: {
        text: 'Optional. Defaults to the Bite Org and location name.',
      },
      writeRight: UserRight.ManageI9n,
    },
    sendOrderWithoutAuth: sendOrderWithoutAuthFieldSchema,
    enablePointsRedemption: {
      type: 'bool',
      displayName: 'Allow points redemption reward',
      tooltip: {
        text: 'If set to true, guests will be able to redeem Como points/credits towards the check total.',
      },
      defaultValue: false,
    },
    transactionIdSource: {
      displayName: 'Transaction ID Source',
      type: 'stringEnum',
      enum: ComoTransactionIdSource,
      enumNameHelper: ComoTransactionIdSourceHelper.name,
      required: true,
      ui: 'dropdown',
      tooltip: {
        text: `Which Bite order field to use for Como's transaction ID.`,
      },
    },
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    overrideDistinguishingApplicableRewards: overrideDistinguishingApplicableRewardsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const ComoOloSchema: I9nSchemaLoyalty<IntegrationSystem.ComoOlo> = {
  _id: IntegrationSystem.ComoOlo,
  system: IntegrationSystem.ComoOlo,
  name: 'Como+Olo Loyalty',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-como-olo.png',
  type: 'loyalty',
  searchTokens: ['como', 'como-olo'],
  searchField: 'apiKey',
  loyaltyCheckIdKey: 'comoConfirmation',
  requiresDiscountId: false,
  applyRewardSteps: ['loyalty', 'pos'],
  supportsCompoundMenuItems: true,
  couponsAndLoyaltyEarningCompatible: true,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: false,
  requiredPosSystem: IntegrationSystem.Olo,
  supportsCoupons: true,
  supportsOrderHistoryLookUp: true,
  supportsOrderValidation: false,
  canSync: false,
  supportedAuthMethods: [LoyaltyAuthMethod.PhoneNumber, LoyaltyAuthMethod.Barcode],
  supportsSignupInstantEnroll: false,

  fields: {
    apiKey: {
      displayName: 'API Key',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    businessID: {
      type: 'keyString',
      displayName: 'Business ID',
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    branchID: {
      type: 'keyString',
      displayName: 'Branch ID',
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const ComoEarnerSchema: I9nSchemaLoyaltyEarner<IntegrationSystem.ComoEarner> = {
  _id: IntegrationSystem.ComoEarner,
  system: IntegrationSystem.ComoEarner,
  name: 'Como Loyalty Earner',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-como-earner.png',
  searchTokens: ['como-earner'],
  searchField: 'apiKey',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  type: 'loyalty-earner',
  allowUserToLogIn: false,
  skipSendOrder: false,
  fields: {
    apiKey: {
      displayName: 'API Key',
      type: 'keyString',
      required: true,
      validationRegex: '^[0-9a-f]{8}$',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    businessID: {
      type: 'keyString',
      displayName: 'Business ID',
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    branchID: {
      type: 'keyString',
      displayName: 'Branch ID',
      identifiesTheLocation: true,
      tooltip: {
        text: 'Optional. Defaults to the Bite Org and location name.',
      },
      writeRight: UserRight.ManageI9n,
    },
    transactionIdSource: {
      displayName: 'Transaction ID Source',
      type: 'stringEnum',
      enum: ComoTransactionIdSource,
      enumNameHelper: ComoTransactionIdSourceHelper.name,
      required: true,
      tooltip: {
        text: `Which Bite order field to use for Como's transaction ID.`,
      },
    },
  },
};

const IncentivioSchema: I9nSchemaLoyalty<IntegrationSystem.Incentivio> = {
  _id: IntegrationSystem.Incentivio,
  system: IntegrationSystem.Incentivio,
  name: 'Incentivio Loyalty',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-incentivio.png',
  type: 'loyalty',
  searchTokens: ['incentivio'],
  searchField: 'locationID',
  loyaltyCheckIdKey: 'incentivioTransactionId',
  requiresDiscountId: true,
  applyRewardSteps: ['loyalty', 'pos'],
  supportsCompoundMenuItems: true,
  supportsOrderValidation: false,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.Olo],
  canSync: false,
  supportedAuthMethods: [
    LoyaltyAuthMethod.Barcode,
    LoyaltyAuthMethod.PhoneNumber,
    LoyaltyAuthMethod.EmailPhoneNumber,
    LoyaltyAuthMethod.CardNumber,
  ],
  validCardNumberLengths: [16],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    incentivioClientId: {
      type: 'keyString',
      displayName: 'Client ID',
      required: true,
      requiresValidationIfChanged: true,
    },
    incentivioSecret: {
      type: 'keyString',
      displayName: 'Secret',
      required: true,
      requiresValidationIfChanged: true,
    },
    locationID: {
      type: 'keyString',
      displayName: 'Location ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
    },
    authMethods: authMethodsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
    showInapplicableRewards: {
      type: 'bool',
      displayName: 'Show Inapplicable Rewards',
      tooltip: {
        text: `If enabled, rewards which can not be currently applied to the order will be shown to the guest, but will not be selectable until the order meets the reward's requirements.`,
      },
    },
  },
};

const FreedomPayKioskSchema: I9nSchemaKioskPayment<IntegrationSystem.FreedomPayKiosk> = {
  _id: IntegrationSystem.FreedomPayKiosk,
  system: IntegrationSystem.FreedomPayKiosk,
  name: 'FreedomPay Terminal',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-freedompay-kiosk.png',
  searchTokens: ['freedompay-kiosk', 'freedompay-terminal'],
  searchField: 'esKey',
  type: 'kiosk-payment',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  apiPayment: null,
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  // These fields are identical to those of FreedomPayFlash.
  // If you change one, you may need to change the other.
  fields: {
    instanceType: instanceTypeFieldSchema,
    esKey: {
      type: 'keyString',
      displayName: 'EsKey',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    storeId: {
      type: 'keyString',
      displayName: 'Store ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    fccServerIp: {
      type: 'keyString',
      displayName: 'FCC Server IP',
      writeRight: UserRight.ManageI9n,
    },
    terminalIds: {
      type: 'array',
      elementType: 'keyString',
      displayName: 'Terminal IDs',
      minCount: 1,
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    binMapUrl: {
      type: 'keyString',
      displayName: 'Custom Bin Map URL',
      tooltip: {
        text: 'If you are using a custom bin map, please provide the URL here.',
      },
      validationRegex:
        '^https://(assets\\.getbite\\.com/integration/freedompay/.*|integration-assets\\.getbite\\.com/freedompay/bin-map/.*)$',
      writeRight: UserRight.ManageI9n,
    },
    ...ebtFieldSchemas,
    enableSaf: {
      type: 'bool',
      defaultValue: false,
      displayName: 'Enable SAF (Store and Forward)',
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'A transaction will only be processed as Store and Forward if this option is enabled. The default value is false.',
      },
    },
    posFloorLimit: {
      type: 'int',
      required: true,
      displayName: 'POS Floor Limit',
      min: 1,
      defaultValue: 150,
      tooltip: {
        text: 'The maximum dollar amount which will be accepted in an Offline Accept. The default value is 150.',
      },
      conditions: [
        {
          field: 'enableSaf',
          values: [true],
        },
      ],
    },
    maxDaysAllowedOffline: {
      type: 'int',
      required: true,
      displayName: 'Max Days Allowed Offline',
      min: 1,
      defaultValue: 5,
      tooltip: {
        text: 'Prevent SaF from executing if we have not done a successful online transaction in X amount of days. This is a safeguard around doing too many SaF transactions with a poorly configured POS. A value less than or equal to 0 is invalid and will cause the AFCC to reject all offline transactions. This configuration implies that at least one online transaction has succeeded before SaF will run. The default value is 5.',
      },
      conditions: [
        {
          field: 'enableSaf',
          values: [true],
        },
      ],
    },
    minimumRequiredMegaBytes: {
      type: 'int',
      required: true,
      min: 1,
      defaultValue: 128,
      displayName: 'Minimum Required MegaBytes',
      tooltip: {
        text: 'The required size of storage needed to allow storing transactions in megabytes. The default value is 128. On average a SaF transaction takes up about 2.5kb of space.',
      },
      conditions: [
        {
          field: 'enableSaf',
          values: [true],
        },
      ],
    },
    forceOffline: {
      type: 'bool',
      required: true,
      defaultValue: true,
      displayName: 'Force Offline',
      tooltip: {
        text: 'When this option is true, after a forceOfflineTransactionCount number of offline approvals, AFCC will be forced into an offline state until a successful online replay. Default value is true.',
      },
      conditions: [
        {
          field: 'enableSaf',
          values: [true],
        },
      ],
    },
    forceOfflineTransactionCount: {
      type: 'int',
      required: true,
      displayName: 'Force Offline Transaction Count',
      min: 1,
      defaultValue: 3,
      tooltip: {
        text: 'In combination with forceOffline, this parameter controls how many offline approvals will trigger AFCC to go into an offline state. Default value is 3.',
      },
      conditions: [
        {
          field: 'enableSaf',
          values: [true],
        },
      ],
    },
    ebtOffline: {
      type: 'bool',
      displayName: 'EBT Offline',
      defaultValue: false,
      tooltip: {
        text: 'An EBT transaction will only be processed as offline if this option is true. The default value is false.',
      },
      conditions: [
        {
          field: 'enableSaf',
          values: [true],
        },
      ],
    },
    dmpActivationKey: {
      type: 'keyString',
      displayName: 'DMP Activation Key',
      tooltip: {
        text: 'The DMP activation key is used to activate the device in DMP.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const FreedomPayKioskStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.FreedomPayKioskStoredValue> =
  {
    _id: IntegrationSystem.FreedomPayKioskStoredValue,
    system: IntegrationSystem.FreedomPayKioskStoredValue,
    name: 'FreedomPay Terminal Stored Value',
    isBrandSpecific: false,
    iconUrl: 'https://static.bureau.getbite.com/images/icon-freedompay-kiosk-gift.png',
    searchTokens: ['freedompay-kiosk-sv', 'freedompay-terminal-sv'],
    searchField: null,
    type: 'stored-value',
    isGiftCard: true,
    requiredPosSystem: null,
    incompatiblePosSystems: [],
    requiredPaymentSystem: IntegrationSystem.FreedomPayKiosk,
    // Terminal-based SV: the card is read through the FreedomPay payment
    // terminal device. The direct-to-FreedomPay QR/scanner flow lives on
    // FreedomPayStoredValue (see below), not here.
    readMethods: [HardwareReadMethod.Terminal],
    fields: {},
  };

const FreedomPayStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.FreedomPayStoredValue> = {
  _id: IntegrationSystem.FreedomPayStoredValue,
  system: IntegrationSystem.FreedomPayStoredValue,
  name: 'FreedomPay Stored Value',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-freedompay-kiosk-gift.png',
  searchTokens: ['freedompay-sv', 'freedompay-stored-value'],
  searchField: null,
  type: 'stored-value',
  isGiftCard: true,
  supportsBarcode: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  // Web authentication uses the SSO URLs below. Vitrine accepts an FPQR from
  // a scanner or HID reader.
  readMethods: [HardwareReadMethod.Scanner, HardwareReadMethod.Hid],
  supportsStoreValueBalanceLookup: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    systemId: {
      type: 'keyString',
      displayName: 'System ID',
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'FreedomPay System ID for SSO token registration. Required for web login/token retrieval, but not for RFID/HID-only payment.',
      },
    },
    storeId: {
      type: 'keyString',
      displayName: 'Store ID',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Freeway Store ID. Required for balance inquiry, sale, and refund calls from QR scans or RFID/HID scans.',
      },
    },
    terminalId: {
      type: 'keyString',
      displayName: 'Terminal ID',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Freeway Terminal ID. Required for balance inquiry, sale, and refund calls from QR scans or RFID/HID scans.',
      },
    },
    esKey: {
      type: 'keyString',
      displayName: 'ES Key',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
      tooltip: {
        text: 'Freeway ES key. Required for balance inquiry, sale, and refund calls from QR scans or RFID/HID scans.',
      },
    },
    externalSsoAuthUrl: {
      type: 'keyString',
      displayName: 'Primary Auth URL',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'SSO authentication URL for the primary stored-value account login',
      },
    },
    allowMultipleAccountsForPrimaryAuthUrl: {
      type: 'bool',
      displayName: 'Allow Multiple Primary Accounts',
      defaultValue: false,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'When enabled, the primary stored-value account login remains available after an account has already been linked.',
      },
    },
    secondaryExternalSsoAuthUrl: {
      type: 'keyString',
      displayName: 'Secondary Auth URL',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'SSO authentication URL for the secondary stored-value account login',
      },
    },
    allowMultipleAccountsForSecondaryAuthUrl: {
      type: 'bool',
      displayName: 'Allow Multiple Secondary Accounts',
      defaultValue: false,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'When enabled, the secondary stored-value account login remains available after an account has already been linked.',
      },
    },
    qrServiceUrl: {
      type: 'keyString',
      displayName: 'QR Service Base URL (override)',
      tooltip: {
        text: 'Optional override for FreedomPay QR encrypt/decrypt service base URL. /api/v3/qrcode/store is appended. Defaults to https://incentives.uat.freedompay.com/qrcodeencryptdecrypt (sandbox) or https://incentives.freedompay.com/QrCodeEncryptDecrypt (prod). Response is text/plain — body is the FPQR string.',
      },
      writeRight: UserRight.ManageI9n,
    },
    identityServerUrl: {
      type: 'keyString',
      displayName: 'Identity Server URL (override)',
      tooltip: {
        text: 'Optional override for FreedomPay Identity Server base URL. Defaults to https://ida.uat.freedompay.com (sandbox) or https://ida.freedompay.com (prod). Token endpoint /connect/token is appended; client_credentials grant; tokens last 1 hour.',
      },
      writeRight: UserRight.ManageI9n,
    },
    qrClientId: {
      type: 'keyString',
      displayName: 'QR OAuth Client ID',
      tooltip: {
        text: 'OAuth 2.0 client ID issued by FreedomPay for QR Code API access. Required only when generating QR codes from saved stored-value accounts.',
      },
      writeRight: UserRight.ManageI9n,
    },
    qrClientSecret: {
      type: 'keyString',
      displayName: 'QR OAuth Client Secret',
      maskInput: true,
      tooltip: {
        text: 'OAuth 2.0 client secret issued by FreedomPay for QR Code API access. Required only when generating QR codes from saved stored-value accounts.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const PinataStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.PinataStoredValue> = {
  _id: IntegrationSystem.PinataStoredValue,
  system: IntegrationSystem.PinataStoredValue,
  name: 'Pinata Stored Value (Scanner)',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata-stored-value.png',
  searchTokens: ['pinata-stored-value'],
  searchField: null,
  type: 'stored-value',
  isGiftCard: true,
  validCardNumberLengths: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  validCardNumberInputRegex: '^[0-9a-zA-Z]$',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  validPinLengths: [3, 5, 6, 7, 8, 9, 10],
  readMethods: [
    HardwareReadMethod.Scanner,
    HardwareReadMethod.Msr,
    HardwareReadMethod.Hid,
    HardwareReadMethod.Manual,
  ],
  supportsStoreValueBalanceLookup: true,
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PinataApiServer>(PinataApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    requestDelay: {
      type: 'int',
      displayName: 'Request Delay (ms)',
      writeRight: UserRight.ManageI9n,
    },
    requiresPin: {
      type: 'bool',
      displayName: 'Show PIN Input on Gift Card view',
      writeRight: UserRight.ManageI9n,
    },
    hasBarcodes: {
      type: 'bool',
      displayName: 'Has Barcodes',
      tooltip: {
        text: 'Set to true if the gift cards have a scannable barcode.',
      },
      writeRight: UserRight.ManageI9n,
      defaultValue: true,
    },
  },
};

const ValutecSchema: I9nSchemaStoredValue<IntegrationSystem.Valutec> = {
  _id: IntegrationSystem.Valutec,
  system: IntegrationSystem.Valutec,
  name: 'Valutec',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-valutec.png',
  searchTokens: ['valutec'],
  searchField: 'clientKey',
  type: 'stored-value',
  isGiftCard: true,
  validCardNumberLengths: [15, 16, 19],
  validCardNumberInputRegex: '^[0-9]$',
  validPinLengths: [3, 6, 8],
  validPinInputRegex: '^[0-9]$',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  supportsBarcode: true,
  readMethods: [HardwareReadMethod.Scanner, HardwareReadMethod.Msr, HardwareReadMethod.Manual],
  fields: {
    clientKey: {
      type: 'keyString',
      displayName: 'Client Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    terminalId: {
      type: 'keyString',
      displayName: 'Terminal ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    requiresPin: {
      type: 'bool',
      displayName: 'Show PIN Input on Gift Card view',
      writeRight: UserRight.ManageI9n,
    },
  },
};

const PaytronixGiftSchema: I9nSchemaStoredValue<IntegrationSystem.PaytronixGift> = {
  _id: IntegrationSystem.PaytronixGift,
  system: IntegrationSystem.PaytronixGift,
  name: 'Paytronix Gift Card',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-paytronix-gift-card.png',
  searchTokens: ['paytronix-gift'],
  searchField: 'merchantID',
  type: 'stored-value',
  isGiftCard: true,
  supportsBarcode: true,
  validCardNumberLengths: [10, 11, 12, 14, 16, 19],
  validCardNumberInputRegex: '^[0-9]$',
  validPinLengths: [3, 4, 5, 6, 8],
  validPinInputRegex: '^[0-9]$',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: null,
  supportsStoreValueBalanceLookup: true,
  readMethods: [HardwareReadMethod.Scanner, HardwareReadMethod.Msr, HardwareReadMethod.Manual],
  fields: {
    instanceType: instanceTypeFieldSchema,
    authSource: {
      displayName: 'Authentication Source',
      type: 'stringEnum',
      enum: PaytronixGiftAuthSource,
      enumNameHelper: PaytronixGiftAuthSourceHelper.name,
      values: allEnumValues<PaytronixGiftAuthSource>(PaytronixGiftAuthSource),
      defaultValue: PaytronixGiftAuthSource.Direct,
      required: true,
      ui: 'dropdown',
      writeRight: UserRight.ManageI9n,
    },
    merchantID: {
      type: 'int',
      displayName: 'Paytronix Merchant ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    storeCode: {
      type: 'keyString',
      displayName: 'Paytronix Store Code',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'Paytronix provided store code to be used for integration.',
      },
      writeRight: UserRight.ManageI9n,
    },
    walletCode: {
      type: 'int',
      displayName: 'Paytronix Gift Card Wallet Code',
      tooltip: {
        text: 'Paytronix provided wallet identifier code. Default to 0.',
      },
      writeRight: UserRight.ManageI9n,
    },
    requiresPin: {
      type: 'bool',
      displayName: 'Show PIN Input on Gift Card view',
      writeRight: UserRight.ManageI9n,
      defaultValue: true,
    },
    hasBarcodes: {
      type: 'bool',
      displayName: 'Has Barcodes',
      tooltip: {
        text: 'Set to true if the gift cards have a scannable barcode.',
      },
      writeRight: UserRight.ManageI9n,
      defaultValue: true,
    },
    useManualEntryAsFallback: {
      type: 'bool',
      displayName: 'Use Manual Entry as Fallback',
      tooltip: {
        text: 'Set to true if manual entry should be allowed as a fallback.',
      },
      writeRight: UserRight.ManageI9n,
      defaultValue: false,
    },
    autoActivateCard: {
      type: 'bool',
      displayName: 'Auto Activate Card',
      defaultValue: true,
      tooltip: {
        text: 'Automatically activate the card when adding it to the check.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const PaytronixCompCardSchema: I9nSchemaCompCard<IntegrationSystem.PaytronixCompCard> = {
  _id: IntegrationSystem.PaytronixCompCard,
  system: IntegrationSystem.PaytronixCompCard,
  name: 'Paytronix Comp Card',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-paytronix-comp-card.png',
  searchTokens: ['paytronix-comp'],
  searchField: 'merchantID',
  type: 'comp-card',
  validCardNumberLengths: [16, 19],
  validCardNumberInputRegex: '^[0-9]$',
  validPinLengths: [3, 4, 5, 6, 8],
  validPinInputRegex: '^[0-9]$',
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.Olo],
  readMethods: [HardwareReadMethod.Msr, HardwareReadMethod.Manual],
  fields: {
    instanceType: instanceTypeFieldSchema,
    merchantID: {
      type: 'int',
      displayName: 'Paytronix Merchant ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    storeCode: {
      type: 'keyString',
      displayName: 'Paytronix Store Code',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'Paytronix provided store code to be used for integration.',
      },
      writeRight: UserRight.ManageI9n,
    },
    itemKey: {
      type: 'keyString',
      displayName: 'Item Key',
      tooltip: {
        text: 'The key used to tie an item on Paytronix to the POS. If barcode is selected, and none exists on the item, we fall back to the i9nId.',
      },
      required: true,
      writeRight: UserRight.ManageI9n,
      ui: 'dropdown',
      validationRegex: '^posId|i9nId|barcode$',
      defaultValue: 'i9nId',
    },
    checkNumberSource: {
      type: 'stringEnum',
      enum: PaytronixCheckNumberSource,
      enumNameHelper: PaytronixCheckNumberSourceHelper.name,
      ui: 'dropdown',
      displayName: 'Check Number Source',
      tooltip: {
        text: 'The source of the check number to send to Paytronix. If the POS Id is not available, we fall back to the Bite order id.',
      },
      required: true,
      writeRight: UserRight.ManageI9n,
      defaultValue: PaytronixCheckNumberSource.Bite,
    },
    requiresPin: {
      type: 'bool',
      displayName: 'Show PIN Input on Comp Card view',
      writeRight: UserRight.ManageI9n,
    },
    useMinorCategory: {
      type: 'bool',
      displayName: 'Use Minor Category',
      tooltip: {
        text: 'Use the minor category instead of the major category when sending items to Paytronix',
      },
    },
    autoActivateCard: {
      type: 'bool',
      displayName: 'Auto Activate Card',
      defaultValue: true,
      tooltip: {
        text: 'Automatically activate the card when adding it to the check.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const PinataCompCardSchema: I9nSchemaCompCard<IntegrationSystem.PinataCompCard> = {
  _id: IntegrationSystem.PinataCompCard,
  system: IntegrationSystem.PinataCompCard,
  name: 'Pinata Comp Card',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata.png',
  searchTokens: ['pinata'],
  searchField: 'apiServer',
  type: 'comp-card',
  validCardNumberLengths: [5, 19],
  validCardNumberInputRegex: '^[0-9a-zA-Z]$',
  validPinLengths: [3, 5, 6, 8],
  validPinInputRegex: '^[0-9]$',
  requiredPosSystem: IntegrationSystem.PinataPos,
  supportsBarcode: true,
  readMethods: [HardwareReadMethod.Scanner, HardwareReadMethod.Msr, HardwareReadMethod.Manual],
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PinataApiServer>(PinataApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    requiresPin: {
      type: 'bool',
      displayName: 'Show PIN Input on Comp Card view',
      writeRight: UserRight.ManageI9n,
    },
  },
};

const NcrRadiantSchema: I9nSchemaPos<IntegrationSystem.NcrRadiant> = {
  _id: IntegrationSystem.NcrRadiant,
  system: IntegrationSystem.NcrRadiant,
  name: 'NCR Radiant',
  isBrandSpecific: false,
  searchTokens: ['ncr-radiant'],
  searchField: 'enterpriseKey',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-ncr-radiant.png',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'RPOSOrderId',
  locationIdKey: 'enterpriseKey',
  syncsSections: true,
  syncsMenuStructures: false, // Radiant returns the sections in a random order from the db
  supportsCompoundMenuItems: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    sharedKey: {
      type: 'keyString',
      displayName: 'Shared Key',
      requiresValidationIfChanged: true,
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    secretKey: {
      type: 'keyString',
      displayName: 'Secret Key',
      requiresValidationIfChanged: true,
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    organizationKey: {
      type: 'keyString',
      displayName: 'Organization ID',
      requiresValidationIfChanged: true,
      required: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    enterpriseKey: {
      type: 'keyString',
      displayName: 'Enterprise Unit ID',
      requiresValidationIfChanged: true,
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    sectionsToSync: {
      type: 'keyString',
      displayName: 'Merchandise Category IDs to sync',
      tooltip: {
        text: 'Comma-separated merchandise section/category IDs',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const VickiSchema: I9nSchemaFulfillment<IntegrationSystem.Vicki> = {
  _id: IntegrationSystem.Vicki,
  system: IntegrationSystem.Vicki,
  name: 'Vicki',
  isBrandSpecific: false,
  searchTokens: ['vicki'],
  searchField: 'vickiAuthEmail',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-vicki.png',
  type: 'fulfillment',
  supportsOrderValidation: false,
  supportsThirdPartyOrderSending: false,
  fulfillmentCheckIdKey: 'vickiOrderId',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  canSync: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    vickiAuthEmail: {
      type: 'keyString',
      displayName: 'Vicki Auth Email',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    vickiAuthPassword: {
      type: 'keyString',
      displayName: 'Vicki Auth Password',
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
  },
};

const PaytronixLoyaltySchema: I9nSchemaLoyalty<IntegrationSystem.PaytronixLoyalty> = {
  _id: IntegrationSystem.PaytronixLoyalty,
  system: IntegrationSystem.PaytronixLoyalty,
  name: 'Paytronix Loyalty',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-paytronix.png',
  type: 'loyalty',
  searchTokens: ['paytronix'],
  searchField: 'merchantID',
  loyaltyCheckIdKey: 'pxTransactionId',
  supportsCompoundMenuItems: true,
  requiresDiscountId: true,
  couponsAndLoyaltyEarningCompatible: true,
  applyRewardSteps: ['loyalty', 'pos'],
  supportsOrderValidation: false,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.Olo, IntegrationSystem.SpotOn],
  canSync: true,
  supportedAuthMethods: [
    LoyaltyAuthMethod.Barcode,
    LoyaltyAuthMethod.PhoneNumber,
    LoyaltyAuthMethod.CardNumber,
  ],
  validCardNumberLengths: [16],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    merchantID: {
      type: 'int',
      displayName: 'Paytronix Merchant ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    storeCode: {
      type: 'keyString',
      displayName: 'Paytronix Store Code',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'Paytronix provided store code to be used for integration.',
      },
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    itemKey: {
      type: 'keyString',
      displayName: 'Item Key',
      tooltip: {
        text: 'The key used to tie an item on Paytronix to the POS. If barcode is selected, and none exists on the item, we fall back to the i9nId.',
      },
      required: true,
      writeRight: UserRight.ManageI9n,
      ui: 'dropdown',
      validationRegex: '^posId|i9nId|barcode$',
      defaultValue: 'i9nId',
    },
    checkNumberSource: {
      type: 'stringEnum',
      enum: PaytronixCheckNumberSource,
      enumNameHelper: PaytronixCheckNumberSourceHelper.name,
      ui: 'dropdown',
      displayName: 'Check Number Source',
      tooltip: {
        text: 'The source of the check number to send to Paytronix. If the POS Id is not available, we fall back to the Bite order id.',
      },
      required: true,
      writeRight: UserRight.ManageI9n,
      defaultValue: PaytronixCheckNumberSource.Bite,
    },
    sendOrderWithoutAuth: sendOrderWithoutAuthFieldSchema,
    showAllApplicableRewards: {
      type: 'bool',
      displayName: 'Show all applicable rewards',
      tooltip: {
        text: 'Show all applicable rewards on the POS, not just the first one.',
      },
    },
    useMinorCategory: {
      type: 'bool',
      displayName: 'Use Minor Category',
      tooltip: {
        text: 'Use the minor category instead of the major category when sending items to Paytronix',
      },
    },
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    overrideDistinguishingApplicableRewards: overrideDistinguishingApplicableRewardsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
    autoActivateCard: {
      type: 'bool',
      displayName: 'Auto Activate Card',
      defaultValue: true,
      tooltip: {
        text: 'Automatically activate the card when adding it to the check.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const NcrAlohaSchema: I9nSchemaPos<IntegrationSystem.NcrAloha> = {
  _id: IntegrationSystem.NcrAloha,
  system: IntegrationSystem.NcrAloha,
  name: 'NCR Aloha',
  isBrandSpecific: false,
  searchTokens: ['ncr-aloha'],
  searchField: 'enterpriseKey',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-ncr-aloha.png',
  type: 'pos',
  webhookIdKey: 'priceOptions.i9nId',
  posCheckIdKey: 'atoId',
  locationIdKey: 'enterpriseKey',
  manuallySetMultipleOfSameMod: true,
  supportsPosCoupons: true,
  syncsSections: true,
  syncsMenuStructures: true,
  syncsDiscounts: true,
  syncsOpenHoursTimetables: true,
  supportsCompoundMenuItems: true,
  supportsTips: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    sharedKey: {
      type: 'keyString',
      displayName: 'Shared Key',
      requiresValidationIfChanged: true,
      required: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    secretKey: {
      type: 'keyString',
      displayName: 'Secret Key',
      requiresValidationIfChanged: true,
      required: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    organizationKey: {
      type: 'keyString',
      displayName: 'Organization ID',
      requiresValidationIfChanged: true,
      required: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    enterpriseKey: {
      type: 'keyString',
      displayName: 'Enterprise Unit ID',
      requiresValidationIfChanged: true,
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    menuId: {
      type: 'keyString',
      displayName: 'Menu ID',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      displayName: 'POS Order Type',
      type: 'stringEnum',
      enum: NcrAlohaOrderMode,
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    prepaidTenderId: {
      type: 'keyString',
      displayName: 'Prepaid Tender ID',
      writeRight: UserRight.ManageI9nLite,
    },
    giftCardTenderId: {
      type: 'keyString',
      displayName: 'Gift Card Tender ID',
      writeRight: UserRight.ManageI9nLite,
    },
  },
};

const ElavonSchema: I9nSchemaKioskPayment<IntegrationSystem.Elavon> = {
  _id: IntegrationSystem.Elavon,
  system: IntegrationSystem.Elavon,
  name: 'Elavon',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-elavon.png',
  searchTokens: ['elavon'],
  searchField: 'locationName',
  type: 'kiosk-payment',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  apiPayment: null,
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  fields: {
    instanceType: instanceTypeFieldSchema,
    terminalId: {
      displayName: 'Terminal ID',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      // We've never had a second Elavon location so we don't know
      identifiesTheLocation: true,
    },
    locationName: {
      displayName: 'Location Name',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      // We've never had a second Elavon location so we don't know
      identifiesTheLocation: true,
    },
    chainCode: {
      displayName: 'Chain Code',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      // We've never had a second Elavon location so we don't know
      identifiesTheLocation: true,
    },
  },
};

const OtterSchema: I9nSchemaPos<IntegrationSystem.Otter> = {
  _id: IntegrationSystem.Otter,
  system: IntegrationSystem.Otter,
  name: 'Otter',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-otter.png',
  searchTokens: ['otter'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'otterExternalId',
  locationIdKey: 'locationID',
  usesManualTaxProfiles: true,
  syncsSections: false,
  syncsMenuStructures: false,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    locationID: {
      type: 'keyString',
      displayName: 'Location ID',
      requiresValidationIfChanged: true,
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
  },
};

const SprinklesSchema: I9nSchemaSupplementaryMenuSource<IntegrationSystem.Sprinkles> = {
  _id: IntegrationSystem.Sprinkles,
  system: IntegrationSystem.Sprinkles,
  name: 'Sprinkles Supplement',
  isBrandSpecific: true,
  searchTokens: ['sprinkles', 'sprinkles-supplement'],
  searchField: 'storeId',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-sprinkles.png',
  type: 'supplementary-menu-source',
  requiredPosSystem: IntegrationSystem.Olo,
  fields: {
    instanceType: instanceTypeFieldSchema,
    storeId: {
      type: 'keyString',
      displayName: 'Store ID',
      requiresValidationIfChanged: true,
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
  },
};

const SimphonySchema: I9nSchemaPos<IntegrationSystem.Simphony> = {
  _id: IntegrationSystem.Simphony,
  system: IntegrationSystem.Simphony,
  name: 'Simphony Gen 2 API',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-oracle-2.0.png',
  searchTokens: ['simphony'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'simphonyId',
  searchableFieldKeys: ['simphonyCheckRef'],
  manuallySetMultipleOfSameMod: true,
  supportsApplyingDiscounts: true,
  locationIdKey: 'locationID',
  supportsTips: true,
  supportsPayingWithCash: true,
  supportsOrderStatusPolling: false,
  supportsOrderStatusWebhooks: true,
  requiresManualDiscountManagement: true,
  supportsFutureOrders: true,
  supportsPosCoupons: false,
  couponsAndLoyaltyEarningCompatible: true,
  syncsSections: true,
  syncsMenuStructures: false, // Simphony's sections are embedded in menu items so there's no explicit order
  supportsCompoundMenuItems: true,
  syncsDiscounts: true,
  syncsCalories: true,
  syncsMacronutrients: true,
  syncsBadges: true,
  supportsConcurrentValidation: true,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    baseUrl: {
      required: true,
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'API URL',
      tooltip: {
        text: 'The API address for non authorization calls',
      },
      writeRight: UserRight.ManageI9n,
    },
    frontendUrl: {
      type: 'keyString',
      displayName: 'Offline URL',
      tooltip: {
        text: 'Including port i.e. "https://DESKTOP-KD61LIH:5443"',
      },
      writeRight: UserRight.ManageI9n,
    },
    authUrl: {
      required: true,
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'Authorization URL',
      tooltip: {
        text: 'The API address for authorization calls only',
      },
      writeRight: UserRight.ManageI9n,
    },
    clientId: {
      required: true,
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'Client ID',
      writeRight: UserRight.ManageI9n,
    },
    userId: {
      required: true,
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'User ID',
      writeRight: UserRight.ManageI9n,
    },
    userPassword: {
      required: true,
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'User Password',
      // This field has to be changed often so we need to allow admins to change it
      writeRight: UserRight.ManageI9nLite,
      maskInput: true,
    },
    organizationShortName: {
      required: true,
      requiresValidationIfChanged: true,
      type: 'keyString',
      displayName: 'Organization Short Name',
      tooltip: {
        text: 'Shortened name used to identify a Simphony organization',
      },
      writeRight: UserRight.ManageI9n,
    },
    locationID: {
      type: 'keyString',
      displayName: 'Location',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'Available after the first Sync. The specific restaurant.',
      },
      writeRight: UserRight.ManageI9n,
    },
    menuId: {
      type: 'keyString',
      displayName: 'Menu',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'Available after the first Sync. The menu to be synced',
      },
      writeRight: UserRight.ManageI9n,
    },
    revenueCenterId: {
      type: 'keyString',
      displayName: 'Revenue Center',
      ui: 'dropdown',
      isSetAfterSecondSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Available after the second Sync. Is the revenue center for the selected menu.',
      },
      writeRight: UserRight.ManageI9n,
    },
    simphonyEmployeeId: {
      requiredForOperations: true,
      type: 'int',
      displayName: 'Employee ID',
      tooltip: {
        text: 'The employee used to send checks when orders are created on the kiosk',
      },
      writeRight: UserRight.ManageI9n,
    },
    additionalRetriesOnSend: {
      type: 'int',
      displayName: 'Additional Retries on Send',
      tooltip: {
        text: 'Only retries 503 errors. Additional retries will be attempted when sending an order to Simphony, maximum of 5 retries.',
      },
      min: 0,
      max: 5,
      writeRight: UserRight.ManageI9n,
    },
    secondsBetweenRetries: {
      type: 'int',
      displayName: 'Timeout Between Retries (seconds)',
      tooltip: {
        text: 'Only retries 503 errors. The time to wait between retries when sending an order to Simphony, maximum of 10 seconds.',
      },
      min: 1,
      max: 10,
      writeRight: UserRight.ManageI9n,
    },
    posSideDuplicateChecking: {
      type: 'bool',
      displayName: 'Enable Simphony Side Duplicate Order Checking',
      tooltip: {
        text: 'Simphony v19.8.4 or later! Simphony will handle duplicate order checking instead of Bite. This skips Bite-side idempotency checks and sends the detect-duplicate-request header to Simphony.',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9nLite,
    },
    sendFutureOrdersAsap: {
      type: 'bool',
      displayName: 'Send Future Orders to POS Immediately',
      tooltip: {
        text: 'When enabled, future orders are sent to Simphony immediately with the scheduled pickup time. When disabled, Bite holds future orders and sends them near the pickup time.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    overrideItemNameWithConsumerName: {
      type: 'bool',
      displayName: 'Override Item Name with Consumer Name',
      writeRight: UserRight.ManageI9nLite,
    },
    overrideModGroupNameWithConsumerDescription: {
      type: 'bool',
      displayName: 'Override Mod Group Name with Consumer Description',
      writeRight: UserRight.ManageI9nLite,
    },
    overrideModNameWithConsumerName: {
      type: 'bool',
      displayName: 'Override Mod Name with Consumer Name',
      writeRight: UserRight.ManageI9nLite,
    },
    syncModsWithoutPriceOptions: {
      type: 'bool',
      displayName: 'Sync Mods without Price Options',
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'If enabled, mods without price options will be synced from the POS. They will be assigned a price of $0.00 and a price sequence of "0".',
      },
      defaultValue: true,
    },
    memoItemID: memoItemIDFieldSchema,
    simphonyWeightedFamilyGroupID: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Weighted Item Family Group',
      isNullable: true,
      tooltip: {
        text: 'Menu items in this group need to have two definitions, one with the base unit price and one with an open price. Weight is always lbs.',
      },
    },
    additionalPriceOptionsCondimentGroupId: {
      type: 'int',
      ui: 'dropdown',
      displayName: 'Condiment Group for Additional Price Options',
      isNullable: true,
      tooltip: {
        text: 'Condiments in this group that share a name with an existing synced condiment will be added as additional price options. This group will not be synced into Bite as its own modifier group.',
      },
    },
    sendGuestCount: {
      type: 'bool',
      displayName: 'Send Guest Count',
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'If enabled, the guest count will be sent to the Simphony kitchen. "1" will be send for every check.',
      },
      defaultValue: true,
    },
    checkNameField: {
      type: 'int',
      ui: 'dropdown',
      displayName: 'Check Name Field',
      tooltip: {
        text: 'The value chosen will dictate what we send as the check name to Simphony kitchen',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    sendPosDefaultModifiers: {
      type: 'bool',
      displayName: 'Always Send "POS Selected by Default" Mods',
      tooltip: {
        text: 'If enabled, modifiers will always be sent to the POS, even if they are selected by default',
      },
      defaultValue: true,
      writeRight: UserRight.ManageI9nLite,
    },
    calorieExtensionFieldName: {
      type: 'keyString',
      displayName: 'Calorie Extension Field Name',
      tooltip: {
        text: 'The name of the field on the menu item that contains the calorie count',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    condimentGroupIdsToSkip: {
      type: 'array',
      elementType: 'int',
      displayName: 'Condiment Group IDs to Skip',
      uniqueElements: true,
      tooltip: {
        text: 'Any condiment groups with this id will not be synced',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    applyDeselectedBehaviorTo: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Apply Deselected Behavior to',
      tooltip: {
        text: 'Should deselected items be sent with extra information for Bite, POS or either default marked',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    sendDeselectedDefaultModPriceSequence: {
      type: 'keyString',
      displayName: 'Send Deselected Mod With Price Sequence',
      tooltip: {
        text: 'If set default mod that are deselected will be sent. The value entered will be used for price sequence of the mod. For example, if the sequence is "4" and the default mod is "Cheese", then "No Cheese" will be sent to Simphony',
      },
      placeholder: 'Enter a definition',
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
      conditions: [
        {
          field: 'applyDeselectedBehaviorTo',
          values: [
            DefaultSelectionType.BiteOrPos,
            DefaultSelectionType.Pos,
            DefaultSelectionType.Bite,
          ],
        },
      ],
    },
    modIdToAddAboveDeselectedMods: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Mod to Add Above Deselected Mods',
      tooltip: {
        text: 'If set, this mod will be added to the order above each deselected default mod',
      },
      writeRight: UserRight.ManageI9nLite,
      isNullable: true,
      conditions: [
        {
          field: 'applyDeselectedBehaviorTo',
          values: [
            DefaultSelectionType.BiteOrPos,
            DefaultSelectionType.Pos,
            DefaultSelectionType.Bite,
          ],
        },
      ],
    },
    menuSectionSluNumbers: {
      type: 'array',
      elementType: 'int',
      elementValues: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      displayName: 'Menu Section SLUs',
      uniqueElements: true,
      tooltip: {
        text: 'SLUs (Screen Look Up) can be used in Simphony to define which menu section the item belongs to. Choose the specific SLUs so Bite can find the menu section information when sections are synced.',
      },
      conditions: [
        {
          field: 'syncSections',
          values: [true],
        },
      ],
      required: true,
    },
    exclusiveDefinitionPriceNumber: {
      type: 'int',
      ui: 'dropdown',
      displayName: 'Exclusive Definition Price Number',
      tooltip: {
        text: 'Only the price level selected will be sync for all item and mod definitions. This limits the number of price options Bite creates',
      },
      writeRight: UserRight.ManageI9nLite,
      isNullable: true,
      values: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    },
    tenderIdsThatSupportTips: {
      type: 'array',
      elementType: 'keyString',
      ui: 'table',
      displayName: 'Tenders that Accept Tips',
      tooltip: {
        text: 'Only add a tender here if the site has confirmed it can accept tips!\nSince Simphony does not tell us which tenders accept tips some need to be added here.\nSync again after adding a tender.',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9nLite,
    },
    mealExchangeGuestFacingItemID: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Meal Exchange Guest Facing Item',
      tooltip: {
        text: 'This is the item a guest will order for a meal exchange.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    mealExchangePosItemID: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Meal Exchange POS Item',
      tooltip: {
        text: 'This is the item that will be sent to the POS for a meal exchange.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    creditCardSurchargeServiceChargeID: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Credit Card Surcharge Service Charge',
      isSetAfterSecondSync: true,
      isNullable: true,
      tooltip: {
        text: 'An amount-type (open) service charge used to send credit card surcharges',
      },
      writeRight: UserRight.ManageI9nLite,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'int',
      required: true,
      displayName: 'Dining Option',
      isSetAfterSecondSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after second sync.',
      },
      ui: 'dropdown',
      writeRight: UserRight.ManageI9nLite,
    },
    taxExemptOrderTypeID: {
      type: 'int',
      displayName: 'Tax Exempt Order Type',
      isSetAfterSecondSync: true,
      tooltip: {
        text: 'The order type to be used for tax exempt orders. Set after second sync.',
      },
      ui: 'dropdown',
      writeRight: UserRight.ManageI9nLite,
    },
    orderChannelID: {
      type: 'int',
      displayName: 'Order Channel',
      isSetAfterSecondSync: true,
      tooltip: {
        text: 'Set after second sync.',
      },
      ui: 'dropdown',
      writeRight: UserRight.ManageI9nLite,
    },
    ...generateCardSpecificTenderTypeSchema({
      isSetAfterSecondSync: true,
      tooltipText: 'Set after second sync. Only active external tender types get synced.',
    }),
    cashTenderTypeID: {
      type: 'keyString',
      displayName: 'Tender for Cash',
      isSetAfterSecondSync: true,
      requiredForOperations: true,
      ui: 'dropdown',
      tooltip: {
        text: 'Available after the second Sync. A non payment tender is needed, such as Send or Print',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    fullyDiscountedOrderTenderTypeID: {
      type: 'keyString',
      displayName: 'Tender Type for Fully Discounted Orders',
      ui: 'dropdown',
      isSetAfterSecondSync: true,
      isNullable: true,
      tooltip: {
        text: 'Available after the second Sync. If set, this tender will be used instead of the Cash tender when the order total is $0 due to discounts.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    orderFieldsForInformationLines: {
      type: 'array',
      ui: 'table',
      elementType: 'stringEnum',
      elementEnum: SimphonyInformationLineField,
      displayName: 'Fields to Send as Information Lines',
      tooltip: {
        text: 'The fields selected here will be appended to the order as information lines. If null, everything will be sent.',
      },
      uniqueElements: true,
      writeRight: UserRight.ManageI9nLite,
      defaultValue: [
        SimphonyInformationLineField.GuestName,
        SimphonyInformationLineField.GuestPhoneNumber,
        SimphonyInformationLineField.GuestEmail,
        SimphonyInformationLineField.OutpostName,
        SimphonyInformationLineField.TableNumber,
      ],
    },
  },
};

const VerifonePointCloudSchema: I9nSchemaKioskPayment<IntegrationSystem.VerifonePointCloud> = {
  _id: IntegrationSystem.VerifonePointCloud,
  system: IntegrationSystem.VerifonePointCloud,
  name: 'Verifone Point Cloud',
  isBrandSpecific: false,
  searchTokens: ['verifone-point-cloud'],
  searchField: 'userId',
  type: 'kiosk-payment',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-verifone-cloud.png',
  apiPayment: {
    canActivateTerminals: false,
    canCancelPayment: false,
    canDeactivateTerminals: false,
    canFetchListOfTerminals: true,
    canFetchTerminalStatus: true,
    canFetchTerminalModel: false,
    canUpdateTerminalIdleText: false,
    canWakeUpTerminals: false,
  },
  canDisplayText: false,
  canSync: true,
  minTransactionAmount: 0,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    instanceType: instanceTypeFieldSchema,
    userId: {
      displayName: 'User ID',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      // We've never had a second Verifone Point Cloud location, so we don't know
      identifiesTheLocation: true,
      tooltip: {
        text: 'Should be the ID of the User who created the key.',
      },
      writeRight: UserRight.ManageI9n,
    },
    apiKey: {
      displayName: 'API Key',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      // We've never had a second Verifone Point Cloud location, so we don't know
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    locationID: {
      displayName: 'Site Entity ID',
      type: 'keyString',
      tooltip: {
        text: 'Sometime required for validation',
      },
      writeRight: UserRight.ManageI9n,
    },
    doCardAcquisition: {
      type: 'bool',
      displayName: 'Acquire Card Before Sale',
      tooltip: {
        text: 'This probably should remain off! If enabled, the guest card info will be acquired before doing a sale.',
        isWarning: true,
      },
      isReadOnly: true,
      writeRight: UserRight.ManageI9n,
    },
  },
};

const LoyaltyPlantSchema: I9nSchemaLoyalty<IntegrationSystem.LoyaltyPlant> = {
  _id: IntegrationSystem.LoyaltyPlant,
  system: IntegrationSystem.LoyaltyPlant,
  name: 'Loyalty Plant',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-loyaltyplant.png',
  type: 'loyalty',
  searchTokens: ['loyalty-plant'],
  loyaltyCheckIdKey: 'loyaltyPlantTransactionId',
  requiresDiscountId: true,
  applyRewardSteps: ['loyalty', 'pos'],
  supportsCompoundMenuItems: true,
  supportsOrderValidation: false,
  requiredPosSystem: null,
  supportsCoupons: false,
  incompatiblePosSystems: [IntegrationSystem.SpotOn],
  supportsRewardsNoOrder: false,
  canSync: false,
  supportedAuthMethods: [LoyaltyAuthMethod.Barcode, LoyaltyAuthMethod.PhoneNumber],
  searchField: 'establishmentId',
  supportsSignupInstantEnroll: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    establishmentId: {
      type: 'keyString',
      displayName: 'Establishment ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    authMethods: authMethodsFieldSchema,
    sendOrderWithoutAuth: sendOrderWithoutAuthFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const LoyaltyPlantEarnerSchema: I9nSchemaLoyaltyEarner<IntegrationSystem.LoyaltyPlantEarner> = {
  _id: IntegrationSystem.LoyaltyPlantEarner,
  system: IntegrationSystem.LoyaltyPlantEarner,
  name: 'Loyalty Plant Earner',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-loyaltyplant.png',
  type: 'loyalty-earner',
  searchTokens: ['loyalty-plant-earner'],
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.SpotOn],
  searchField: 'establishmentId',
  allowUserToLogIn: false,
  skipSendOrder: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    establishmentId: {
      type: 'keyString',
      displayName: 'Establishment ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
  },
};

const LunchboxSchema: I9nSchemaLoyalty<IntegrationSystem.Lunchbox> = {
  _id: IntegrationSystem.Lunchbox,
  system: IntegrationSystem.Lunchbox,
  name: 'Lunchbox',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-lunchbox.png',
  type: 'loyalty',
  searchTokens: ['lunchbox'],
  searchField: 'apiKey',
  loyaltyCheckIdKey: 'lunchboxTransactionId',
  requiresDiscountId: true,
  applyRewardSteps: ['loyalty', 'pos'],
  supportsCompoundMenuItems: true,
  supportsOrderValidation: false,
  requiredPosSystem: null,
  supportsCoupons: false,
  incompatiblePosSystems: [IntegrationSystem.SpotOn],
  supportsRewardsNoOrder: false,
  canSync: false,
  supportedAuthMethods: [LoyaltyAuthMethod.PhoneNumber, LoyaltyAuthMethod.Barcode],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    productionUrl: {
      type: 'keyString',
      displayName: 'Production URL',
      tooltip: {
        text: 'Required for Barcode Auth. The production URL for the Lunchbox API. This is used to fetch the customer loyalty information.',
        isWarning: true,
      },
      writeRight: UserRight.ManageI9n,
      requiresValidationIfChanged: true,
    },
    authMethods: authMethodsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const VerifonePointScaSchema: I9nSchemaKioskPayment<IntegrationSystem.VerifonePointSca> = {
  _id: IntegrationSystem.VerifonePointSca,
  system: IntegrationSystem.VerifonePointSca,
  name: 'Verifone Point SCA',
  isBrandSpecific: false,
  searchTokens: ['verifone-point-sca'],
  searchField: 'posappTerminalSerialNumber',
  type: 'kiosk-payment',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-verifone.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  apiPayment: null,
  canDisplayText: false, // TODO(sca): verify
  canSync: false,
  minTransactionAmount: 0,
  fields: {
    instanceType: instanceTypeFieldSchema,
    posappTerminalSerialNumber: {
      type: 'keyString',
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      displayName: 'POSAPP Terminal Serial Number',
      // Multiple times, I have received a serial number that breaks the validation. Some even
      // violate multiple conditions. It seems like sca does not validate the serial number at all.
      // Anything can be a serial number. If they don't validate it in their system and make this a
      // valid serial number, I should also remove the validation. Thus now validates to alphanumeric
      // and '_', '-' with length > 8.
      validationRegex: '^[a-zA-Z0-9_-]{9,}$',
      writeRight: UserRight.ManageI9n,
    },
    clientId: {
      type: 'keyString',
      displayName: 'Client ID',
      isReadOnly: true,
    },
    deviceKey: {
      type: 'keyString',
      displayName: 'Key',
      tooltip: {
        text: 'Key gets from the PAYWare Connect call. Once we have the key, please do not delete this integration as we have to reset manually to get a new key.',
        isWarning: true,
      },
      isReadOnly: true,
    },
  },
};

const ParPaySchema: I9nSchemaKioskPayment<IntegrationSystem.ParPay> = {
  _id: IntegrationSystem.ParPay,
  system: IntegrationSystem.ParPay,
  name: 'PAR Pay',
  isBrandSpecific: false,
  searchTokens: ['par-pay'],
  searchField: 'corpId',
  type: 'kiosk-payment',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-parpay.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  apiPayment: null,
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  requiresRefundsToBeProcessedOnNative: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    corpId: {
      type: 'keyString',
      required: true,
      displayName: 'Corp ID',
    },
    ...ebtFieldSchemas,
  },
};

const ParPayStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.ParPayStoredValue> = {
  _id: IntegrationSystem.ParPayStoredValue,
  system: IntegrationSystem.ParPayStoredValue,
  name: 'PAR Pay Stored Value',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-parpay-stored-value.png',
  searchTokens: ['par-pay-stored-value'],
  searchField: null,
  type: 'stored-value',
  isGiftCard: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: IntegrationSystem.ParPay,
  readMethods: [HardwareReadMethod.Terminal],
  fields: {},
};

const AurusPaySchema: I9nSchemaKioskPayment<IntegrationSystem.AurusPay> = {
  _id: IntegrationSystem.AurusPay,
  system: IntegrationSystem.AurusPay,
  name: 'Aurus Pay',
  isBrandSpecific: false,
  searchTokens: ['aurus-pay'],
  searchField: 'corpId',
  type: 'kiosk-payment',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-aurus.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  apiPayment: null,
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  requiresRefundsToBeProcessedOnNative: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    corpId: {
      type: 'keyString',
      required: true,
      displayName: 'Corp ID',
    },
    enableDcc: {
      type: 'bool',
      defaultValue: false,
      displayName: 'Enable DCC (Dynamic Currency Conversion)',
      tooltip: {
        text: 'If enabled, the pinpad will offer Dynamic Currency Conversion to the cardholder when an eligible foreign card is presented.',
      },
      writeRight: UserRight.ManageI9n,
    },
    ...ebtFieldSchemas,
  },
};

const AurusPayStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.AurusPayStoredValue> = {
  _id: IntegrationSystem.AurusPayStoredValue,
  system: IntegrationSystem.AurusPayStoredValue,
  name: 'Aurus Pay Stored Value',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-aurus-stored-value.png',
  searchTokens: ['aurus-pay-stored-value'],
  searchField: null,
  type: 'stored-value',
  isGiftCard: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: IntegrationSystem.AurusPay,
  readMethods: [HardwareReadMethod.Terminal],
  fields: {},
};

const ChaseSchema: I9nSchemaKioskPayment<IntegrationSystem.Chase> = {
  _id: IntegrationSystem.Chase,
  system: IntegrationSystem.Chase,
  name: 'Chase',
  isBrandSpecific: false,
  searchTokens: ['chase'],
  searchField: 'hwVendorId',
  type: 'kiosk-payment',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-chase.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  apiPayment: null,
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  requiresRefundsToBeProcessedOnNative: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    hwVendorId: {
      type: 'keyString',
      required: true,
      displayName: 'HW Vendor ID',
      tooltip: {
        text: 'The POS Vendor ID that is assigned for each Integration.',
      },
    },
    ...ebtFieldSchemas,
  },
};

const ChaseStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.ChaseStoredValue> = {
  _id: IntegrationSystem.ChaseStoredValue,
  system: IntegrationSystem.ChaseStoredValue,
  name: 'Chase Stored Value',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-chase-stored-value.png',
  searchTokens: ['chase-stored-value'],
  searchField: null,
  type: 'stored-value',
  isGiftCard: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: IntegrationSystem.Chase,
  readMethods: [HardwareReadMethod.Terminal],
  fields: {},
};

const PDISchema: I9nSchemaPos<IntegrationSystem.PDI> = {
  _id: IntegrationSystem.PDI,
  system: IntegrationSystem.PDI,
  name: 'PDI',
  isBrandSpecific: false,
  searchTokens: ['pdi'],
  searchField: 'siteID',
  type: 'pos',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pdi.png',

  syncsSupplementaryData: true,
  syncsSections: true,
  syncsMenuStructures: false, // PDI sections are embedded in items so there's no explicit order
  syncsItemTaxProfiles: true,
  usesManualTaxProfiles: true,

  webhookIdKey: 'i9nId',
  posCheckIdKey: 'pdiCheckId',
  locationIdKey: 'siteID',

  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,

  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    companyId: {
      type: 'keyString',
      required: true,
      displayName: 'Company ID',
    },
    siteID: {
      type: 'keyString',
      required: true,
      identifiesTheLocation: true,
      displayName: 'Site ID',
    },
  },
};

const LogicControlsSchema: I9nSchemaFulfillment<IntegrationSystem.LogicControls> = {
  _id: IntegrationSystem.LogicControls,
  system: IntegrationSystem.LogicControls,
  name: 'Logic Controls',
  isBrandSpecific: false,
  searchTokens: ['logic-controls'],
  searchField: 'logicControlsApiKey',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-logic-controls.png',
  type: 'fulfillment',
  supportsOrderValidation: false,
  supportsThirdPartyOrderSending: true,
  fulfillmentCheckIdKey: 'logicControlsOrderId',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  canSync: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    useDirectToKds: {
      type: 'bool',
      displayName: 'Use Direct to KDS (Non-On Prem)',
      tooltip: {
        text: 'If enabled, orders will be sent directly to the KDS from the Bite server instead of going through the Logic Controls API',
      },
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'logicControlsUseOnPremConnection',
          values: [false, undefined],
        },
      ],
    },
    logicControlsKdsIpAddress: {
      type: 'keyString',
      displayName: 'IP Address or Hostname',
      requiresValidationIfChanged: true,
      requiredForOperations: true,
      conditions: [
        {
          field: 'useDirectToKds',
          values: [true],
        },
      ],
      writeRight: UserRight.ManageI9n,
    },
    logicControlsKdsPort: {
      type: 'int',
      displayName: 'Port',
      requiresValidationIfChanged: true,
      requiredForOperations: true,
      identifiesTheLocation: true,
      conditions: [
        {
          field: 'useDirectToKds',
          values: [true],
        },
      ],
      writeRight: UserRight.ManageI9n,
    },
    logicControlsUseOnPremConnection: {
      type: 'bool',
      displayName: 'Use On Prem Direct to KDS',
      tooltip: {
        text: 'If enabled, orders will be sent directly to the KDS from the Kiosk Directly.',
      },
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'useDirectToKds',
          values: [false, undefined],
        },
      ],
    },
    logicControlsOnPremKdsIpAddress: {
      type: 'keyString',
      displayName: 'Local Network IP Address',
      requiresValidationIfChanged: true,
      requiredForOperations: true,
      conditions: [
        {
          field: 'logicControlsUseOnPremConnection',
          values: [true],
        },
      ],
      writeRight: UserRight.ManageI9n,
    },
    logicControlsOnPremKdsPort: {
      type: 'int',
      displayName: 'Port',
      requiresValidationIfChanged: true,
      requiredForOperations: true,
      conditions: [
        {
          field: 'logicControlsUseOnPremConnection',
          values: [true],
        },
      ],
      writeRight: UserRight.ManageI9n,
    },
    logicControlsApiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      requiresValidationIfChanged: true,
      requiredForOperations: true,
      tooltip: {
        text: 'API Key used to authorize with Logic Controls. Can be found under store --> marketplace --> api --> edit',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
      conditions: [
        {
          field: 'useDirectToKds',
          values: [false, undefined],
        },
        {
          field: 'logicControlsUseOnPremConnection',
          values: [false, undefined],
        },
      ],
      isNullable: true,
    },
    sendDefaultMods: {
      type: 'bool',
      displayName: 'Send Default Mods',
      tooltip: {
        text: 'If enabled, then mods selected by default will be sent to Logic Controls',
      },
      writeRight: UserRight.ManageI9nLite,
      defaultValue: true,
    },
    sendDeselectedDefaultModPrefix: {
      type: 'keyString',
      displayName: 'Send Deselected Prefixed Mod',
      tooltip: {
        text: 'If set default mods that are deselected will be sent. The value entered will be prefixed to the mod name. For example, if the prefix is "No" and the default mod is "Cheese", then "No Cheese" will be sent to Logic Controls',
      },
      placeholder: 'Currently Not Sending',
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
    },
  },
  diningOptionFields: {
    logicControlsStationID: {
      type: 'int',
      displayName: 'KDS Station ID',
      tooltip: {
        text: 'The ID of the KDS screen to send the order to. Can be found under store --> kds station --> id column',
      },
      writeRight: UserRight.ManageI9nLite,
      min: 0,
      defaultValue: 1,
    },
    logicControlsDestination: {
      type: 'keyString',
      displayName: 'KDS Destination',
      tooltip: {
        text: 'The destination of the KDS order. If not set the fulfillment method will be used.',
      },
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
    },
    logicControlsThirdPartyDestination: {
      type: 'keyString',
      displayName: 'KDS Destination (Third-Party)',
      tooltip: {
        text: 'The destination of the KDS order for third-party providers. If not set the KDS destination will be used.',
      },
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
    },
  },
};

const EigenSchema: I9nSchemaKioskPayment<IntegrationSystem.Eigen> = {
  _id: IntegrationSystem.Eigen,
  system: IntegrationSystem.Eigen,
  name: 'Eigen',
  isBrandSpecific: false,
  searchTokens: ['eigen'],
  searchField: 'eigenAppId',
  type: 'kiosk-payment',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-eigen.png',
  apiPayment: {
    canActivateTerminals: false,
    canCancelPayment: false,
    canDeactivateTerminals: false,
    canFetchListOfTerminals: false,
    canFetchTerminalStatus: true,
    canFetchTerminalModel: false,
    canUpdateTerminalIdleText: false,
    canWakeUpTerminals: false,
  },
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<EigenApiServer>(EigenApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    eigenAppId: {
      displayName: 'APP ID',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      tooltip: {
        text: 'This value is provided by Eigen.',
      },
      writeRight: UserRight.ManageI9n,
    },
    epaKey: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      displayName: 'EPA Key',
      tooltip: {
        text: 'The server-to-server authentication key used to validate the request',
      },
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    processor: {
      type: 'stringEnum',
      enum: PaymentProcessor,
      values: [PaymentProcessor.Eigen, PaymentProcessor.Shift4],
      displayName: 'Processor',
      ui: 'dropdown',
      required: true,
      defaultValue: PaymentProcessor.Eigen,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'The underlying processor this Eigen integration is pointed at. Shift4 (Eigen’s acquirer) does not support transaction lookup; choose Shift4 if this account has been repointed.',
      },
    },
  },
};

const EigenStoredValueClient: I9nSchemaStoredValue<IntegrationSystem.EigenStoredValue> = {
  _id: IntegrationSystem.EigenStoredValue,
  system: IntegrationSystem.EigenStoredValue,
  name: 'Eigen Stored Value',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-eigen.png',
  searchTokens: ['eigen', 'eigen-stored-value'],
  searchField: null,
  type: 'stored-value',
  isGiftCard: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  requiredPaymentSystem: IntegrationSystem.Eigen,
  usesApiPayment: true,
  readMethods: [HardwareReadMethod.Terminal],
  fields: {},
};

const AdyenSchema: I9nSchemaKioskPayment<IntegrationSystem.Adyen> = {
  _id: IntegrationSystem.Adyen,
  system: IntegrationSystem.Adyen,
  name: 'Adyen',
  isBrandSpecific: false,
  searchTokens: ['adyen'],
  searchField: 'apiKey',
  type: 'kiosk-payment',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-adyen.png',
  apiPayment: {
    canActivateTerminals: false,
    canCancelPayment: false,
    canDeactivateTerminals: false,
    canFetchListOfTerminals: true,
    canFetchTerminalStatus: true,
    canFetchTerminalModel: false,
    canUpdateTerminalIdleText: false,
    canWakeUpTerminals: false,
  },
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    instanceType: instanceTypeFieldSchema,
    apiKey: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      displayName: 'API Key',
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    companyAccount: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      displayName: 'Company Account',
      tooltip: {
        text: 'The code for the company account. This is available in Adyen',
      },
      writeRight: UserRight.ManageI9n,
    },
    merchantAccount: {
      displayName: 'Merchant Account',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      // We've never had a second Adyen location, so we don't know.
      identifiesTheLocation: true,
      tooltip: {
        text: 'The code for the merchant account related to the store. This is available in Adyen',
      },
      writeRight: UserRight.ManageI9n,
    },
    store: {
      displayName: 'Store',
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      // We've never had a second Adyen location, so we don't know.
      identifiesTheLocation: true,
      tooltip: {
        text: 'The store code of the store. This is available in Adyen',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
};

const SpotOnSchema: I9nSchemaPos<IntegrationSystem.SpotOn> = {
  _id: IntegrationSystem.SpotOn,
  system: IntegrationSystem.SpotOn,
  name: 'SpotOn',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-spoton.png',
  searchTokens: ['spot-on'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'spotOnOrderNumber',
  searchableFieldKeys: ['spotOnOrderId', 'spotOnCheckId', 'spotOnCheckNumber'],
  locationIdKey: 'locationID',
  supportsTips: true,
  syncsSections: true,
  syncsMenuStructures: false, // SpotOn returns sections in a dictionary so the order is random
  supportsCompoundMenuItems: true,
  supportsPosCoupons: true,
  supportsFutureOrders: true,
  syncsCalories: true,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    locationID: {
      requiredForOperations: true,
      type: 'keyString',
      displayName: 'Location ID',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'The specific restaurant ID. Available after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    menuIds: {
      type: 'array',
      elementType: 'keyString',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      displayName: 'Menus to Sync',
      ui: 'table',
      placeholder: 'Select a menu to sync',
      emptyListWarningHtml: 'Please select at least one menu to sync.',
      tooltip: {
        text: 'The specific menus to sync. Available after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    memoItemID: memoItemIDFieldSchema,
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'keyString',
      required: true,
      displayName: 'Dining Option',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Available after the first sync.',
      },
      ui: 'dropdown',
      writeRight: UserRight.ManageI9nLite,
    },
    tenderTypeID: {
      type: 'keyString',
      displayName: 'Tender Type',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'Set after first sync.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
  },
};

const PinataFulfillmentSchema: I9nSchemaFulfillment<IntegrationSystem.PinataFulfillment> = {
  _id: IntegrationSystem.PinataFulfillment,
  system: IntegrationSystem.PinataFulfillment,
  name: 'Pinata Fulfillment',
  isBrandSpecific: false,
  searchTokens: ['pinata-fulfillment'],
  searchField: null,
  fulfillmentCheckIdKey: 'pinataFulfillmentOrderId',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata-fulfillment.png',
  type: 'fulfillment',
  supportsOrderValidation: false,
  supportsThirdPartyOrderSending: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  canSync: false,
  fields: {
    apiServer: {
      type: 'int',
      displayName: 'API Server',
      ui: 'dropdown',
      values: allEnumValues<PinataApiServer>(PinataApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
  },
  diningOptionFields: {
    pinataFulfillmentStationID: {
      type: 'keyString',
      required: true,
      displayName: 'KDS Station ID',
      writeRight: UserRight.ManageI9nLite,
    },
  },
};

const IQTouchSchema: I9nSchemaPos<IntegrationSystem.IQTouch> = {
  _id: IntegrationSystem.IQTouch,
  system: IntegrationSystem.IQTouch,
  name: 'Panasonic - IQ Touch',
  isBrandSpecific: false,
  searchTokens: ['panasonic-iqtouch'],
  searchField: 'locationID',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-iq-touch.png',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'iqtouchOrderId',
  locationIdKey: 'locationID',
  syncsSections: true,
  syncsMenuStructures: true,
  supportsCompoundMenuItems: true,
  supportsFutureOrders: true,
  supportsTips: true,
  syncsCalories: true,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  syncsDiscounts: true,
  supportsApplyingDiscounts: true,
  manuallySetMultipleOfSameMod: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    apiKey: {
      type: 'keyString',
      required: true,
      requiresValidationIfChanged: true,
      displayName: 'API Key',
      writeRight: UserRight.ManageI9n,
      maskInput: true,
      tooltip: {
        text: 'The authentication key. Provided by Panasonic.',
      },
    },
    franchiseID: {
      required: true,
      type: 'keyString',
      displayName: 'Franchise ID',
      writeRight: UserRight.ManageI9n,
      maskInput: true,
      tooltip: {
        text: 'The specific franchise ID. Provided by Panasonic.',
      },
    },
    locationID: {
      requiredForOperations: true,
      type: 'keyString',
      displayName: 'Store',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'The specific restaurant ID. Available after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    menuIds: {
      type: 'array',
      elementType: 'keyString',
      displayName: 'Menus to Sync',
      ui: 'table',
      placeholder: 'Select a menu to sync',
      tooltip: {
        text: 'By default we will sync the whole menu. However if specific menu ids are set we will only sync these items',
      },
      writeRight: UserRight.ManageI9n,
    },
    memoItemID: memoItemIDFieldSchema,
    promptForDonationRoundUp: promptForDonationSchema,
    donationItemId: {
      type: 'keyString',
      displayName: 'Donation Roundup Item',
      ui: 'dropdown',
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'A $0 item with no modifiers, used for sending the donation amount to the POS. The ClearView side must accept the price we send for this item.',
      },
      isNullable: true,
      writeRight: UserRight.ManageI9nLite,
      conditions: [
        {
          field: 'promptForDonationRoundUp',
          values: [DonationRoundUpPromptType.Custom, DonationRoundUpPromptType.Predefined],
        },
      ],
      required: true,
    },
    syncSelectedByDefault: {
      type: 'bool',
      displayName: 'Sync default selected modifiers',
      tooltip: {
        text: 'If enabled, then default selected modifiers will be synced into Bite',
      },
      writeRight: UserRight.ManageI9n,
      defaultValue: true,
    },
    syncMinimumQuantity: {
      type: 'bool',
      displayName: 'Sync minimum quantity',
      tooltip: {
        text: 'If enabled, then minimum quantity will be synced into Bite. Otherwise it will be set to 0.',
      },
      writeRight: UserRight.ManageI9n,
      defaultValue: true,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'keyString',
      required: true,
      displayName: 'Destination Name',
      requiredForOperations: true,
      tooltip: {
        text: 'How you wish the destination to be displayed on the POS. Spaces are not accepted. (to-go, dine-in, etc.)',
      },
      writeRight: UserRight.ManageI9nLite,
      validationRegex: '^[^ ]+$',
    },
  },
};

const CheetahOloLoyaltySchema: I9nSchemaLoyalty<IntegrationSystem.CheetahOloLoyalty> = {
  _id: IntegrationSystem.CheetahOloLoyalty,
  system: IntegrationSystem.CheetahOloLoyalty,
  name: 'Cheetah+OLO',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-cheetah-olo.png',
  type: 'loyalty',
  requiredPosSystem: IntegrationSystem.Olo,
  applyRewardSteps: ['loyalty', 'pos'],
  searchTokens: ['cheetah'],
  searchField: null,
  loyaltyCheckIdKey: 'cheetahLoyaltyCheckId',
  requiresDiscountId: false,
  supportsOrderValidation: false,
  supportsCompoundMenuItems: false,
  supportsOrderHistoryLookUp: true,
  canSync: false,
  couponsAndLoyaltyEarningCompatible: true,
  supportedAuthMethods: [
    LoyaltyAuthMethod.Email,
    LoyaltyAuthMethod.PhoneNumber,
    LoyaltyAuthMethod.Barcode,
    LoyaltyAuthMethod.CardNumber,
  ],
  validCardNumberLengths: [16],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    authMethods: authMethodsFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const FreshKDSSchema: I9nSchemaFulfillment<IntegrationSystem.FreshKDS> = {
  _id: IntegrationSystem.FreshKDS,
  system: IntegrationSystem.FreshKDS,
  name: 'Fresh KDS',
  isBrandSpecific: false,
  canSync: true,
  searchTokens: ['fresh-kds'],
  searchField: 'locationID',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-fresh-kds.png',
  type: 'fulfillment',
  supportsOrderStatusWebhooks: true,
  supportsOrderValidation: false,
  supportsThirdPartyOrderSending: false,
  requiredPosSystem: null,
  fulfillmentCheckIdKey: 'freshKdsOrderId',
  supportsFutureOrders: true,
  incompatiblePosSystems: [],
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    freshKdsToken: {
      type: 'keyString',
      displayName: 'Fresh KDS Token',
      required: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
      tooltip: {
        text: 'The token used to authenticate with Fresh KDS',
      },
      conditions: [
        {
          field: 'freshKdsUseOnPremConnection',
          values: [false, undefined],
        },
      ],
    },
    locationID: {
      type: 'keyString',
      displayName: 'Location ID',
      ui: 'dropdown',
      writeRight: UserRight.ManageI9n,
      identifiesTheLocation: true,
      tooltip: {
        text: 'The location ID used to identify the restaurant',
      },
      conditions: [
        {
          field: 'freshKdsUseOnPremConnection',
          values: [false, undefined],
        },
      ],
    },
    freshKdsUseOnPremConnection: {
      type: 'bool',
      displayName: 'Use On Prem Direct to KDS',
      tooltip: {
        text: 'If enabled, orders will be sent directly to the KDS from the Kiosk Directly.',
      },
      writeRight: UserRight.ManageI9n,
    },
    freshKdsOnPremDevices: {
      type: 'array',
      elementType: 'object',
      ui: 'accordion',
      uiSubtype: 'FreshKdsOnPremDevices',
      placeholder: 'Add On Prem Device',
      displayName: 'On Prem Devices',
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'freshKdsUseOnPremConnection',
          values: [true],
        },
      ],
      fields: {
        deviceIpAddress: {
          type: 'keyString',
          displayName: 'Device IP Address',
          placeholder: 'Device IP Address',
          required: true,
          writeRight: UserRight.ManageI9nLite,
        },
        devicePort: {
          type: 'int',
          displayName: 'Device Port',
          required: true,
          writeRight: UserRight.ManageI9nLite,
          defaultValue: 9104,
        },
        deviceName: {
          type: 'keyString',
          displayName: 'Device Name',
          placeholder: 'Device Name',
          required: true,
          writeRight: UserRight.ManageI9nLite,
        },
        fulfillmentStationIds: {
          type: 'array',
          elementType: 'keyString',
          ui: 'table',
          displayName: 'Fulfillment Stations',
          placeholder: 'Select a Station',
          tooltip: {
            text: 'The stations where menu items will be sent to. Based on assignments from Menu Sections and Menu Items',
          },
        },
      },
    },
  },
  diningOptionFields: {
    kdsDeviceIds: {
      type: 'array',
      required: true,
      displayName: 'Fresh KDS Device IDs',
      elementType: 'keyString',
      ui: 'table',
      placeholder: 'Select KDS Device ID',
      writeRight: UserRight.ManageI9nLite,
    },
  },
  diningOptionConditions: [
    {
      field: 'freshKdsUseOnPremConnection',
      values: [false, undefined],
    },
  ],
};

const ThanxOloSchema: I9nSchemaLoyalty<IntegrationSystem.ThanxOlo> = {
  _id: IntegrationSystem.ThanxOlo,
  system: IntegrationSystem.ThanxOlo,
  name: 'Thanx+OLO',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-thanx-olo.png',
  searchTokens: ['thanx', 'thanx-olo'],
  searchField: 'merchantId',
  loyaltyCheckIdKey: 'TODO',
  type: 'loyalty',
  canSync: false,
  requiresDiscountId: false,
  supportsOrderValidation: false,
  skipSendOrder: true,
  requiredPosSystem: IntegrationSystem.Olo,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: false,
  supportsCompoundMenuItems: false,
  supportsOrderHistoryLookUp: true,
  applyRewardSteps: ['loyalty', 'pos'],
  couponsAndLoyaltyEarningCompatible: true,
  supportedAuthMethods: [
    LoyaltyAuthMethod.PhoneNumber,
    LoyaltyAuthMethod.Email,
    LoyaltyAuthMethod.EmailPhoneNumber,
  ],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    authMethods: authMethodsFieldSchema,
    merchantId: {
      type: 'keyString',
      displayName: 'Merchant ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const OloAccountsLoyaltySchema: I9nSchemaLoyalty<IntegrationSystem.OloAccountsLoyalty> = {
  _id: IntegrationSystem.OloAccountsLoyalty,
  system: IntegrationSystem.OloAccountsLoyalty,
  name: 'Olo Accounts Loyalty',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-olo-loyalty.png',
  searchTokens: ['olo', 'olo-accounts', 'otp', 'borderless'],
  searchField: 'oloAuthChannelId',
  // `skipSendOrder` means no order reaches loyalty, so there's no transaction id to key on.
  loyaltyCheckIdKey: null,
  type: 'loyalty',
  canSync: false,
  requiresDiscountId: false,
  supportsOrderValidation: false,
  skipSendOrder: true,
  // The OTP token feeds getOrCreateUser via the Olo Ordering client, so the location needs an
  // Olo POS i9n; `newClient` auto-wraps `OloLoyaltyClient` for loyalty schemas requiring Olo.
  requiredPosSystem: IntegrationSystem.Olo,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: false,
  supportsCompoundMenuItems: false,
  supportsOrderHistoryLookUp: true,
  applyRewardSteps: ['loyalty', 'pos'],
  couponsAndLoyaltyEarningCompatible: true,
  // OTP proves ownership of a texted phone, so only PhoneNumber applies.
  supportedAuthMethods: [LoyaltyAuthMethod.PhoneNumber],
  otpEligibleAuthMethods: [LoyaltyAuthMethod.PhoneNumber],
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    authMethods: authMethodsFieldSchema,
    oloAuthChannelId: oloAuthChannelIdFieldSchema,
    oloAuthApiUrlOverride: oloAuthApiUrlOverrideFieldSchema,
    oloAuthClientId: oloAuthClientIdFieldSchema,
    oloAuthClientSecret: oloAuthClientSecretFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const LovesRewardsSchema: I9nSchemaLoyalty<IntegrationSystem.LovesRewards> = {
  _id: IntegrationSystem.LovesRewards,
  system: IntegrationSystem.LovesRewards,
  name: 'Loves Rewards',
  isBrandSpecific: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-loves-loyalty.png',
  searchTokens: ['loves-rewards'],
  searchField: 'storeId',
  loyaltyCheckIdKey: 'transactionId',
  type: 'loyalty',
  canSync: false,
  sendOrderWithoutAuth: true,
  supportsOrderValidation: false,
  waitForSendOrderBeforePrint: true,
  applyRewardSteps: ['loyalty', 'pos'],
  requiresDiscountId: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.Olo, IntegrationSystem.SpotOn],
  supportedAuthMethods: [LoyaltyAuthMethod.Barcode, LoyaltyAuthMethod.WebSessionToken],
  supportsCompoundMenuItems: true,
  couponsAndLoyaltyEarningCompatible: true,
  supportsSignupInstantEnroll: false,
  createsTransactions: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    mlrSandboxServer: {
      type: 'stringEnum',
      displayName: 'Sandbox Api Server',
      ui: 'dropdown',
      enum: LovesSandboxApiServer,
      enumNameHelper: LovesSandboxApiServerHelper.name,
      values: allEnumValues<LovesSandboxApiServer>(LovesSandboxApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'instanceType',
          values: [IntegrationInstanceType.Sandbox],
        },
      ],
    },
    storeId: {
      displayName: 'Store ID',
      type: 'keyString',
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    shouldSendOrderForEarning: {
      type: 'keyString',
      displayName: 'Should Bite send orders to MLR for earning points?',
      ui: 'dropdown',
      required: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: "Some Love's venders, such as restaurants, do not want orders to turn MLR points.",
      },
      validationRegex: '^(yes|no)$',
    },
    authMethods: authMethodsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const QuSchema: I9nSchemaPos<IntegrationSystem.Qu> = {
  _id: IntegrationSystem.Qu,
  system: IntegrationSystem.Qu,
  name: 'Qu',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-qu.png',
  searchTokens: ['qu'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId', // TODO - confirm
  posCheckIdKey: 'quOrderNumber', // TODO Should this be the human readable number or the actuall ID?
  locationIdKey: 'locationID',
  searchableFieldKeys: ['quOrderNumber'],
  syncsSections: true,
  syncsMenuStructures: true,
  syncsDiscounts: true,
  supportsOrderCreation: true,
  supportsPosCoupons: true,
  supportsApplyingDiscounts: true,
  supportsCompoundMenuItems: true,
  supportsTips: true,
  supportsFutureOrders: true,
  syncsCalories: true,
  syncsMacronutrients: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    clientId: {
      type: 'keyString',
      displayName: 'Client ID',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
    },
    clientSecret: {
      type: 'keyString',
      displayName: 'Client Secret',
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    locationID: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Location',
      tooltip: {
        text: 'The specific restaurant location ID. Available after the first sync.',
      },
      requiredForOperations: true,
      isSetAfterFirstSync: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
    },
    orderChannelID: {
      type: 'keyString',
      displayName: 'Order Channel',
      ui: 'dropdown',
      tooltip: {
        text: 'Available after setting the location and syncing.',
      },
      requiredForOperations: true,
      isSetAfterSecondSync: true,
      writeRight: UserRight.ManageI9n,
    },
    orderTypeID: {
      type: 'keyString',
      displayName: 'Order Type',
      ui: 'dropdown',
      tooltip: {
        text: 'Available after setting the location and syncing.',
      },
      requiredForOperations: true,
      isSetAfterSecondSync: true,
      writeRight: UserRight.ManageI9n,
    },
  },
  diningOptionFields: {
    serviceChargeId: {
      type: 'keyString',
      displayName: 'Service Charge',
      ui: 'dropdown',
      tooltip: {
        text: 'Set after first sync. Optional. Will be applied to orders with only this dining option',
      },
      isNullable: true,
      isSetAfterSecondSync: true,
      writeRight: UserRight.ManageI9nLite,
    },
    serviceChargeAmount: {
      type: 'price',
      displayName: 'Service Charge Amount',
      writeRight: UserRight.ManageI9nLite,
      tooltip: {
        text: 'The amount of the service charge in dollars. Only works if the Toast service charge is open',
      },
    },
  },
};

const LrsTableTrackerSchema: I9nSchemaTableTracker<IntegrationSystem.LrsTableTracker> = {
  _id: IntegrationSystem.LrsTableTracker,
  system: IntegrationSystem.LrsTableTracker,
  name: 'LRS Table Tracker',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-lrs-table-tracker.png',
  searchTokens: ['lrs-table-tracker'],
  searchField: 'gatewaySerialNumber',
  type: 'table-tracker',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    gatewaySerialNumber: {
      type: 'keyString',
      displayName: 'Gateway Serial Number',
      placeholder: 'Gateway Serial Number',
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9nLite,
      validationRegex: '^[0-9A-F]{12}$',
      tooltip: {
        text: 'The serial number of the gateway, found on the silver label on the back of the device. All caps.',
      },
    },
    gatewayIpAddress: {
      type: 'keyString',
      displayName: 'Gateway IP Address',
      placeholder: 'Gateway IP Address',
      writeRight: UserRight.ManageI9nLite,
      identifiesTheLocation: true,
      validationRegex: ipAddressRegexString,
      tooltip: {
        text: 'The IP address of the gateway on the local network, found on the Devices tab of the Table Tracker iPad app. The kiosk can find the IP address automatically.',
      },
    },
    gatewayAccessToken: {
      displayName: 'Access Token',
      tooltip: {
        text: 'The access token of the gateway. The kiosk will acquire the access token automatically.',
      },
      type: 'keyString',
      writeRight: UserRight.BiteEng,
      maskInput: true,
    },
    skipIfCartOnlyContainsCategories: skipIfCartOnlyContainsCategoriesFieldSchema,
  },
};

const PinataTableTrackerSchema: I9nSchemaTableTracker<IntegrationSystem.PinataTableTracker> = {
  _id: IntegrationSystem.PinataTableTracker,
  system: IntegrationSystem.PinataTableTracker,
  name: 'Pinata Table Tracker',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata-table-tracker.png',
  searchTokens: ['pinata-table-tracker'],
  searchField: 'sectionNumber',
  type: 'table-tracker',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    pinataTableTrackerResult: {
      type: 'stringEnum',
      enum: PinataTableTrackerResult,
      displayName: 'Table Tracker Result',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    pinataTableTrackerRequestType: {
      type: 'stringEnum',
      enum: PinataTableTrackerRequestType,
      displayName: 'Table Tracker Request Type',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    sectionNumber: {
      type: 'int',
      displayName: 'Section Number',
      required: true,
      writeRight: UserRight.ManageI9n,
      min: 1,
      max: 99,
    },
    skipIfCartOnlyContainsCategories: skipIfCartOnlyContainsCategoriesFieldSchema,
    shouldSkipTableCustomerIdentifier: shouldSkipTableCustomerIdentifierFieldSchema,
    minimumTableNumber: minimumTableNumberSchema,
    maximumTableNumber: maximumTableNumberSchema,
  },
};

const JtechSchema: I9nSchemaTableTracker<IntegrationSystem.Jtech> = {
  _id: IntegrationSystem.Jtech,
  system: IntegrationSystem.Jtech,
  name: 'Jtech',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-jtech.png',
  searchTokens: ['jtech'],
  searchField: 'sectionNumber',
  type: 'table-tracker',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  shouldSkipTableCustomerIdentifier: true,
  fields: {
    sectionNumber: {
      type: 'int',
      displayName: 'Section Number',
      required: true,
      writeRight: UserRight.ManageI9n,
      min: 1,
      max: 99,
    },
    volume: {
      type: 'int',
      displayName: 'Volume',
      required: true,
      writeRight: UserRight.ManageI9n,
      min: 0,
      max: 7,
      tooltip: {
        text: 'The volume of the table tracker. 0 to disable the voice prompt. 1 to 7 volume levels.',
      },
    },
    skipIfCartOnlyContainsCategories: skipIfCartOnlyContainsCategoriesFieldSchema,
    minimumTableNumber: minimumTableNumberSchema,
    maximumTableNumber: maximumTableNumberSchema,
  },
};

const ThanxSchema: I9nSchemaLoyalty<IntegrationSystem.Thanx> = {
  _id: IntegrationSystem.Thanx,
  system: IntegrationSystem.Thanx,
  name: 'Thanx',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-thanx.png',
  searchTokens: ['thanx'],
  searchField: 'merchantId',
  loyaltyCheckIdKey: 'thanxBasketId',
  type: 'loyalty',
  canSync: true,
  requiresDiscountId: true,
  supportsOrderValidation: false,
  skipSendOrder: false,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: false,
  supportsCompoundMenuItems: true,
  requiredPosSystem: null,
  applyRewardSteps: ['loyalty', 'pos'],
  incompatiblePosSystems: [IntegrationSystem.Olo],
  supportedAuthMethods: [
    LoyaltyAuthMethod.PhoneNumber,
    LoyaltyAuthMethod.Email,
    LoyaltyAuthMethod.EmailPhoneNumber,
  ],
  createsTransactions: true,
  supportsSignupInstantEnroll: false,

  fields: {
    instanceType: instanceTypeFieldSchema,
    merchantId: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Merchant ID',
      requiredForOperations: true,
      isSetAfterFirstSync: true,
      writeRight: UserRight.ManageI9n,
    },
    thanxLocationID: {
      type: 'keyString',
      ui: 'dropdown',
      displayName: 'Location ID',
      requiredForOperations: true,
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'Choose a merchant ID and sync the integration for a list of locations assigned to the merchant',
      },
      writeRight: UserRight.ManageI9n,
    },
    merchantKey: {
      type: 'keyString',
      displayName: 'Merchant Key',
      requiredForOperations: true,
      tooltip: {
        text: 'Only required for sending orders. The integration can be synced without the merchant key.',
      },
      writeRight: UserRight.ManageI9n,
    },
    oneRewardPerItem: {
      type: 'bool',
      displayName: 'One Reward Per Item',
      tooltip: {
        text: 'If enable we will make a reward for each applicable item',
      },
      defaultValue: true,
      writeRight: UserRight.ManageI9nLite,
    },
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    authMethods: authMethodsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const DeliverectSchema: I9nSchemaPos<IntegrationSystem.Deliverect> = {
  _id: IntegrationSystem.Deliverect,
  system: IntegrationSystem.Deliverect,
  name: 'OLD Deliverect',
  isBrandSpecific: false,
  // isDeprecated: true, // TODO - deprecate once all current customers are migrated to Deliverect Commerce
  iconUrl: 'https://static.bureau.getbite.com/images/icon-deliverect.png',
  searchTokens: ['deliverect'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'deliverectOrderID',
  locationIdKey: 'orderChannelID',
  searchableFieldKeys: ['deliverectOrderID'],
  supportsTips: true,
  syncsSections: true,
  syncsMenuStructures: true,
  supportsCompoundMenuItems: true,
  supportsPosCoupons: true,
  supportsFutureOrders: true,
  usesManualTaxProfiles: true,
  supportsApplyingDiscounts: true,
  supportsPayingWithCash: true,
  syncsOpenHoursTimetables: true,
  supportsOrderStatusWebhooks: true,
  syncsMultipleOfSameMod: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    accountID: {
      type: 'keyString',
      displayName: 'Account ID',
      ui: 'dropdown',
      requiredForOperations: true,
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'The Deliverect account. Available after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    locationID: {
      type: 'keyString',
      displayName: 'Location ID',
      ui: 'dropdown',
      isSetAfterSecondSync: true,
      requiredForOperations: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'The Deliverect location. Available after the second sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    orderChannelID: {
      type: 'keyString',
      displayName: 'Order Channel',
      ui: 'dropdown',
      isSetAfterThirdSync: true,
      requiredForOperations: true,
      tooltip: {
        text: 'The specific order channel for Bite. Available after the third sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    taxClassID: {
      type: 'int',
      displayName: 'Tax Class ID',
      tooltip: {
        text: 'Sent with taxes to Deliverect when creating an order.',
      },
      requiredForOperations: true,
      writeRight: UserRight.ManageI9n,
    },
  },
  diningOptionFields: {
    orderTypeID: {
      type: 'stringEnum',
      displayName: 'Order Type',
      isNullable: true,
      enum: DeliverectOrderType,
      enumNameHelper: DeliverectOrderTypeHelper.name,
      tooltip: {
        text: 'Override the order type sent to Deliverect for this dining option. If not set, the order type is determined automatically.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
    bagFee: {
      type: 'price',
      displayName: 'Bag Fee',
      tooltip: {
        text: 'The bag fee amount in dollars. Will be applied to orders with only this dining option.',
      },
      writeRight: UserRight.ManageI9nLite,
    },
  },
};

const DeliverectCommerceSchema: I9nSchemaPos<IntegrationSystem.DeliverectCommerce> = {
  _id: IntegrationSystem.DeliverectCommerce,
  system: IntegrationSystem.DeliverectCommerce,
  name: 'Deliverect Commerce',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-deliverect.png',
  searchTokens: ['deliverect', 'commerce'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'deliverectOrderID',
  locationIdKey: 'locationID',
  searchableFieldKeys: ['deliverectOrderID'],
  supportsTips: true,
  syncsSections: true,
  syncsMenuStructures: true,
  supportsCompoundMenuItems: true,
  supportsFutureOrders: true,
  supportsApplyingDiscounts: true,
  supportsPayingWithCash: true,
  syncsOpenHoursTimetables: true,
  syncsMultipleOfSameMod: true,
  syncsCalories: false,
  syncsMacronutrients: false,
  supportsConcurrentValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    accountID: {
      type: 'keyString',
      displayName: 'Account ID',
      ui: 'dropdown',
      requiredForOperations: true,
      isSetAfterFirstSync: true,
      tooltip: {
        text: 'The Deliverect account. Available after the first sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
    locationID: {
      type: 'keyString',
      displayName: 'Store',
      ui: 'dropdown',
      isSetAfterSecondSync: true,
      requiredForOperations: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'The Deliverect Commerce store. Available after the second sync.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
  diningOptionFields: {},
};

const AppfrontSchema: I9nSchemaLoyalty<IntegrationSystem.Appfront> = {
  _id: IntegrationSystem.Appfront,
  system: IntegrationSystem.Appfront,
  name: 'Appfront',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-appfront.png',
  searchTokens: ['appfront'],
  searchField: 'externalId',
  loyaltyCheckIdKey: 'appfrontPurchaseEventId',
  type: 'loyalty',
  canSync: false,
  requiresDiscountId: true,
  supportsOrderValidation: false,
  skipSendOrder: false,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: true,
  supportsCompoundMenuItems: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  applyRewardSteps: ['loyalty', 'pos'],
  supportedAuthMethods: [
    LoyaltyAuthMethod.Barcode,
    LoyaltyAuthMethod.PhoneNumber,
    LoyaltyAuthMethod.Email,
  ],
  supportsSignupInstantEnroll: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    authToken: {
      type: 'keyString',
      displayName: 'Authorization Token',
      required: true,
      maskInput: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'The pre-shared secret sent in the Authorization header on every Appfront request.',
      },
    },
    externalId: {
      type: 'keyString',
      displayName: 'External ID',
      required: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'The per-location identifier sent in the External-ID header on every Appfront request.',
      },
    },
    authMethods: authMethodsFieldSchema,
    enablePaymentReward: {
      type: 'bool',
      displayName: 'Enable Payment Reward',
      defaultValue: false,
      tooltip: {
        isWarning: true,
        text: 'Lets guests pay with loyalty balance at checkout.',
      },
    },
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
  genericFieldDefaultValueByFieldName: {
    enableMultipleRewardRedemption: false,
  },
};

const SmoothCommerceSchema: I9nSchemaLoyalty<IntegrationSystem.SmoothCommerce> = {
  _id: IntegrationSystem.SmoothCommerce,
  system: IntegrationSystem.SmoothCommerce,
  name: 'Smooth Commerce',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-smooth-commerce.png',
  searchTokens: ['smooth-commerce'],
  searchField: 'locationID',
  loyaltyCheckIdKey: 'smoothOrderId',
  type: 'loyalty',
  canSync: false,
  requiresDiscountId: true,
  supportsOrderValidation: false,
  skipSendOrder: false,
  supportsRewardsNoOrder: true,
  supportsDistinguishingApplicableRewards: false,
  supportsCompoundMenuItems: true,
  requiredPosSystem: null,
  incompatiblePosSystems: [IntegrationSystem.SpotOn],
  applyRewardSteps: ['loyalty', 'pos'],
  supportedAuthMethods: [LoyaltyAuthMethod.EmailPhoneNumber, LoyaltyAuthMethod.Barcode],
  createsTransactions: true,
  loyaltyNeedsSignOutAfterPartialPayment: true,
  supportsSignupInstantEnroll: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    locationID: {
      type: 'keyString',
      displayName: 'Store ID',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    customSubdomain: {
      type: 'keyString',
      displayName: 'Custom Subdomain',
      isNullable: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'The Smooth Commerce subdomain for the org. For example, the subdomain in "https://api.sample.smoothpay.com" would be "sample". Not all orgs have a custom subdomain',
      },
      conditions: [
        {
          field: 'instanceType',
          values: [IntegrationInstanceType.Prod],
        },
      ],
    },
    authMethods: authMethodsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
    useLoyaltyForPayments: {
      type: 'bool',
      displayName: 'Use loyalty for payments',
      defaultValue: true,
      tooltip: {
        isWarning: true,
        text: "Lets guests pay with loyalty balance at checkout. Only disable this if the org's Smooth Commerce integration is not configured to accept loyalty as a payment method.",
      },
    },
  },
};

const SpendgoSchema: I9nSchemaLoyalty<IntegrationSystem.Spendgo> = {
  _id: IntegrationSystem.Spendgo,
  system: IntegrationSystem.Spendgo,
  name: 'Spendgo',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-spendgo.png',
  searchTokens: ['spendgo'],
  searchField: 'apiKey',
  loyaltyCheckIdKey: 'spendgoOrderId',
  type: 'loyalty',
  canSync: false,
  requiresDiscountId: true,
  supportsOrderValidation: false,
  skipSendOrder: false,
  requiredPosSystem: null,
  applyRewardSteps: ['loyalty', 'pos'],
  supportsCompoundMenuItems: true,
  supportedAuthMethods: [LoyaltyAuthMethod.PhoneNumber],
  incompatiblePosSystems: [IntegrationSystem.SpotOn],
  createsTransactions: true,
  supportsSignupInstantEnroll: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    apiKey: {
      type: 'keyString',
      displayName: 'API Key',
      required: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    apiSecret: {
      type: 'keyString',
      displayName: 'API Secret',
      required: true,
      writeRight: UserRight.ManageI9n,
      maskInput: true,
    },
    spendgoAccountID: {
      type: 'keyString',
      displayName: 'Account (Brand) ID',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    storeCode: {
      type: 'keyString',
      displayName: 'Store Code',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    authMethods: authMethodsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

// Phone-number auth only. Olo resolves the guest against Spendgo through
// `/users/getorcreatebyphonenumber`, so Bite never calls the Spendgo API.
const SpendgoOloSchema: I9nSchemaLoyalty<IntegrationSystem.SpendgoOlo> = {
  _id: IntegrationSystem.SpendgoOlo,
  system: IntegrationSystem.SpendgoOlo,
  name: 'Spendgo+OLO',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-spendgo-olo.png',
  searchTokens: ['spendgo', 'spendgo-olo'],
  searchField: 'oloProviderName',
  type: 'loyalty',
  requiredPosSystem: IntegrationSystem.Olo,
  applyRewardSteps: ['loyalty', 'pos'],
  // Olo accrues on submit, so we never send the order to Spendgo and never get a check id.
  loyaltyCheckIdKey: null,
  skipSendOrder: true,
  canSync: false,
  requiresDiscountId: false,
  supportsOrderValidation: false,
  supportsCompoundMenuItems: false,
  supportsRewardsNoOrder: true,
  supportsOrderHistoryLookUp: true,
  couponsAndLoyaltyEarningCompatible: true,
  supportsSignupInstantEnroll: false,
  supportedAuthMethods: [LoyaltyAuthMethod.PhoneNumber],

  fields: {
    instanceType: instanceTypeFieldSchema,
    oloProviderName: {
      type: 'keyString',
      displayName: 'Olo Provider Name',
      required: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: "Brand prefix Olo assigned for this customer's Spendgo provider, e.g. 'mamounsfalafel'. Bite appends '-spendgo-phone-number'. Ask the Olo CSM if getorcreate returns HTTP 204.",
      },
    },
    authMethods: authMethodsFieldSchema,
    lookupOrderHistoryOnLoyalty: lookupOrderHistoryOnLoyaltySchema,
    groupRewardsByType: groupRewardsByTypeFieldSchema,
    overrideDistinguishingApplicableRewards: overrideDistinguishingApplicableRewardsFieldSchema,
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
  },
};

const MonerisSchema: I9nSchemaKioskPayment<IntegrationSystem.Moneris> = {
  _id: IntegrationSystem.Moneris,
  system: IntegrationSystem.Moneris,
  name: 'Moneris',
  isBrandSpecific: false,
  searchTokens: ['moneris'],
  searchField: 'merchantId',
  type: 'kiosk-payment',
  iconUrl: 'https://static.bureau.getbite.com/images/icon-moneris.png',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  apiPayment: null,
  canDisplayText: false,
  canSync: false,
  minTransactionAmount: 0,
  requiresRefundsToBeProcessedOnNative: true,
  fields: {
    configCode: {
      type: 'keyString',
      required: true,
      displayName: 'Config Code',
    },
    merchantId: {
      type: 'keyString',
      required: true,
      displayName: 'Merchant ID',
    },
  },
};

const StarbucksPayAndEarnLoyaltySchema: I9nSchemaLoyalty<IntegrationSystem.StarbucksPayAndEarnLoyalty> =
  {
    _id: IntegrationSystem.StarbucksPayAndEarnLoyalty,
    system: IntegrationSystem.StarbucksPayAndEarnLoyalty,
    name: 'Starbucks Pay and Earn Loyalty',
    isBrandSpecific: true,
    iconUrl: 'https://static.bureau.getbite.com/images/icon-starbucks-loyalty.png',
    searchTokens: ['starbucks'],
    searchField: null,
    loyaltyCheckIdKey: null,
    type: 'loyalty',
    applyRewardSteps: ['loyalty', 'pos'],
    requiredPosSystem: IntegrationSystem.Olo,
    requiresDiscountId: false,
    skipSendOrder: true,
    canSync: false,
    supportsOrderValidation: false,
    createsTransactions: true,
    supportsCompoundMenuItems: true,
    supportsOrderHistoryLookUp: false,
    supportedAuthMethods: [LoyaltyAuthMethod.Barcode, LoyaltyAuthMethod.CardNumber],
    validCardNumberLengths: [23, 16],
    alwaysUsedAsPayment: true,
    supportsSignupInstantEnroll: false,
    fields: {
      instanceType: instanceTypeFieldSchema,
      authMethods: authMethodsFieldSchema,
      storeMerchantId: {
        type: 'keyString',
        displayName: 'Store ID',
        required: true,
        writeRight: UserRight.ManageI9n,
        tooltip: {
          text: 'Identifies the store where the transactions will take place.',
        },
      },
      terminalId: {
        type: 'keyString',
        displayName: 'Terminal ID',
        required: true,
        writeRight: UserRight.ManageI9n,
        tooltip: {
          text: 'Identifies the register used in the transactions.',
        },
      },
      performLuhnCheckOnCustomerId: {
        displayName: 'Perform Luhn Check on Customer ID',
        type: 'bool',
        writeRight: UserRight.ManageI9n,
      },
      hideStatusAndPoints: hideStatusAndPointsFieldSchema,
      redeemTip: {
        type: 'bool',
        displayName: 'Redeem Tips',
        tooltip: {
          text: 'If enabled, tips will be taken from a loyalty card if the entire order was paid with the loyalty card.',
        },
        writeRight: UserRight.ManageI9n,
      },
    },
  };

const StarbucksFullLoyaltySchema: I9nSchemaLoyalty<IntegrationSystem.StarbucksFullLoyalty> = {
  _id: IntegrationSystem.StarbucksFullLoyalty,
  system: IntegrationSystem.StarbucksFullLoyalty,
  name: 'Starbucks Full Loyalty',
  isBrandSpecific: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-starbucks-loyalty.png',
  searchTokens: ['starbucks'],
  searchField: null,
  loyaltyCheckIdKey: null,
  type: 'loyalty',
  applyRewardSteps: ['loyalty', 'pos'],
  requiredPosSystem: IntegrationSystem.StarbucksPos,
  requiresDiscountId: true, // TODO: we don't know if this is accurate
  skipSendOrder: true,
  canSync: false,
  supportsOrderValidation: false,
  createsTransactions: true,
  skipRedundantRewardFetch: true,
  supportsCompoundMenuItems: true,
  supportsOrderHistoryLookUp: false,
  supportedAuthMethods: [
    LoyaltyAuthMethod.Barcode,
    // The implementation actually supports card number auth, but we do not want it enabled in prod.
    // To re-enable, restore LoyaltyAuthMethod.CardNumber above and validCardNumberLengths below.
    // LoyaltyAuthMethod.CardNumber,
  ],
  // validCardNumberLengths: [23, 16],
  supportsSignupInstantEnroll: false,
  fields: {
    instanceType: instanceTypeFieldSchema,
    authMethods: authMethodsFieldSchema,
    storeMerchantId: {
      type: 'keyString',
      displayName: 'Store ID',
      required: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Identifies the store where the transactions will take place.',
      },
    },
    starbucksMerchantId: {
      type: 'stringEnum',
      enum: StarbucksMerchantId,
      displayName: 'Merchant',
      required: true,
      defaultValue: StarbucksMerchantId.Starbucks,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Selects which merchant ID to use for Starbucks Loyalty card calls.',
      },
    },
    terminalId: {
      type: 'keyString',
      displayName: 'Terminal ID',
      required: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Identifies the register used in the transactions.',
      },
    },
    hideStatusAndPoints: hideStatusAndPointsFieldSchema,
    redeemTip: {
      type: 'bool',
      displayName: 'Redeem Tips',
      tooltip: {
        text: 'If enabled, tips will be taken from a loyalty card if the entire order was paid with the loyalty card.',
      },
      writeRight: UserRight.ManageI9n,
    },
  },
  genericFieldDefaultValueByFieldName: {
    enableMultipleRewardRedemption: false,
  },
};

const StarbucksStoredValueSchema: I9nSchemaStoredValue<IntegrationSystem.StarbucksStoredValue> = {
  _id: IntegrationSystem.StarbucksStoredValue,
  system: IntegrationSystem.StarbucksStoredValue,
  name: 'Starbucks Gift Card',
  isBrandSpecific: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-starbucks-loyalty-stored-value.png',
  searchTokens: ['starbucks', 'starbucks-stored-value'],
  searchField: null,
  type: 'stored-value',
  isGiftCard: true,
  validCardNumberLengths: [16, 23],
  requiredPosSystem: IntegrationSystem.StarbucksPos,
  requiredPaymentSystem: null,
  supportsStoreValueBalanceLookup: true,
  supportsBarcode: true,
  stripTip: true,
  readMethods: [HardwareReadMethod.Scanner],
  fields: {
    instanceType: instanceTypeFieldSchema,
    storeMerchantId: {
      type: 'keyString',
      displayName: 'Store ID',
      required: true,
      writeRight: UserRight.ManageI9n,
      identifiesTheLocation: true,
      tooltip: {
        text: 'Identifies the store where the transactions will take place.',
      },
    },
    starbucksMerchantId: {
      type: 'stringEnum',
      enum: StarbucksMerchantId,
      displayName: 'Merchant',
      required: true,
      defaultValue: StarbucksMerchantId.Starbucks,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Selects which merchant ID to use for Starbucks gift card calls.',
      },
    },
    terminalId: {
      type: 'keyString',
      displayName: 'Terminal ID',
      required: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Identifies the register used in the transactions.',
      },
    },
  },
};

const StarbucksPosSchema: I9nSchemaPos<IntegrationSystem.StarbucksPos> = {
  _id: IntegrationSystem.StarbucksPos,
  system: IntegrationSystem.StarbucksPos,
  name: 'Starbucks POS',
  isBrandSpecific: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-starbucks-pos.png',
  searchTokens: ['starbucks-pos'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId', // TODO - confirm
  posCheckIdKey: 'starbucksOrderID',
  locationIdKey: 'locationID',
  searchableFieldKeys: ['starbucksOrderID'],
  supportsTips: true, // TODO - confirm
  syncsMenuStructures: true,
  syncsSections: true,
  syncsMultipleOfSameMod: true,
  syncsMacronutrients: false, // They actually can,
  syncsCalories: false, // They actually can,
  syncsBadges: true,
  supportsConcurrentValidation: true,
  supportsPosCoupons: true,
  skipRedundantValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    locationID: {
      type: 'keyString',
      displayName: 'Store ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Starbucks store number',
      },
    },
    starbucksConcept: {
      displayName: 'Concept',
      type: 'stringEnum',
      required: true,
      enum: StarbucksConcept,
      enumNameHelper: StarbucksConceptHelper.name,
      ui: 'radiolist',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'The concept may affect item visibility (eg no coffee traveler items at airports)',
        isWarning: true,
      },
    },
    fetchNationalMenu: {
      displayName: 'Fetch National Menu?',
      type: 'bool',
      tooltip: {
        text: 'Will fetch the national menu without any prices; validation will not work',
        isWarning: true,
      },
      conditions: [
        {
          field: 'instanceType',
          values: [IntegrationInstanceType.Sandbox],
        },
      ],
    },
    syncItemPrices: {
      type: 'bool',
      displayName: 'Sync Item Prices',
      defaultValue: true,
      tooltip: {
        text: 'Uses cached prices with a best-effort daily refresh.',
      },
      writeRight: UserRight.ManageI9n,
    },
    registerNumber: {
      type: 'keyString',
      displayName: 'Register Number',
      required: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'The register number used when submitting orders to Starbucks.',
      },
    },
    skipTipReporting: {
      displayName: 'Skip Tip Reporting',
      type: 'bool',
      writeRight: UserRight.BiteOps,
    },
    additionalRetailItems: {
      displayName: 'Additional Retail Items',
      type: 'array',
      elementType: 'object',
      // This is only true because menuSectionI9nId is required.
      isSetAfterFirstSync: true,
      ui: 'accordion',
      uiSubtype: 'StarbucksAdditionalRetailItems',
      placeholder: 'Add a retail item',
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Items sold from the kiosk that are not in the Starbucks menu catalog. The SKU must exist in the ordering API to be priced.',
      },
      fields: {
        sku: {
          type: 'keyString',
          displayName: 'SKU',
          required: true,
          writeRight: UserRight.ManageI9n,
        },
        name: {
          type: 'keyString',
          required: true,
          writeRight: UserRight.ManageI9n,
        },
        category: {
          type: 'int',
          values: allEnumValues<MenuItemCategory>(MenuItemCategory),
          ui: 'radiolist',
          required: true,
          writeRight: UserRight.ManageI9n,
        },
        menuSectionI9nId: {
          type: 'keyString',
          ui: 'dropdown',
          displayName: 'Section',
          placeholder: 'Select a section',
          required: true,
          writeRight: UserRight.ManageI9n,
          tooltip: {
            text: 'Pick a menu section to place this item in. The section must exist in the Starbucks menu catalog.',
          },
        },
      },
    },
    licenseeCoupons: {
      type: 'array',
      ui: 'table',
      elementType: 'stringEnum',
      elementEnum: StarbucksCouponType,
      displayName: 'Licensee Coupons',
      uniqueElements: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Coupon types this licensed store accepts. The kiosk accepts a code that maps to one of these Starbucks promotions.',
      },
      conditions: [
        {
          field: 'starbucksConcept',
          values: [StarbucksConcept.Airport],
        },
      ],
    },
  },
  diningOptionFields: {
    orderTypeID: {
      displayName: 'Collection Type',
      type: 'stringEnum',
      enum: StarbucksOrderType,
      enumNameHelper: StarbucksOrderTypeHelper.name,
      ui: 'dropdown',
      writeRight: UserRight.ManageI9nLite,
      isNullable: true,
      tooltip: {
        text: 'When unset, orders are sent as in-store.',
      },
    },
    consumptionTypeID: {
      displayName: 'Consumption Type',
      type: 'stringEnum',
      enum: StarbucksConsumptionType,
      enumNameHelper: StarbucksConsumptionTypeHelper.name,
      ui: 'dropdown',
      writeRight: UserRight.ManageI9nLite,
      isNullable: true,
      tooltip: {
        text: 'When unset, orders are sent as consumed in-store.',
      },
    },
  },
  genericFieldDefaultValueByFieldName: {
    syncMenuStructures: true,
    syncSections: true,
    preserveSectionItemOrder: true,
    syncBadges: true,
    preserveModGroupModOrder: true,
    preserveItemModGroupOrder: true,
    diningOptions: [
      {
        fulfillmentMethod: FulfillmentMethod.KIOSK_TO_GO,
        orderTypeID: StarbucksOrderType.InStore,
        consumptionTypeID: StarbucksConsumptionType.OutOfStore,
      },
    ],
  },
};

const PinataCashMachineSchema: I9nSchemaCashMachine<IntegrationSystem.PinataCashMachine> = {
  _id: IntegrationSystem.PinataCashMachine,
  system: IntegrationSystem.PinataCashMachine,
  name: 'Pinata Cash Machine',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-pinata-cash-machine.png',
  searchTokens: ['pinata', 'cash'],
  searchField: null,
  type: 'cash-machine',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    key: {
      type: 'keyString',
      displayName: 'Key',
      writeRight: UserRight.ManageI9n,
    },
  },
};

const GlorySchema: I9nSchemaCashMachine<IntegrationSystem.Glory> = {
  _id: IntegrationSystem.Glory,
  system: IntegrationSystem.Glory,
  name: 'Glory',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-glory.png',
  searchTokens: ['glory', 'cash'],
  searchField: null,
  type: 'cash-machine',
  requiredPosSystem: null,
  incompatiblePosSystems: [],
  fields: {
    ignoreLowInventory: {
      type: 'bool',
      displayName: 'Ignore Low Inventory',
      defaultValue: false,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'If enabled, Garcon will not set the Glory cash machine status to low inventory.',
      },
    },
  },
};

const VerifoneCommanderSchema: I9nSchemaPos<IntegrationSystem.VerifoneCommander> = {
  _id: IntegrationSystem.VerifoneCommander,
  system: IntegrationSystem.VerifoneCommander,
  name: 'Verifone Commander',
  isBrandSpecific: false,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-verifone-commander.png',
  searchTokens: ['verifone', 'commander'],
  searchField: 'ipAddress',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'verifoneOrderID',
  locationIdKey: 'ipAddress',
  searchableFieldKeys: ['verifoneOrderID'],
  supportsCompoundMenuItems: true, // TODO @jerry[bite-16646] - confirm
  supportsFutureOrders: false, // TODO @jerry[bite-16646] - confirm
  syncsCalories: false, // TODO @jerry[bite-16646] - confirm
  syncsMacronutrients: false, // TODO @jerry[bite-16646] - confirm
  syncsSections: true, // TODO @jerry[bite-16646] - confirm
  syncsMenuStructures: true, // TODO @jerry[bite-16646] - confirm
  supportsPayingWithCash: true,
  fields: {
    isLiveIntegration: isLiveIntegrationUnconditionalFieldSchema,
    ipAddress: {
      type: 'keyString',
      displayName: 'IP Address',
      validationRegex: ipAddressRegexString,
    },
    vistaSchemaVersion: {
      type: 'keyString',
      required: true,
      displayName: 'Vista Schema Version',
      validationRegex: vistaSchemaVersionRegexString,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Vista Schema Version sent on the begin-session <externalUserInformation> envelope (e.g. 01.05.00). See VISTA Client Implementation Guide §Begin Session.',
      },
    },
    posUserId: {
      type: 'keyString',
      displayName: 'POS User ID',
      required: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Cashier login ID from Commander Configuration Client > Security > POS Security.',
      },
    },
    posUserPassword: {
      type: 'keyString',
      displayName: 'POS User Password',
      required: true,
      maskInput: true,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Cashier login password from Commander Configuration Client > Security > POS Security.',
      },
    },
    tokens: {
      type: 'map',
      key: {
        type: 'keyString',
        displayName: 'Device Serial Number',
      },
      fields: {
        token: {
          type: 'keyString',
          displayName: 'Token',
        },
      },
    },
  },
  diningOptionFields: {
    ...generateCardSpecificTenderTypeSchema({
      isSetAfterFirstSync: true,
      tooltipText: 'Set after first sync.',
    }),
  },
};

const LovesPosSchema: I9nSchemaPos<IntegrationSystem.LovesPos> = {
  _id: IntegrationSystem.LovesPos,
  system: IntegrationSystem.LovesPos,
  name: 'Loves POS',
  isBrandSpecific: true,
  iconUrl: 'https://static.bureau.getbite.com/images/icon-loves-pos.png',
  searchTokens: ['loves'],
  searchField: 'locationID',
  type: 'pos',
  webhookIdKey: 'i9nId',
  posCheckIdKey: 'lovesMobileTraOrderId',
  locationIdKey: 'locationID',
  searchableFieldKeys: ['lovesMobileTraOrderId'],
  supportsCompoundMenuItems: true,
  supportsFutureOrders: false,
  syncsCalories: false,
  syncsMacronutrients: false,
  syncsSections: true,
  syncsMenuStructures: false,
  syncsTaxes: true,
  validateOnLoyaltyAuth: true,
  skipRedundantValidation: true,
  supportsConcurrentValidation: true,
  fields: {
    instanceType: instanceTypeFieldSchema,
    isLiveIntegration: isLiveIntegrationFieldSchema,
    lovesOrderingSandboxServer: {
      type: 'stringEnum',
      displayName: 'Ordering Sandbox API Server',
      ui: 'dropdown',
      enum: LovesSandboxApiServer,
      enumNameHelper: LovesSandboxApiServerHelper.name,
      values: allEnumValues<LovesSandboxApiServer>(LovesSandboxApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'instanceType',
          values: [IntegrationInstanceType.Sandbox],
        },
      ],
    },
    mlrSandboxServer: {
      type: 'stringEnum',
      displayName: 'MLR Sandbox API Server',
      ui: 'dropdown',
      enum: LovesSandboxApiServer,
      enumNameHelper: LovesSandboxApiServerHelper.name,
      values: allEnumValues<LovesSandboxApiServer>(LovesSandboxApiServer),
      required: true,
      requiresValidationIfChanged: true,
      writeRight: UserRight.ManageI9n,
      conditions: [
        {
          field: 'instanceType',
          values: [IntegrationInstanceType.Sandbox],
        },
      ],
    },
    locationID: {
      type: 'keyString',
      displayName: 'Store ID',
      required: true,
      requiresValidationIfChanged: true,
      identifiesTheLocation: true,
      tooltip: {
        text: 'No padding (no trailing 4 zeros)',
      },
      writeRight: UserRight.ManageI9n,
    },
    lovesConcept: {
      type: 'stringEnum',
      displayName: 'Concept',
      enum: LovesConcept,
      required: true,
      defaultValue: LovesConcept.ExpressCheckout,
      writeRight: UserRight.ManageI9n,
      tooltip: {
        text: 'Determines the menu data synced from Loves. Requires a resync on change.',
      },
    },
    terminalNumber: {
      type: 'int',
      displayName: 'Terminal Number',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
    cashierID: {
      type: 'int',
      displayName: 'Cashier ID',
      required: true,
      writeRight: UserRight.ManageI9n,
    },
  },
  diningOptionFields: {},
};

export const I9nSchemaBySystem: BaseI9nSchemaBySystem = {
  [IntegrationSystem.Adyen]: AdyenSchema,
  [IntegrationSystem.Appfront]: AppfrontSchema,
  [IntegrationSystem.Archer]: ArcherSchema,
  [IntegrationSystem.AtriumCampusCard]: AtriumCampusCardSchema,
  [IntegrationSystem.AtriumLoyalty]: AtriumLoyaltySchema,
  [IntegrationSystem.AtriumStoredValue]: AtriumStoredValueSchema,
  [IntegrationSystem.Attentive]: AttentiveSchema,
  [IntegrationSystem.AurusPay]: AurusPaySchema,
  [IntegrationSystem.AurusPayStoredValue]: AurusPayStoredValueSchema,
  [IntegrationSystem.BreadcrumbDeprecated]: BreadcrumbSchema,
  [IntegrationSystem.CardConnect]: CardConnectSchema,
  [IntegrationSystem.CbordCampusCard]: CbordCampusCardSchema,
  [IntegrationSystem.QuickchargeCampusCard]: QuickchargeCampusCardSchema,
  [IntegrationSystem.CbordStoredValue]: CbordStoredValueSchema,
  [IntegrationSystem.Chase]: ChaseSchema,
  [IntegrationSystem.ChaseStoredValue]: ChaseStoredValueSchema,
  [IntegrationSystem.CheetahOloLoyalty]: CheetahOloLoyaltySchema,
  [IntegrationSystem.ChoptLoyalty]: ChoptLoyaltySchema,
  [IntegrationSystem.Como]: ComoSchema,
  [IntegrationSystem.ComoEarner]: ComoEarnerSchema,
  [IntegrationSystem.ComoOlo]: ComoOloSchema,
  [IntegrationSystem.Deliverect]: DeliverectSchema,
  [IntegrationSystem.DeliverectCommerce]: DeliverectCommerceSchema,
  [IntegrationSystem.Eigen]: EigenSchema,
  [IntegrationSystem.EigenStoredValue]: EigenStoredValueClient,
  [IntegrationSystem.Elavon]: ElavonSchema,
  [IntegrationSystem.FreedomPayFlash]: FreedomPayFlashSchema,
  [IntegrationSystem.FreedomPayKiosk]: FreedomPayKioskSchema,
  [IntegrationSystem.FreedomPayKioskStoredValue]: FreedomPayKioskStoredValueSchema,
  [IntegrationSystem.FreedomPayStoredValue]: FreedomPayStoredValueSchema,
  [IntegrationSystem.FreshKDS]: FreshKDSSchema,
  [IntegrationSystem.Givex]: GivexSchema,
  [IntegrationSystem.Glory]: GlorySchema,
  [IntegrationSystem.Heartland]: HeartlandSchema,
  [IntegrationSystem.TransactCampusCard]: TransactCampusCardSchema,
  [IntegrationSystem.Incentivio]: IncentivioSchema,
  [IntegrationSystem.Infor]: InforSchema,
  [IntegrationSystem.IQTouch]: IQTouchSchema,
  [IntegrationSystem.Jtech]: JtechSchema,
  [IntegrationSystem.LogicControls]: LogicControlsSchema,
  [IntegrationSystem.LovesPos]: LovesPosSchema,
  [IntegrationSystem.LoyaltyPlant]: LoyaltyPlantSchema,
  [IntegrationSystem.LoyaltyPlantEarner]: LoyaltyPlantEarnerSchema,
  [IntegrationSystem.LrsTableTracker]: LrsTableTrackerSchema,
  [IntegrationSystem.Lunchbox]: LunchboxSchema,
  [IntegrationSystem.Moneris]: MonerisSchema,
  [IntegrationSystem.LovesRewards]: LovesRewardsSchema,
  [IntegrationSystem.NcrAloha]: NcrAlohaSchema,
  [IntegrationSystem.NcrRadiant]: NcrRadiantSchema,
  [IntegrationSystem.Olo]: OloSchema,
  [IntegrationSystem.OloAccountsLoyalty]: OloAccountsLoyaltySchema,
  [IntegrationSystem.OloPayKioskOrderingApi]: OloPayKioskOrderingApiSchema,
  [IntegrationSystem.OloPayKioskPaymentApi]: OloPayKioskPaymentApiSchema,
  [IntegrationSystem.OloStoredValue]: OloStoredValueSchema,
  [IntegrationSystem.Omnivore]: OmnivoreSchema,
  [IntegrationSystem.Otter]: OtterSchema,
  [IntegrationSystem.ParBrink]: ParBrinkSchema,
  [IntegrationSystem.ParBrinkLoyaltyEarner]: ParBrinkLoyaltySchema,
  [IntegrationSystem.ParPay]: ParPaySchema,
  [IntegrationSystem.ParPayStoredValue]: ParPayStoredValueSchema,
  [IntegrationSystem.ParPosDataSource]: ParPosDataSourceSchema,
  [IntegrationSystem.PaytronixCompCard]: PaytronixCompCardSchema,
  [IntegrationSystem.PaytronixGift]: PaytronixGiftSchema,
  [IntegrationSystem.PaytronixLoyalty]: PaytronixLoyaltySchema,
  [IntegrationSystem.PaytronixOlo]: PaytronixOloSchema,
  [IntegrationSystem.PDI]: PDISchema,
  [IntegrationSystem.PeetsPaytronixOlo]: PeetsPaytronixOloSchema,
  [IntegrationSystem.PinataCashMachine]: PinataCashMachineSchema,
  [IntegrationSystem.PinataCampusCard]: PinataCampusCardSchema,
  [IntegrationSystem.PinataCompCard]: PinataCompCardSchema,
  [IntegrationSystem.PinataFulfillment]: PinataFulfillmentSchema,
  [IntegrationSystem.PinataLoyalty]: PinataLoyaltySchema,
  [IntegrationSystem.PinataNotifier]: PinataNotifierSchema,
  [IntegrationSystem.PinataPay]: PinataPaySchema,
  [IntegrationSystem.PinataPayNative]: PinataPayNativeSchema,
  [IntegrationSystem.PinataPayNativeStoredValue]: PinataPayNativeStoredValueSchema,
  [IntegrationSystem.PinataPayStoredValue]: PinataPayStoredValueSchema,
  [IntegrationSystem.PinataPos]: PinataPosSchema,
  [IntegrationSystem.PinataStoredValue]: PinataStoredValueSchema,
  [IntegrationSystem.PinataTableTracker]: PinataTableTrackerSchema,
  [IntegrationSystem.Punchh]: PunchhSchema,
  [IntegrationSystem.PunchhOlo]: PunchhOloSchema,
  [IntegrationSystem.QsrAutomations]: QsrAutomationsSchema,
  [IntegrationSystem.Qu]: QuSchema,
  [IntegrationSystem.Qualtrics]: QualtricsSchema,
  [IntegrationSystem.Revel]: RevelSchema,
  [IntegrationSystem.Salido]: SalidoSchema,
  [IntegrationSystem.SessionMOlo]: SessionMOloSchema,
  [IntegrationSystem.Simphony]: SimphonySchema,
  [IntegrationSystem.Smg]: SmgSchema,
  [IntegrationSystem.SmoothCommerce]: SmoothCommerceSchema,
  [IntegrationSystem.Sparkfly]: SparkflySchema,
  [IntegrationSystem.SparkflyLoyaltyEarner]: SparkflyLoyaltyEarnerSchema,
  [IntegrationSystem.Spendgo]: SpendgoSchema,
  [IntegrationSystem.SpendgoOlo]: SpendgoOloSchema,
  [IntegrationSystem.SpotOn]: SpotOnSchema,
  [IntegrationSystem.Sprinkles]: SprinklesSchema,
  [IntegrationSystem.SquarePos]: SquarePosSchema,
  [IntegrationSystem.StarbucksFullLoyalty]: StarbucksFullLoyaltySchema,
  [IntegrationSystem.StarbucksPayAndEarnLoyalty]: StarbucksPayAndEarnLoyaltySchema,
  [IntegrationSystem.StarbucksPos]: StarbucksPosSchema,
  [IntegrationSystem.StarbucksStoredValue]: StarbucksStoredValueSchema,
  [IntegrationSystem.Stripe]: StripeSchema,
  [IntegrationSystem.StripeTerminal]: StripeTerminalSchema,
  [IntegrationSystem.Tcc]: TccSchema,
  [IntegrationSystem.Thanx]: ThanxSchema,
  [IntegrationSystem.ThanxOlo]: ThanxOloSchema,
  [IntegrationSystem.Toast]: ToastSchema,
  [IntegrationSystem.Twilio]: TwilioSchema,
  [IntegrationSystem.Valutec]: ValutecSchema,
  [IntegrationSystem.VerifoneCommander]: VerifoneCommanderSchema,
  [IntegrationSystem.VerifonePointCloud]: VerifonePointCloudSchema,
  [IntegrationSystem.VerifonePointSca]: VerifonePointScaSchema,
  [IntegrationSystem.Vibes]: VibesSchema,
  [IntegrationSystem.Vicki]: VickiSchema,
  [IntegrationSystem.Worldpay]: WorldpaySchema,
  [IntegrationSystem.WorldpayStoredValue]: WorldpayStoredValueSchema,
} as const;

export function getI9nSchemaNameBySystem(system: IntegrationSystem): string {
  return I9nSchemaBySystem[system].name;
}

export function getI9nSchemaTypeBySystem(system: IntegrationSystem): I9nSchemaType {
  return I9nSchemaBySystem[system].type;
}

const allSystems = allEnumValues<IntegrationSystem>(IntegrationSystem);
export const cashMachineSystems = allSystems.filter(
  (system): system is IntegrationSystem.CashMachine =>
    I9nSchemaBySystem[system].type === 'cash-machine',
);
export function systemIsCashMachine(
  system: IntegrationSystem,
): system is IntegrationSystem.CashMachine {
  return cashMachineSystems.includes(system as IntegrationSystem.CashMachine);
}
export const campusCardSystems = allSystems.filter(
  (system): system is IntegrationSystem.CampusCard =>
    I9nSchemaBySystem[system].type === 'campus-card',
);
export function systemIsCampusCard(
  system: IntegrationSystem,
): system is IntegrationSystem.CampusCard {
  return campusCardSystems.includes(system as IntegrationSystem.CampusCard);
}
export const compCardSystems = allSystems.filter(
  (system): system is IntegrationSystem.CompCard => I9nSchemaBySystem[system].type === 'comp-card',
);
export function systemIsCompCard(system: IntegrationSystem): system is IntegrationSystem.CompCard {
  return compCardSystems.includes(system as IntegrationSystem.CompCard);
}
export const fulfillmentSystems = allSystems.filter(
  (system): system is IntegrationSystem.Fulfillment =>
    I9nSchemaBySystem[system].type === 'fulfillment',
);
export function systemIsFulfillment(
  system: IntegrationSystem,
): system is IntegrationSystem.Fulfillment {
  return fulfillmentSystems.includes(system as IntegrationSystem.Fulfillment);
}
export const kioskPaymentSystems = allSystems.filter(
  (system): system is IntegrationSystem.KioskPayment =>
    I9nSchemaBySystem[system].type === 'kiosk-payment',
);
export function systemIsKioskPayment(
  system: IntegrationSystem,
): system is IntegrationSystem.KioskPayment {
  return kioskPaymentSystems.includes(system as IntegrationSystem.KioskPayment);
}
export const loyaltySystems = allSystems.filter(
  (system): system is IntegrationSystem.Loyalty => I9nSchemaBySystem[system].type === 'loyalty',
);
export function systemIsLoyalty(system: IntegrationSystem): system is IntegrationSystem.Loyalty {
  return loyaltySystems.includes(system as IntegrationSystem.Loyalty);
}
export const marketingSystems = allSystems.filter(
  (system): system is IntegrationSystem.Marketing => I9nSchemaBySystem[system].type === 'marketing',
);
export function systemIsMarketing(
  system: IntegrationSystem,
): system is IntegrationSystem.Marketing {
  return marketingSystems.includes(system as IntegrationSystem.Marketing);
}
export const posSystems = allSystems.filter(
  (system): system is IntegrationSystem.Pos => I9nSchemaBySystem[system].type === 'pos',
);
export function systemIsPos(system: IntegrationSystem): system is IntegrationSystem.Pos {
  return posSystems.includes(system as IntegrationSystem.Pos);
}
export const storedValueSystems = allSystems.filter(
  (system): system is IntegrationSystem.StoredValue =>
    I9nSchemaBySystem[system].type === 'stored-value',
);
export const posDataSourceSystems = allSystems.filter(
  (system): system is IntegrationSystem.PosDataSource =>
    I9nSchemaBySystem[system].type === 'pos-data-source',
);
export function systemIsPosDataSource(
  system: IntegrationSystem,
): system is IntegrationSystem.PosDataSource {
  return posDataSourceSystems.includes(system as IntegrationSystem.PosDataSource);
}
export function systemIsStoredValue(
  system: IntegrationSystem,
): system is IntegrationSystem.StoredValue {
  return storedValueSystems.includes(system as IntegrationSystem.StoredValue);
}
export const tableTrackerSystems = allSystems.filter(
  (system): system is IntegrationSystem.TableTracker =>
    I9nSchemaBySystem[system].type === 'table-tracker',
);
export function systemIsTableTracker(
  system: IntegrationSystem,
): system is IntegrationSystem.TableTracker {
  return tableTrackerSystems.includes(system as IntegrationSystem.TableTracker);
}

export type I9nSchema = (typeof I9nSchemaBySystem)[keyof typeof I9nSchemaBySystem];
export type I9nSchemaType = I9nSchema['type'] | 'email-notifier';

type BaseI9nSchemaByType = {
  [I9nType in I9nSchemaType]: BaseI9nSchema<IntegrationSystem> & { type: I9nType };
};
export interface I9nSchemaByType extends BaseI9nSchemaByType {
  'campus-card': I9nSchemaCampusCard<IntegrationSystem.CampusCard>;
  'cash-machine': I9nSchemaCashMachine<IntegrationSystem.CashMachine>;
  'comp-card': I9nSchemaCompCard<IntegrationSystem.CompCard>;
  'ecomm-payment': I9nSchemaEcommPayment<IntegrationSystem.EcommPayment>;
  'email-notifier': I9nSchemaEmailNotifier<IntegrationSystem.EmailNotifier>;
  fulfillment: I9nSchemaFulfillment<IntegrationSystem.Fulfillment>;
  'kiosk-payment': I9nSchemaKioskPayment<IntegrationSystem.KioskPayment>;
  loyalty: I9nSchemaLoyalty<IntegrationSystem.Loyalty>;
  'loyalty-earner': I9nSchemaLoyaltyEarner<IntegrationSystem.LoyaltyEarner>;
  marketing: I9nSchemaMarketing<IntegrationSystem.Marketing>;
  pos: I9nSchemaPos<IntegrationSystem.Pos>;
  'pos-data-source': I9nSchemaPosDataSource<IntegrationSystem.PosDataSource>;
  'sms-notifier': I9nSchemaSmsNotifier<IntegrationSystem.SmsNotifier>;
  'stored-value': I9nSchemaStoredValue<IntegrationSystem.StoredValue>;
  'supplementary-menu-source': I9nSchemaSupplementaryMenuSource<IntegrationSystem.SupplementaryMenuSource>;
  'table-tracker': I9nSchemaTableTracker<IntegrationSystem.TableTracker>;
}
